// Boost Blenz Admin — Finance, Ads, Settings views (incl. Daily Pulse overview).
// Ported verbatim from the prototype's admin-finance.jsx.

import React from "react";
import { OSWALD, FIGTREE, MONO, useT, useIsMobile } from "../theme";
import { Icon, ClaudeMark, Title, Card, Badge, Btn, BuildNote, SectionTitle, Toggle, Sparkline } from "../atoms";
import { BBFin } from "../data/finance";
import { Avatar } from "./appviews";
import { useRangeMetrics, DateRangePicker, RANGE_OPTS } from "../rangemetrics";
import { useCachedJson, useCachedResource } from "../datacache";

const F = () => BBFin;
const money = (n: number, dp = 2) => (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
const pctS = (n: number, dp = 1) => n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp }) + '%';

// ---- inline number editor (text input: no spinners, arrow keys bump by step) ----
// `box` swaps the compact pill for the inset dark field used on the reskinned
// Expenses / New Stuff forms. Behavior is identical either way.
export function NumInput({ value, onChange, step = 1, prefix, suffix, width = 78, box }: any) {
  const t = useT();
  const [txt, setTxt] = React.useState(String(value));
  React.useEffect(() => { setTxt(String(value)); }, [value]);
  const bump = (d: number) => {
    const n = Math.round(((parseFloat(txt) || 0) + d) * 100) / 100;
    setTxt(String(n)); onChange(n);
  };
  const shell: any = box
    ? { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(13,10,30,0.6)', border: '1px solid rgba(150,130,230,0.2)', borderRadius: 11, padding: '10px 15px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: t.faint }
    : { display: 'inline-flex', alignItems: 'center', gap: 4, background: t.surface2, border: '1px solid ' + t.border, borderRadius: 999, padding: '3px 10px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.muted };
  return (
    <span style={shell}>
      {prefix}
      <input type="text" inputMode="decimal" value={txt}
        onChange={(e) => { const v = e.target.value; if (/^-?\d*\.?\d*$/.test(v)) { setTxt(v); onChange(parseFloat(v) || 0); } }}
        onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); bump(step); } else if (e.key === 'ArrowDown') { e.preventDefault(); bump(-step); } }}
        onBlur={() => setTxt(String(value))}
        style={{ width, border: 'none', background: 'transparent', color: t.fg, fontFamily: FIGTREE, fontWeight: 700, fontSize: box ? 15 : 14, outline: 'none', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
      {suffix}
    </span>
  );
}

// Confirm gate for anything that changes or hides data — deletes, archives,
// restores. Every such action goes through here rather than firing on click, so
// a stray click can't silently move a number. `tone` colors the confirm button:
// 'danger' (coral) for irreversible removal, 'accent' (orange) for reversible
// state changes like archive/restore.
export function ConfirmDialog({ title, body, confirmLabel = 'Delete', tone = 'danger', onConfirm, onCancel }: any) {
  const t = useT();
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onCancel]);
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(6,4,18,0.6)', padding: 16 }}>
      <div style={{ background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: 16, padding: '24px 26px', width: 352, maxWidth: '90vw', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 16, color: t.fg, marginBottom: 9 }}>{title}</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, lineHeight: 1.55, marginBottom: 20 }}>{body}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid ' + t.borderStrong, color: t.fg2, fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, padding: '9px 18px', borderRadius: 999, cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ background: tone === 'danger' ? '#ff6f5e' : t.accent, border: 0, color: '#1a1330', fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, padding: '9px 22px', borderRadius: 999, cursor: 'pointer' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function Bar({ pct, color }: any) {
  const t = useT();
  return <div style={{ height: 7, borderRadius: 999, background: t.surface2, overflow: 'hidden' }}><div style={{ width: Math.min(100, pct) + '%', height: '100%', background: color, borderRadius: 999 }} /></div>;
}

// ============================================================ OVERVIEW / DAILY PULSE
// Shopify-analytics-owned fields on the Daily Pulse `day` object. When Shopify
// is fully unreachable we blank these so the cards read "—" instead of showing a
// stale bootstrap (yesterday) value under the selected range's label.
const SHOPIFY_DAY_FIELDS = ['revenue', 'grossSales', 'grossSalesDelta', 'orders', 'aov', 'revenueDelta', 'ordersDelta', 'newCustomers', 'newCustomersDelta', 'returningCustomers', 'returningCustomersDelta', 'newOrders', 'returningOrders', 'returningRate', 'returningRateDeltaPp', 'visitors', 'visitorsDelta', 'atc', 'atcDelta', 'sessions', 'cvr'];

// Cloudflare capacity peak — powers the Pulse "Integration health" dot + a Morning
// Brief alert when infra usage climbs toward a monthly plan limit. Read-only glance;
// the full gauges live on the Integrations page.
export function useCfUsage() {
  const { data } = useCachedJson<any>('/api/usage');
  const metrics: any[] = data?.metrics || [];
  const peak = metrics.reduce((m: number, x: any) => Math.max(m, x.pct || 0), 0);
  const top = metrics.slice().sort((a, b) => (b.pct || 0) - (a.pct || 0))[0] || null;
  return { peak, top, connected: !!data?.ok && metrics.length > 0 };
}

// ---- Daily Pulse redesign atoms (design_handoff_daily_pulse) ----------------
// ROAS threshold FALLBACKS only — the live values come from /api/kpi (the KPI
// Targets source of truth, derived from real rolling-90-day economics). These
// hold the layout together if that endpoint is unreachable.
const ROAS_BREAKEVEN_FALLBACK = 1.6;
const ROAS_TARGET_FALLBACK = 2.5;
const AD_CEILING_FALLBACK = 40;

// KPI Targets source of truth → breakeven/target ROAS + the ad-spend ceiling %
// used by the Pulse and Profit callouts, so every threshold on the site moves
// when the owner edits the KPI Targets page (or the 90-day economics shift).
export function useKpiTargets() {
  const { data: raw } = useCachedJson<any>('/api/kpi');
  const data = raw?.ok ? raw : null; // only trust a fully-formed KPI payload
  return {
    data,
    breakeven: data?.outputs?.breakevenRoas ?? ROAS_BREAKEVEN_FALLBACK,
    target: data?.outputs?.targetRoas ?? ROAS_TARGET_FALLBACK,
    // blended target = the ROAS implied by the ad-spend ceiling (100 / ceiling%)
    targetBlended: data?.outputs?.targetBlendedRoas ?? (100 / (data?.config?.targetAdSpendPct ?? AD_CEILING_FALLBACK)),
    adCeiling: data?.config?.targetAdSpendPct ?? AD_CEILING_FALLBACK,
    live: !!data, // false → showing fallbacks
  };
}

// Section label — Oswald caps eyebrow above each card group.
function PulseLabel({ children }: any) {
  const t = useT();
  return <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 12, color: t.muted }}>{children}</div>;
}

// ---- Fit-to-card stat numbers --------------------------------------------
// FitValue renders a stat number at the design size and scales it DOWN (never
// up) just enough to fit its card on one line. Measurement uses a hidden clone
// rendered at the BASE size, so the fitted size is exact and never feedback-
// loops. Wrap a row of cards in <FitGroup> and every value in the row locks to
// the SAME size — the largest that lets the longest number fit — so a card row
// never shows mixed font sizes.
const FitGroupCtx = React.createContext<any>(null);
export function FitGroup({ children }: any) {
  const [sizes, setSizes] = React.useState<Record<string, number>>({});
  const report = React.useCallback((id: string, s: number | null) => {
    setSizes((m) => {
      if (s == null) { if (!(id in m)) return m; const { [id]: _drop, ...rest } = m; return rest; }
      return m[id] === s ? m : { ...m, [id]: s };
    });
  }, []);
  const vals = Object.values(sizes);
  const min = vals.length ? Math.min(...vals) : null;
  const ctx = React.useMemo(() => ({ report, min }), [report, min]);
  return <FitGroupCtx.Provider value={ctx}>{children}</FitGroupCtx.Provider>;
}

export function FitValue({ size = 34, color, children, style }: any) {
  const id = React.useId();
  const group = React.useContext(FitGroupCtx);
  const wrapRef = React.useRef<any>(null);
  const measureRef = React.useRef<any>(null);
  const [fitted, setFitted] = React.useState(size);
  React.useLayoutEffect(() => {
    const wrap = wrapRef.current, meas = measureRef.current;
    if (!wrap || !meas) return;
    const fit = () => {
      const avail = wrap.clientWidth;
      const natural = meas.scrollWidth; // rendered at the base size, always
      if (!avail || !natural) return;
      const next = natural > avail ? Math.max(14, Math.floor(size * (avail / natural) * 10) / 10) : size;
      setFitted(next);
      if (group) group.report(id, next);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    return () => { ro.disconnect(); if (group) group.report(id, null); };
  }, [size, children, group, id]);
  const fs = group && group.min != null ? Math.min(fitted, group.min) : fitted;
  const font: any = { fontFamily: FIGTREE, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  return (
    <div ref={wrapRef} style={{ minWidth: 0, position: 'relative' }}>
      <div style={{ ...font, fontSize: fs, color: color || '#fff', ...style }}>{children}</div>
      {/* hidden clone at the base size — the measuring stick */}
      <div ref={measureRef} aria-hidden style={{ ...font, fontSize: size, position: 'absolute', top: 0, left: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }}>{children}</div>
    </div>
  );
}

// Page header — the reskinned big-title treatment (32px Oswald caps + subtitle),
// with optional right-aligned controls (date range, tabs, refresh). Matches the
// Ad Performance / Email / Quiz / KPI mocks and the other reskinned pages.
export function PageHead({ title, sub, right, isMobile }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
      <div style={{ minWidth: 0, flex: '1 1 240px' }}>
        <Title size={isMobile ? 24 : 32}>{title}</Title>
        {sub && <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 6, lineHeight: 1.5, maxWidth: 560 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// KPI stat tile — the handoff's reskinned card: Oswald caps label + small icon
// on top, a big responsive number below (FitValue shrinks long money values to
// fit the card instead of clipping, per the "$81,092.08 was cut off" fix in the
// design README). Optional sparkline + sub caption. Display-only — same data as
// the old Stat atom, restyled to match the Ad Performance / Email / Quiz mocks.
export function StatTile({ label, value, sub, accent, icon, spark }: any) {
  const t = useT();
  return (
    <div style={{ background: t.surface, border: '1px solid ' + t.border, borderRadius: 16, padding: '18px 18px 20px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: 11, color: t.muted, lineHeight: 1.25 }}>{label}</span>
        {icon && <Icon name={icon} size={15} color={accent || t.faint} style={{ flexShrink: 0 }} />}
      </div>
      <FitValue size={34} color={accent || t.fg}>{value}</FitValue>
      {spark && <div style={{ marginTop: 12 }}><Sparkline data={spark} color={accent || t.accent} /></div>}
      {sub && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 8 }}>{sub}</div>}
    </div>
  );
}

// Big hero stat card (Business Vitals row + P&L KPI row) — gradient surface,
// icon chip, 800-weight number. `caps` renders the label in Oswald caps;
// grad/borderColor/valueColor override the defaults for emphasized cards.
function HeroCard({ icon, tint, tintBg, label, value, foot, caps, valueColor, grad, borderColor }: any) {
  const t = useT();
  return (
    <div style={{ background: grad || t.heroGrad, border: '1px solid ' + (borderColor || 'rgba(150,130,230,0.16)'), borderRadius: t.cardRadius, padding: 22, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: caps ? 18 : 20 }}>
        <span style={{ width: 32, height: 32, borderRadius: 9, background: tintBg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={icon} size={16} color={tint} strokeWidth={2.2} />
        </span>
        {caps
          ? <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11.5, color: t.muted }}>{label}</span>
          : <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, fontWeight: 600 }}>{label}</span>}
      </div>
      <FitValue size={34} color={valueColor}>{value}</FitValue>
      <div style={{ marginTop: 14, minHeight: 18, fontFamily: FIGTREE, fontSize: 12 }}>{foot}</div>
    </div>
  );
}

// Per-platform split shown under every COMBINED ad-spend / blended-ROAS figure —
// "Meta $1,186 · Google $20". Driven by `day.adBreakdown` from the Worker's ad-spend
// registry (worker/adspend.ts), so a channel added there appears here with no UI
// change. A platform that couldn't report is called out in amber: its spend is
// MISSING from the total (which understates ad % and overstates ROAS), not zero.
function AdSplit({ d, style, prefix }: any) {
  const t = useT();
  const rows: any[] = d?.adBreakdown || [];
  if (rows.length < 2 && !rows.some((r: any) => !r.ok)) return null; // single platform → the total already says it
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 5, ...style }}>
      {prefix ? <span>{prefix}</span> : null}
      {rows.map((r: any, i: number) => (
        <span key={r.id} style={{ whiteSpace: 'nowrap', color: r.ok ? t.faint : t.warn, fontWeight: r.ok ? 400 : 600 }}>
          {i > 0 ? <span style={{ color: t.faint, marginRight: 8 }}>·</span> : null}
          {r.label} {r.ok ? money(r.spend, 0) : 'unavailable'}
        </span>
      ))}
    </div>
  );
}

// Period-over-period pill ("▼ 28% vs prior") — coral when bad, green when good.
function DeltaPill({ pct, goodWhenUp = true, suffix = ' vs prior' }: any) {
  const t = useT();
  if (pct == null) return null;
  const up = pct >= 0;
  const good = goodWhenUp ? up : !up;
  const [fg, bg] = good ? [t.good, t.goodSoft] : [t.bad, t.badSoft];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: fg, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%{suffix}
    </span>
  );
}

// Tiny inline caption delta — pass exactly one of pct (% change), pp (percentage-
// point change, for metrics that ARE percentages) or abs (dollar change, for
// values that can cross zero where a % of the base is noise).
function MiniDelta({ pct, pp, abs, goodWhenUp = true }: any) {
  const t = useT();
  const v = pct != null ? pct : pp != null ? pp : abs;
  if (v == null) return null;
  const up = v >= 0;
  const txt = pct != null ? Math.abs(pct) + '%' : pp != null ? Math.abs(pp) + 'pp' : money(Math.abs(abs), 0);
  return <span style={{ color: (goodWhenUp ? up : !up) ? t.good : t.bad, fontWeight: 700, whiteSpace: 'nowrap' }}>{up ? '▲' : '▼'}{txt}</span>;
}

// Combined-card header: colored dot + Oswald caps title (+ optional right slot).
function PanelHead({ dot, children, right, mb = 16 }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: mb, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: dot, flexShrink: 0 }} />
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 12, color: t.fg2 }}>{children}</span>
      </div>
      {right}
    </div>
  );
}

// Small footer stat inside a combined card (value on top, caption under).
function FootStat({ value, cap, valueColor, capColor, size = 19, align }: any) {
  const t = useT();
  return (
    <div style={{ minWidth: 0, textAlign: align || 'left' }}>
      <FitValue size={size} color={valueColor || t.fg} style={{ textAlign: align || 'left' }}>{value}</FitValue>
      <div style={{ fontFamily: FIGTREE, fontSize: 11, color: capColor || t.muted, marginTop: 2 }}>{cap}</div>
    </div>
  );
}

// Daily Pulse → Progression: interactive multi-line trend chart. Metric tabs
// (Revenue / Meta / Google / Retention) × time views (Day / Week / Month / YTD),
// fed by /api/trend (the pre-aggregated decision_daily series). Hover for exact
// values; the Retention returning line also shows its % in the tooltip.
function ProgressionChart() {
  const t = useT();
  const [metric, setMetric] = React.useState<'revenue' | 'paidads' | 'retention'>('revenue');
  const [mode, setMode] = React.useState<'day' | 'week' | 'month' | 'year'>('week');
  const [hover, setHover] = React.useState<number | null>(null);
  const { data, loading } = useCachedJson<any>(`/api/trend?mode=${mode}`);
  const points: any[] = data?.points || [];

  // Per channel: spend = solid, attributed revenue = dashed (same hue), so each
  // channel reads as a pair against the purple total-revenue line.
  const META = '#f8925c', GOOGLE = '#5b8def';
  const METRICS: any = {
    revenue: { title: 'Revenue', lines: [{ key: 'revenue', name: 'Revenue', color: t.purple, kind: 'money' }] },
    paidads: { title: 'Paid ads · spend vs attributed revenue', lines: [
      { key: 'revenue', name: 'Total revenue', color: t.purple, kind: 'money' },
      { key: 'meta', name: 'Meta spend', color: META, kind: 'money' },
      { key: 'metaRev', name: 'Meta revenue', color: META, kind: 'money', dash: true },
      { key: 'google', name: 'Google spend', color: GOOGLE, kind: 'money' },
      { key: 'googleRev', name: 'Google revenue', color: GOOGLE, kind: 'money', dash: true },
    ] },
    retention: { title: 'New vs returning customers', lines: [{ key: 'newCustomers', name: 'New', color: t.purple, kind: 'int' }, { key: 'retCustomers', name: 'Returning', color: t.good, kind: 'int', pctKey: 'retRate' }] },
  };
  const lines = METRICS[metric].lines;
  const fmt = (kind: string, v: any) => (v == null ? '—' : kind === 'money' ? money(v, 0) : v.toLocaleString('en-US'));

  const n = points.length;
  const vals: number[] = [];
  for (const p of points) for (const l of lines) { const v = p[l.key]; if (v != null) vals.push(v); }
  const maxV = vals.length ? Math.max(...vals) : 1;
  const W = 1000, H = 300, padT = 14, padB = 26, padX = 8;
  const xAt = (i: number) => padX + (n <= 1 ? (W - 2 * padX) / 2 : (i / (n - 1)) * (W - 2 * padX));
  const yAt = (v: number) => padT + (1 - v / (maxV || 1)) * (H - padT - padB);
  const linePath = (l: any) => points.map((p, i) => (i ? 'L' : 'M') + xAt(i).toFixed(1) + ' ' + yAt(p[l.key] ?? 0).toFixed(1)).join(' ');
  const step = Math.max(1, Math.ceil(n / 7));
  const axisIdx = points.map((_, i) => i).filter((i) => i % step === 0 || i === n - 1);

  const seg = (active: boolean): React.CSSProperties => ({ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.06em', fontSize: 11, textTransform: 'uppercase', padding: '7px 13px', borderRadius: 999, cursor: 'pointer', background: active ? t.accent : 'transparent', color: active ? '#1a1330' : t.muted });
  const hp = hover != null && points[hover] ? points[hover] : null;

  return (
    <Card pad={22} style={{ marginTop: -4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ display: 'inline-flex', background: t.surface2, border: '1px solid ' + t.border, borderRadius: 999, padding: 3, flexWrap: 'wrap' }}>
          {([['revenue', 'Revenue'], ['paidads', 'Paid ads'], ['retention', 'Retention']] as const).map(([k, lab]) => (
            <span key={k} onClick={() => setMetric(k)} style={seg(metric === k)}>{lab}</span>
          ))}
        </div>
        <div style={{ display: 'inline-flex', background: t.surface2, border: '1px solid ' + t.border, borderRadius: 999, padding: 3 }}>
          {([['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']] as const).map(([k, lab]) => (
            <span key={k} onClick={() => setMode(k)} style={seg(mode === k)}>{lab}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 15, color: t.fg }}>{METRICS[metric].title}</span>
        <span style={{ display: 'flex', gap: 14, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {lines.map((l: any) => (
            <span key={l.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FIGTREE, fontSize: 12, color: t.fg2 }}>
              <span style={l.dash ? { width: 16, borderTop: '2px dashed ' + l.color } : { width: 16, height: 3, borderRadius: 999, background: l.color }} />{l.name}
            </span>
          ))}
        </span>
      </div>

      {n === 0 ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, padding: '28px 0' }}>{loading ? 'Loading trend…' : 'No data for this view yet — the daily feed is still warming up.'}</div>
      ) : (
        <div style={{ position: 'relative', touchAction: 'pan-y' }}
          onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1))))); }}
          onMouseLeave={() => setHover(null)}
          onTouchStart={(e) => { const p = e.touches[0]; if (!p) return; const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(n - 1, Math.round(((p.clientX - r.left) / r.width) * (n - 1))))); }}
          onTouchMove={(e) => { const p = e.touches[0]; if (!p) return; const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(n - 1, Math.round(((p.clientX - r.left) / r.width) * (n - 1))))); }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
            <line x1={padX} y1={H - padB} x2={W - padX} y2={H - padB} stroke={t.border} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            {hover != null && <line x1={xAt(hover)} y1={padT} x2={xAt(hover)} y2={H - padB} stroke={t.borderStrong} strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
            {lines.map((l: any) => <path key={l.key} d={linePath(l)} fill="none" stroke={l.color} strokeWidth="2.4" strokeDasharray={l.dash ? '5 4' : undefined} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />)}
            {hover != null && lines.map((l: any) => points[hover][l.key] != null && <circle key={l.key} cx={xAt(hover)} cy={yAt(points[hover][l.key])} r="4.5" fill={l.color} stroke={t.surface} strokeWidth="1.5" />)}
            {axisIdx.map((i) => <text key={i} x={xAt(i)} y={H - 8} fill={t.faint} fontSize="12" textAnchor="middle" fontFamily={FIGTREE}>{points[i].label}</text>)}
          </svg>
          {hp && (
            <div style={{ position: 'absolute', top: 0, left: `${Math.max(7, Math.min(93, (hover! / Math.max(1, n - 1)) * 100))}%`, transform: 'translateX(-50%)', pointerEvents: 'none', background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', padding: '9px 12px', minWidth: 128, zIndex: 5 }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11, color: t.fg, marginBottom: 6 }}>{hp.label}</div>
              {lines.map((l: any) => (
                <div key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, marginTop: 3, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: l.color, flexShrink: 0 }} />
                  <span style={{ color: t.muted }}>{l.name}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(l.kind, hp[l.key])}{l.pctKey && hp[l.pctKey] != null ? ` · ${hp[l.pctKey]}%` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---- Blog automation health banner ------------------------------------------
// Polls /api/blog/status (Shopify + Anthropic connectivity + the last successful
// build) and re-checks every 2 min while Daily Pulse is open. Three states, per the
// owner's spec: healthy · API not connecting (says which) · healthy + a blog cleaned up.
export function useBlogHealth() {
  const r = useCachedJson<any>('/api/blog/status');
  const refreshRef = React.useRef(r.refresh);
  refreshRef.current = r.refresh;
  React.useEffect(() => {
    const id = setInterval(() => refreshRef.current(), 120000);
    return () => clearInterval(id);
  }, []);
  return r.data as {
    health?: { at: string; shopify: { ok: boolean; error?: string }; anthropic: { ok: boolean; error?: string } } | null;
    lastBuilt?: { at: string; title: string; handle: string } | null;
  } | null;
}

function agoLabel(iso?: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

function BlogHealthBanner() {
  const t = useT();
  const bh = useBlogHealth();
  const health = bh?.health || null;
  const lastBuilt = bh?.lastBuilt || null;

  // Plain-English reason a service is down, from the stored probe error.
  const svcReason = (name: string, e?: string) => {
    const err = (e || '').toLowerCase();
    if (!e || err.includes('not_connected') || err.includes('token') || err.includes('no_anthropic_key'))
      return `${name} isn’t connected`;
    if (err.includes('401') || err.includes('403') || err.includes('unauthor'))
      return `${name} rejected the credentials`;
    return `${name} isn’t responding`;
  };

  let tone: 'good' | 'bad' | 'neutral' = 'neutral';
  let icon = 'pulse';
  let headline = 'Checking blog automation…';
  let detail: React.ReactNode = 'Running the first connectivity check.';

  if (health) {
    const down: string[] = [];
    if (!health.shopify.ok) down.push(svcReason('Shopify', health.shopify.error));
    if (!health.anthropic.ok) down.push(svcReason('Anthropic (Claude)', health.anthropic.error));
    if (down.length) {
      tone = 'bad'; icon = 'alert';
      headline = 'Blog automation API not connecting';
      detail = <>{down.join(' · ')}. Drafts tagged{' '}
        <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, background: t.badSoft, padding: '1px 5px', borderRadius: 5 }}>ready-to-build</span>{' '}
        will not build until this is fixed.</>;
    } else {
      tone = 'good'; icon = 'check';
      headline = 'Blog automation live and healthy';
      const builtRecent = lastBuilt && (Date.now() - new Date(lastBuilt.at).getTime()) < 24 * 3600 * 1000;
      detail = builtRecent
        ? <><b style={{ color: t.good }}>“{lastBuilt!.title}”</b> has been successfully cleaned up ({agoLabel(lastBuilt!.at)}).</>
        : <>Shopify + Anthropic connections verified — tagged drafts build automatically.</>;
    }
  }

  const fg = tone === 'bad' ? t.bad : tone === 'good' ? t.good : t.muted;
  const soft = tone === 'bad' ? t.badSoft : tone === 'good' ? t.goodSoft : 'transparent';

  return (
    <>
      <PulseLabel>Blog Automation</PulseLabel>
      <Card pad={14} style={{ marginTop: -4, borderLeft: '3px solid ' + fg, background: soft || undefined, display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: tone === 'neutral' ? t.border : soft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={icon} size={15} color={fg} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, fontWeight: 700, color: fg }}>{headline}</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.55, marginTop: 2 }}>{detail}</div>
        </div>
        {health?.at && (
          <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2 }}>
            checked {agoLabel(health.at)}
          </span>
        )}
      </Card>
    </>
  );
}

// One-line SEO alert on Daily Pulse: a single banner when the SEO tab has open
// issues (Merchant disapprovals, feed/indexing warnings). Clicking opens the SEO
// tab for the full list — so the Pulse stays uncluttered but nothing slips by.
function SeoPulseBanner({ onJump }: any) {
  const t = useT();
  const { data } = useCachedJson<any>('/api/seo');
  const c = data?.counts;
  const total = (c?.critical || 0) + (c?.warning || 0);
  if (!data || total === 0) return null;
  const crit = c.critical || 0;
  const col = crit > 0 ? t.bad : t.warn;
  return (
    <Card pad={13} onClick={() => onJump && onJump('seo')} hoverable style={{ borderLeft: '3px solid ' + col, display: 'flex', gap: 11, alignItems: 'center', cursor: 'pointer' }}>
      <Icon name="search" size={16} color={col} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>{total} SEO {total === 1 ? 'item' : 'items'} to check</span>
        <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginLeft: 8 }}>{crit > 0 ? crit + ' critical · ' : ''}across Search Console, GA4 &amp; Merchant</span>
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: t.accent, whiteSpace: 'nowrap' }}>Open SEO <Icon name="chevron" size={13} color={t.accent} /></span>
    </Card>
  );
}

// Separate Daily Pulse banner: unread customer email sitting in the boostblenz@gmail
// account (which the owner doesn't check). Clears itself once the mail is read in
// Gmail. Opens Gmail directly — until the Help Desk inbox is built here.
function CustomerMailBanner() {
  const t = useT();
  const { data } = useCachedJson<any>('/api/inbox');
  const count = data?.count || 0;
  if (!count) return null;
  const latest = data.items && data.items[0];
  return (
    <Card pad={13} onClick={() => window.open('https://mail.google.com/mail/u/0/#inbox', '_blank')} hoverable style={{ borderLeft: '3px solid ' + t.purple, display: 'flex', gap: 11, alignItems: 'center', cursor: 'pointer' }}>
      <Icon name="mail" size={16} color={t.purple} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>{count} customer {count === 1 ? 'email' : 'emails'} waiting in Gmail</span>
        <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginLeft: 8 }}>{latest ? '· latest: ' + String(latest.subject || '').slice(0, 60) : 'go check the boostblenz inbox'}</span>
      </div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: t.accent, whiteSpace: 'nowrap' }}>Open Gmail <Icon name="chevron" size={13} color={t.accent} /></span>
    </Card>
  );
}

// ---- Company Value (foot of Daily Pulse) ------------------------------------
// A ballpark valuation off the same books the CPA would use: TTM EBITDA from the
// QuickBooks accrual P&L (/api/valuation), normalized for owner pay, times the
// operating agreement's 2.5×–3.75× range. The point is the TREND — a number that
// moves as the business grows — not a defensible price.
//
// THE OWNER-PAY LEVER (why the chips exist): reported EBITDA is inflated by both
// owners drawing far under market. A buyer prices the company with someone paid
// properly in those seats, so the chip picks that scenario and the whole card
// re-computes: adjusted EBITDA = reported EBITDA + owner pay actually booked −
// the market comp you pick. All client-side — the endpoint only reports the books.
const OWNER_COMP_SCENARIOS = [
  { comp: 54000, label: '$54k', note: 'your stated salaries — $27k each, what the books are built on' },
  { comp: 120000, label: '$120k', note: 'one full-time operator running the business at market pay' },
  { comp: 160000, label: '$160k', note: 'two market-rate seats — closest to what you two actually do' },
  { comp: 200000, label: '$200k', note: 'a full replacement bench, the most conservative read' },
];
const MULT_LOW = 2.5;
const MULT_HIGH = 3.75;
const COMP_KEY = 'bb.valuation.ownercomp';

const moneyK = (n: number) => {
  const a = Math.abs(n);
  const s = n < 0 ? '-' : '';
  if (a >= 1_000_000) return s + '$' + (a / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 2) + 'M';
  if (a >= 1_000) return s + '$' + Math.round(a / 1000) + 'k';
  return s + '$' + Math.round(a);
};

function ValRow({ label, sub, value, valueColor, strong }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '8px 0', borderTop: '1px solid ' + t.border }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: FIGTREE, fontWeight: strong ? 700 : 600, fontSize: 13, color: strong ? t.fg : t.fg2 }}>{label}</div>
        {sub && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: strong ? 15 : 13.5, fontVariantNumeric: 'tabular-nums', color: valueColor || t.fg, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function CompanyValueCard() {
  const t = useT();
  const isMobile = useIsMobile();
  const { data } = useCachedJson<any>('/api/valuation');
  const [comp, setComp] = React.useState<number>(() => {
    const saved = typeof window !== 'undefined' ? parseInt(window.localStorage.getItem(COMP_KEY) || '', 10) : NaN;
    return OWNER_COMP_SCENARIOS.some((s) => s.comp === saved) ? saved : 160000;
  });
  const pick = (c: number) => { setComp(c); try { window.localStorage.setItem(COMP_KEY, String(c)); } catch { /* private mode */ } };

  // Silent when QuickBooks isn't connected, the pull failed, or the viewer lacks
  // the `profit` permission (the API 403s → data stays null). Never a broken card.
  if (!data || data.ok !== true || !data.ttm) return null;

  const scenario = OWNER_COMP_SCENARIOS.find((s) => s.comp === comp) || OWNER_COMP_SCENARIOS[2];
  const adjust = (ebitda: number, ownerComp: number) => ebitda + ownerComp - comp;
  const adjusted = adjust(data.ttm.ebitda, data.ttm.ownerComp);
  const margin = data.ttm.revenue > 0 ? (adjusted / data.ttm.revenue) * 100 : 0;
  const low = Math.max(0, adjusted * MULT_LOW);
  const high = Math.max(0, adjusted * MULT_HIGH);

  // Rolling-TTM history at the SAME scenario, so the trend line isn't distorted by
  // when payroll happened to be booked. Needs 2+ points to say anything.
  const series: any[] = (data.series || []).map((p: any) => ({ ...p, adj: adjust(p.ebitda, p.ownerComp) }));
  const trend = series.map((p) => Math.max(0, p.adj * MULT_HIGH));
  const first = series[0];
  const lastAdj = series.length ? series[series.length - 1].adj : adjusted;
  const growth = series.length >= 2 ? (lastAdj - first.adj) * MULT_HIGH : null;
  const negative = adjusted <= 0;

  return (
    <Card pad={22} style={{ borderLeft: '3px solid ' + t.purple }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Icon name="chart" size={16} color={t.purple} />
        <Title size={14}>Company Value</Title>
        <Badge tone="neutral">Ballpark</Badge>
        <span style={{ marginLeft: 'auto', fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>
          Trailing 12 months · {data.window.label}{data.window.partial ? ' (annualized)' : ''}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap: isMobile ? 18 : 26 }}>
        {/* LEFT — the number + how it has moved */}
        <div>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: isMobile ? 30 : 38, letterSpacing: '-0.01em', color: negative ? t.muted : t.fg, lineHeight: 1.05 }}>
            {negative ? 'Below a multiple' : `${moneyK(low)} – ${moneyK(high)}`}
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 6, lineHeight: 1.5 }}>
            {negative
              ? `At ${scenario.label} of owner pay the business doesn't clear a profit yet, so an earnings multiple gives no value. Lower the scenario or grow EBITDA.`
              : `${MULT_LOW}× – ${MULT_HIGH}× adjusted EBITDA of ${money(adjusted, 0)} — the range in your operating agreement.`}
          </div>

          {trend.length >= 2 && (
            <div style={{ marginTop: 16 }}>
              <Sparkline data={trend} color={t.purple} height={44} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 5 }}>
                <span>{first.label}</span>
                <span>{series[series.length - 1].label}</span>
              </div>
              {growth != null && (
                <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: growth >= 0 ? t.good : t.bad, fontWeight: 700, marginTop: 6 }}>
                  {growth >= 0 ? '▲' : '▼'} {moneyK(Math.abs(growth))} at the high end since {first.label}
                  <span style={{ color: t.faint, fontWeight: 600 }}> · each point is a full trailing year</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — the arithmetic, in the order it happens */}
        <div>
          <PulseLabel>If both owners were paid market wages</PulseLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 4px' }}>
            {OWNER_COMP_SCENARIOS.map((s) => {
              const on = s.comp === comp;
              return (
                <button key={s.comp} onClick={() => pick(s.comp)} title={s.note} style={{
                  fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: '5px 11px', borderRadius: 999, cursor: 'pointer',
                  border: '1px solid ' + (on ? t.purple : t.border), background: on ? t.purpleSoft : 'transparent', color: on ? t.fg : t.muted,
                }}>{s.label}</button>
              );
            })}
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.5, marginBottom: 10 }}>{scenario.note}</div>

          <ValRow label="Revenue" sub="trailing 12 months" value={money(data.ttm.revenue, 0)} />
          <ValRow label="EBITDA as booked" sub={`${data.ttm.revenue > 0 ? pctS((data.ttm.ebitda / data.ttm.revenue) * 100) : '—'} margin${data.ttm.addBacks.length ? ' · incl. ' + data.ttm.addBacks.length + ' add-back' + (data.ttm.addBacks.length > 1 ? 's' : '') : ''}`} value={money(data.ttm.ebitda, 0)} />
          <ValRow label="Owner pay actually booked" sub="added back, then charged at market" value={'+' + money(data.ttm.ownerComp, 0)} valueColor={t.good} />
          <ValRow label={`Market owner pay (${scenario.label})`} value={'−' + money(comp, 0)} valueColor={t.bad} />
          <ValRow label="Adjusted EBITDA" sub={`${pctS(margin)} margin`} value={money(adjusted, 0)} valueColor={negative ? t.bad : t.fg} strong />
        </div>
      </div>

      {(data.skipped || []).length > 0 && (
        <div style={{ marginTop: 14, display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 12px', borderRadius: t.tileRadius, background: t.warnSoft }}>
          <Icon name="alert" size={14} color={t.warn} style={{ marginTop: 1 }} />
          <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2, lineHeight: 1.5 }}>
            <b style={{ color: t.warn }}>{data.skipped.map((s: any) => s.label).join(', ')} left out</b> — {data.skipped[0].reason}. The window ends at the last month that looks closed.
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.55 }}>
        Directional only — a real number comes from your CPA and an actual buyer. This ignores customer concentration, channel risk, working capital, inventory, and cash on hand, and it assumes the multiple in the operating agreement still applies. Books are read straight from QuickBooks (accrual), so it is only as current as your bookkeeping.
      </div>
    </Card>
  );
}

export function OverviewView({ onJump }: any) {
  const t = useT();
  const isMobile = useIsMobile();
  const rm = useRangeMetrics("today");
  const health = useIntegrationHealth();
  const status = useSystemStatus();
  // eBook bought/free split — fetched separately (order-line scan) so it never
  // blocks the main Pulse metrics. Re-runs when the date range changes.
  const [ebooks, setEbooks] = React.useState<any>(null);
  React.useEffect(() => {
    const p = new URLSearchParams({ range: rm.range });
    if (rm.range === 'custom') {
      if (!(rm.custom?.start && rm.custom?.end)) return; // wait for a complete custom range
      p.set('start', rm.custom.start); p.set('end', rm.custom.end);
    }
    setEbooks(null);
    fetch(`/api/ebooks/breakdown?${p.toString()}`).then((r) => (r.ok ? r.json() : null)).then((x) => setEbooks(x && x.ok !== false ? x : null)).catch(() => setEbooks(null));
  }, [rm.range, rm.custom?.start, rm.custom?.end]);
  const dd = rm.data?.day || {};
  const shopDown = !!dd.shopifyDown; // Shopify unreachable (analytics + Admin API)
  const shopApprox = !!dd.shopifyApprox; // analytics down, showing raw Admin-API estimate
  const d: any = { ...F().day, ...dd };
  if (shopDown) for (const k of SHOPIFY_DAY_FIELDS) d[k] = null;
  const issues: any[] = status?.issues || [];
  const sevCfg: any = { down: [t.bad, t.badSoft], degraded: [t.warn, t.warnSoft] };
  const weekDrift = useWeekDrift(); // current Sun–Sat week cost-drift → Morning Brief alert
  const monthPay = useMonthPayroll(); // current-month payroll coverage → Morning Brief alert
  const cf = useCfUsage(); // Cloudflare capacity → health dot + threshold alert
  const cfAlert = cf.connected && cf.peak >= 50; // amber ≥50, red ≥80 (matches the gauges)
  const cfCritical = cf.connected && cf.peak >= 80;
  const cfPct = cf.peak < 0.1 ? '<0.1' : cf.peak;
  const cfDot = cfCritical ? t.bad : cfAlert ? t.warn : t.good;
  const cfSoft = cfCritical ? t.badSoft : cfAlert ? t.warnSoft : t.goodSoft;
  // Shop App has no API — remind (once we're into a new month) to upload the month's
  // snapshot. Fires when the saved snapshot's capture month is behind the current month.
  const shopCaptured: string | null = rm.data?.shopApp?.capturedAt || null;
  const nowYm = new Date().toISOString().slice(0, 7);
  const shopReminder = !!shopCaptured && shopCaptured.slice(0, 7) < nowYm;
  const shopCapturedLabel = shopCaptured ? new Date(shopCaptured + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';

  // Short range word for section labels ("Business Vitals · Today")
  const rangeWord = rm.range === 'custom' ? 'Custom' : (RANGE_OPTS.find((o) => o.id === rm.range)?.label || 'Today');
  const kpi = useKpiTargets(); // live breakeven/target ROAS + ad ceiling (KPI Targets page)
  const adPctHot = d.adSpendPctRev != null && d.adSpendPctRev >= kpi.adCeiling;
  const roasAbove = d.roas >= kpi.breakeven;
  // Marketing Efficiency threshold bar: axis scaled so the blended target (the
  // highest of the three thresholds) sits at ~85%, leaving headroom on the right;
  // current ROAS is never clipped off the end.
  const roasAxisMax = Math.max(kpi.targetBlended, d.roas, kpi.target) / 0.85;
  const roasPct = (v: number) => Math.max(0, Math.min(100, (v / roasAxisMax) * 100));
  const roasFill = roasPct(d.roas);
  const bePct = roasPct(kpi.breakeven);
  const tgtPct = roasPct(kpi.target);
  const blPct = roasPct(kpi.targetBlended);
  // Which band is current ROAS in? (drives the header status pill's word + color)
  const roasZone = d.roas >= kpi.targetBlended ? { label: 'above blended target', color: t.good }
    : d.roas >= kpi.target ? { label: 'above target', color: t.good }
    : d.roas >= kpi.breakeven ? { label: 'below target', color: t.warn }
    : { label: 'below breakeven', color: t.bad };
  // Customers split bar
  const newC = d.newCustomers, retC = d.returningCustomers;
  const custTotal = (newC ?? 0) + (retC ?? 0);
  const newW = custTotal > 0 ? ((newC ?? 0) / custTotal) * 100 : 0;
  // Funnel bar widths (visitors = 100%)
  const funnelW = (v: any) => (v != null && d.visitors ? Math.max(1.5, (v / d.visitors) * 100) : 0);
  const aovVal = d.aov != null ? money(d.aov) : (d.revenue != null && d.orders ? money(d.revenue / d.orders) : '—');
  // Integration health live count (Cloudflare dot included when connected)
  const intStatuses = F().integrations.map((i: any) => resolveIntegrationStatus(i, health));
  const intLive = intStatuses.filter((s: string) => s === 'live').length + (cf.connected && !cfAlert ? 1 : 0);
  const intTotal = F().integrations.length + (cf.connected ? 1 : 0);
  const intBad = F().integrations.filter((i: any, ix: number) => intStatuses[ix] === 'down' || intStatuses[ix] === 'degraded');

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* header — page title + subtitle left, range pills + refresh right */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 6 }}>
        <div>
          <Title size={isMobile ? 24 : 32}>Daily Pulse</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>The health of the business at a glance · {rm.data?.label || d.date}</div>
        </div>
        <DateRangePicker range={rm.range} setRange={rm.setRange} custom={rm.custom} setCustom={rm.setCustom} onRefresh={() => { rm.refresh(); }} loading={rm.loading} />
      </div>

      <SeoPulseBanner onJump={onJump} />
      <CustomerMailBanner />

      {(shopDown || shopApprox) && (
        <Card pad={13} style={{ borderLeft: '3px solid ' + (shopDown ? t.bad : t.warn), display: 'flex', gap: 11, alignItems: 'flex-start' }}>
          <Icon name="alert" size={16} color={shopDown ? t.bad : t.warn} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.55 }}>
            {shopDown
              ? <>Shopify is temporarily unreachable — live sales figures are paused (shown as “—”) rather than risk a wrong number. They’ll return automatically when Shopify recovers.</>
              : <><b style={{ color: t.warn }}>Shopify analytics is degraded.</b> Order count is exact for this range; revenue is an <b>estimate</b> (gross, from the orders API) until Shopify’s analytics recovers.</>}
          </div>
        </Card>
      )}

      {/* ---- SECTION 1 · BUSINESS VITALS (hero row) ---- */}
      <PulseLabel>Business Vitals · {rangeWord}</PulseLabel>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 16, marginTop: -4 }}>
        <FitGroup>
        <HeroCard icon="pulse" tint={t.accent} tintBg={t.accentSoft} label="Revenue"
          value={d.revenue != null ? money(d.revenue, 0) + (shopApprox ? '*' : '') : '—'}
          foot={d.revenueDelta != null ? <DeltaPill pct={d.revenueDelta} /> : <span style={{ color: t.muted }}>{shopApprox ? '* estimate — analytics degraded' : 'net of discounts/returns'}</span>} />
        <HeroCard icon="zap" tint={t.warn} tintBg={t.warnSoft} label="Ad spend"
          value={money(d.adSpend, 0)}
          foot={<>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {d.adSpendDelta != null && <DeltaPill pct={d.adSpendDelta} goodWhenUp={false} suffix="" />}
              <span style={{ color: adPctHot ? t.warn : t.muted, fontWeight: 600 }}>{d.adSpendPctRev != null ? pctS(d.adSpendPctRev) + ` of revenue · ceiling ${kpi.adCeiling}%` : `ceiling ${kpi.adCeiling}%`}{d.adSpendProvisional ? ' · provisional' : ''}</span>
            </span>
            <AdSplit d={d} />
          </>} />
        <HeroCard icon="spark" tint={t.good} tintBg={t.goodSoft} label="Blended ROAS"
          value={d.roas.toFixed(2)}
          foot={<>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {d.roasDelta != null && <DeltaPill pct={d.roasDelta} suffix="" />}
              <span style={{ color: roasAbove ? t.good : t.bad, fontWeight: 600 }}>{roasAbove ? 'above' : 'below'} breakeven ~{kpi.breakeven.toFixed(2)}{d.adSpendProvisional ? ' · provisional' : ''}</span>
            </span>
            {/* what's in the denominator — revenue ÷ COMBINED spend */}
            <AdSplit d={d} prefix="revenue ÷" />
          </>} />
        <HeroCard icon="cart" tint={t.purple} tintBg={t.purpleSoft} label="Total orders"
          value={d.orders != null ? d.orders.toLocaleString() : '—'}
          foot={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {d.ordersDelta != null && <DeltaPill pct={d.ordersDelta} suffix="" />}
            <span style={{ color: t.muted }}>{(d.newOrders != null ? d.newOrders.toLocaleString() : '—') + ' new · ' + (d.returningOrders != null ? d.returningOrders.toLocaleString() : '—') + ' returning'}</span>
          </span>} />
        </FitGroup>
      </div>

      {/* ---- eBook sales (digital product line) — PAID copies only; full page under Books ---- */}
      {ebooks && ebooks.bought > 0 ? (
        <>
          <PulseLabel>Digital Products · {rangeWord}</PulseLabel>
          <Card pad={22} style={{ marginTop: -4 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <PanelHead dot={t.claude} mb={0}>eBook Sales</PanelHead>
              <Btn variant="ghost" size="sm" icon="chevron" onClick={() => onJump && onJump('books')}>View all books</Btn>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14, borderTop: '1px solid ' + t.border, paddingTop: 15, marginTop: 14 }}>
              <FitGroup>
              <FootStat size={28} value={(ebooks.bought ?? 0).toLocaleString()} valueColor="#fff" cap="books bought" />
              <FootStat size={28} value={money(ebooks.revenue ?? 0, 0)} valueColor={t.good} cap="ebook revenue" />
              <FootStat size={28} value={d.revenue > 0 ? Math.round((ebooks.revenue / d.revenue) * 1000) / 10 + '%' : '—'} cap="of total sales" />
              <FootStat size={28} value={ebooks.books?.[0] ? ebooks.books[0].bought.toLocaleString() : '—'} cap={ebooks.books?.[0] ? 'best · ' + ebooks.books[0].name : 'best seller'} />
              </FitGroup>
            </div>
          </Card>
        </>
      ) : null}

      {/* ---- SECTION 2 · TODAY'S BREAKDOWN (combined cards) ---- */}
      <PulseLabel>{rangeWord === 'Today' ? "Today's Breakdown" : 'Breakdown · ' + rangeWord}</PulseLabel>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 16, marginTop: -4 }}>
        {/* Revenue Breakdown — revenue + gross sales + orders + AOV */}
        <Card pad={22}>
          <PanelHead dot={t.accent} mb={14}>Revenue Breakdown</PanelHead>
          <FitValue size={32}>{d.revenue != null ? money(d.revenue) + (shopApprox ? '*' : '') : '—'}</FitValue>
          <div style={{ margin: '9px 0 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <DeltaPill pct={d.revenueDelta} suffix="" />
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{shopApprox ? 'estimate — analytics degraded' : 'net of discounts/returns'}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, borderTop: '1px solid ' + t.border, paddingTop: 15 }}>
            <FitGroup>
            <FootStat value={d.grossSales != null ? money(d.grossSales, 0) : '—'} valueColor={d.grossSales != null ? t.fg : '#7a7099'} cap="gross sales" />
            <FootStat value={d.orders != null ? d.orders : '—'} cap={d.ordersDelta != null ? <>orders <span style={{ color: d.ordersDelta >= 0 ? t.good : t.bad }}>{d.ordersDelta >= 0 ? '▲' : '▼'}{Math.abs(d.ordersDelta)}%</span></> : 'orders'} />
            <FootStat value={aovVal} cap={d.aovDelta != null ? <>AOV <MiniDelta pct={d.aovDelta} /></> : 'AOV'} />
            </FitGroup>
          </div>
        </Card>

        {/* Customers — new vs returning split */}
        <Card pad={22}>
          <PanelHead dot={t.purple}>Customers</PanelHead>
          <div style={{ display: 'flex', height: 10, borderRadius: 999, overflow: 'hidden', marginBottom: 10, background: 'rgba(255,255,255,0.06)' }}>
            <div style={{ width: newW + '%', background: t.purple, transition: 'width 240ms' }} />
            <div style={{ width: (custTotal > 0 ? 100 - newW : 0) + '%', background: t.accent, transition: 'width 240ms' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.purple }}>● New buyers</span>
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.accent }}>Returning ●</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, borderTop: '1px solid ' + t.border, paddingTop: 15 }}>
            <FitGroup>
            <FootStat size={24} value={newC != null ? newC.toLocaleString() : '—'} valueColor="#fff" cap="new · first-time" />
            <FootStat size={24} value={retC != null ? retC.toLocaleString() : '—'} valueColor="#fff" cap="returning buyers" />
            <FootStat size={24} value={d.returningRate != null ? d.returningRate + '%' : '—'} valueColor={t.good}
              cap={d.returningRateDeltaPp != null ? <span>returning rate <MiniDelta pp={d.returningRateDeltaPp} /></span> : 'returning rate'} />
            </FitGroup>
          </div>
        </Card>

        {/* Profitability — net / margins, awaiting-cost state preserved */}
        <Card pad={22}>
          <PanelHead dot={t.good} right={dd.net == null
            ? <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.06em', fontSize: 9.5, color: t.warn, background: t.warnSoft, padding: '3px 9px', borderRadius: 999, textTransform: 'uppercase' }}>{dd.costUnavailable ? 'Cost data paused' : 'Awaiting cost data'}</span>
            : dd.profitEstimate ? <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.06em', fontSize: 9.5, color: t.muted, background: t.surface2, padding: '3px 9px', borderRadius: 999, textTransform: 'uppercase' }}>Estimate</span> : null}>Profitability</PanelHead>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
            <FitGroup>
            {[
              { k: 'Net Profit', v: dd.net != null ? money(dd.net) : null, c: dd.net != null ? (dd.net >= 0 ? t.good : t.bad) : null, delta: dd.net != null && dd.netDeltaAbs != null ? <MiniDelta abs={dd.netDeltaAbs} /> : null },
              { k: 'Gross Margin', v: dd.grossMargin != null ? dd.grossMargin + '%' : null, c: dd.grossMargin != null && dd.grossMargin < 0 ? t.bad : null, delta: dd.grossMargin != null && dd.grossMarginDeltaPp != null ? <MiniDelta pp={dd.grossMarginDeltaPp} /> : null },
              { k: 'Net Margin', v: dd.netMargin != null ? dd.netMargin + '%' : null, c: dd.netMargin != null && dd.netMargin < 0 ? t.bad : null, delta: dd.netMargin != null && dd.netMarginDeltaPp != null ? <MiniDelta pp={dd.netMarginDeltaPp} /> : null },
            ].map((m) => (
              <div key={m.k} style={{ minWidth: 0 }}>
                <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{m.k}</div>
                <FitValue size={24} color={m.v != null ? (m.c || '#fff') : '#7a7099'}>{m.v != null ? m.v : '—'}</FitValue>
                {m.delta && <div style={{ fontFamily: FIGTREE, fontSize: 11, marginTop: 3 }}>{m.delta} <span style={{ color: t.faint }}>vs prior</span></div>}
              </div>
            ))}
            </FitGroup>
          </div>
          <div style={{ marginTop: 16, fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, borderTop: '1px solid ' + t.border, paddingTop: 12, lineHeight: 1.5 }}>
            {dd.net == null
              ? <>Add per-unit cost in <span onClick={() => onJump && onJump('unit')} style={{ color: t.purple, cursor: 'pointer', fontWeight: 600 }}>Per-Unit Cost</span> to unlock profit metrics.</>
              : <>{dd.overheadPending ? 'Before overhead/OpEx (QuickBooks pending)' : 'After overhead'} · full P&L below.</>}
          </div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        {/* Traffic & Funnel — visitors / add-to-cart / conversion + funnel bars */}
        <Card pad={22}>
          <PanelHead dot={t.purple} mb={18}>Traffic &amp; Funnel</PanelHead>
          <div style={{ display: 'flex', gap: 32, marginBottom: 20, flexWrap: 'wrap' }}>
            <FitGroup>
            <FootStat size={24} value={d.visitors != null ? d.visitors.toLocaleString() : '—'} valueColor="#fff" cap="unique visitors" />
            <FootStat size={24} value={d.atc != null ? d.atc.toLocaleString() : '—'} valueColor="#fff" cap="add to cart" />
            <FootStat size={24} value={d.cvr != null ? d.cvr + '%' : '—'} valueColor={d.cvr != null ? '#fff' : '#7a7099'} cap={d.cvr != null && d.cvrDeltaPp != null ? <>conversion rate <MiniDelta pp={d.cvrDeltaPp} /></> : 'conversion rate'} />
            </FitGroup>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {[
              { label: 'Visitors', v: d.visitors, full: true },
              { label: 'Add to cart', v: d.atc },
              { label: 'Orders', v: d.orders, color: t.accent },
            ].map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 84, flexShrink: 0, fontFamily: FIGTREE, fontSize: 11, color: t.muted }}>{r.label}</span>
                <div style={{ flex: 1, height: 13, borderRadius: 999, background: r.full ? 'rgba(185,169,255,0.45)' : 'rgba(255,255,255,0.05)' }}>
                  {!r.full && <div style={{ width: funnelW(r.v) + '%', height: '100%', borderRadius: 999, background: r.color || 'rgba(185,169,255,0.9)' }} />}
                </div>
                <span style={{ width: 46, flexShrink: 0, textAlign: 'right', fontFamily: FIGTREE, fontSize: 12, color: t.fg2, fontWeight: 700 }}>{r.v != null ? r.v.toLocaleString() : '—'}</span>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 12 }}>
            {[
              d.atc != null ? `${d.atc.toLocaleString()} sessions with a cart add` : null,
              d.cvr == null ? 'conversion awaiting attribution' : (d.sessions != null ? `${d.sessions.toLocaleString()} sessions` : null),
            ].filter(Boolean).join(' · ')}
          </div>
        </Card>

        {/* Marketing Efficiency — ROAS + thresholds + spend + email revenue */}
        <Card pad={22}>
          <PanelHead dot={t.accent}>Marketing Efficiency</PanelHead>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 32, color: '#fff', lineHeight: 1 }}>{d.roas.toFixed(2)}</span>
            <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>blended ROAS</span>
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: roasZone.color, fontWeight: 600, marginLeft: 'auto' }}>{roasZone.label}</span>
          </div>
          <div style={{ position: 'relative', height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.08)', marginBottom: 6 }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: roasFill + '%', borderRadius: 999, background: `linear-gradient(90deg,${t.good},${t.purple})` }} />
            <div style={{ position: 'absolute', left: bePct + '%', top: -3, bottom: -3, width: 2, background: t.bad }} />
            <div style={{ position: 'absolute', left: tgtPct + '%', top: -3, bottom: -3, width: 2, background: t.fg2 }} />
            <div style={{ position: 'absolute', left: blPct + '%', top: -3, bottom: -3, width: 2, background: t.good }} />
          </div>
          {/* labels centered under each tick — breakeven / target / blended target */}
          <div style={{ position: 'relative', height: 38, marginBottom: 14 }}>
            {[
              { pct: bePct, name: 'breakeven', val: kpi.breakeven, color: t.bad },
              { pct: tgtPct, name: 'target', val: kpi.target, color: t.fg2 },
              { pct: blPct, name: 'blended target', val: kpi.targetBlended, color: t.good },
            ].map((m) => (
              <span key={m.name} onClick={() => onJump && onJump('kpi')} title="Set on KPI Targets"
                style={{ position: 'absolute', left: Math.max(6, Math.min(94, m.pct)) + '%', top: 0, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', lineHeight: 1.15 }}>
                <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 12, color: m.color, fontVariantNumeric: 'tabular-nums' }}>{m.val.toFixed(2)}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 9, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'center', maxWidth: 54, lineHeight: 1.1 }}>{m.name}</span>
              </span>
            ))}
            {!kpi.live && <span style={{ position: 'absolute', right: 0, bottom: -2, fontFamily: FIGTREE, fontSize: 9, color: t.faint }}>(fallback)</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, borderTop: '1px solid ' + t.border, paddingTop: 15 }}>
            <FitGroup>
            <FootStat size={20} value={money(d.adSpend)} cap={d.adSpendProvisional ? 'ad spend · provisional' : `ad spend · ${d.adSourcesLabel || 'all channels'}`} />
            <FootStat size={20} value={d.adSpendPctRev != null ? pctS(d.adSpendPctRev) : '—'} valueColor={adPctHot ? t.warn : t.fg} cap={adPctHot ? `of revenue · over ${kpi.adCeiling}%` : `of revenue · ceiling ${kpi.adCeiling}%`} />
            <FootStat size={20} value={money(d.emailRev != null ? d.emailRev : 0)} cap={<span onClick={() => onJump && onJump('email')} style={{ cursor: 'pointer' }}>email rev{d.emailRevPct != null ? ' · ' + d.emailRevPct + '%' : ''}</span>} />
            </FitGroup>
          </div>
          {/* every channel in that combined figure, spelled out */}
          <AdSplit d={d} style={{ marginTop: 12, fontSize: 12 }} />
          {d.adDegraded && d.adDegraded.length > 0 && (
            <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.warn, marginTop: 8, lineHeight: 1.5 }}>
              {d.adDegraded.join(' + ')} {d.adDegraded.length > 1 ? 'are' : 'is'} not reporting — that spend is missing from the total, so ad % reads low and blended ROAS reads high.
            </div>
          )}
        </Card>
      </div>

      {/* ---- SECTION 3 · PROGRESSION (interactive trend chart) ---- */}
      <PulseLabel>Progression</PulseLabel>
      <ProgressionChart />

      {/* ---- SECTION 4 · CASH, CREDIT & HEALTH ---- */}
      <PulseLabel>Cash, Credit &amp; Health</PulseLabel>
      <CashCard />

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.5fr) minmax(0,1fr)', gap: 16 }}>
        <Card pad={22}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <Title size={14}>Morning Brief</Title>
            <Badge tone="claude"><ClaudeMark size={11} /> Claude, on the dash</Badge>
            <span style={{ marginLeft: 'auto' }}>
              {status == null
                ? <Badge tone="neutral">Checking…</Badge>
                : issues.length === 0 && !cfAlert && !shopReminder
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: t.good, fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.05em', fontSize: 10.5, textTransform: 'uppercase' }}><span style={{ width: 6, height: 6, borderRadius: 999, background: t.good }} />All systems operational</span>
                  : <Badge tone={issues.some((i) => i.severity === 'down') || cfCritical ? 'bad' : 'warn'}>{issues.length + (cfAlert ? 1 : 0) + (shopReminder ? 1 : 0)} active {(issues.length + (cfAlert ? 1 : 0) + (shopReminder ? 1 : 0)) > 1 ? 'issues' : 'issue'}</Badge>}
            </span>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {status == null && (
              <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, padding: '12px 14px' }}>Checking every integration…</div>
            )}
            {status != null && issues.length === 0 && !cfAlert && !shopReminder && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.good, background: t.surface2 }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: t.goodSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="check" size={14} color={t.good} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>All systems operational</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>Every connected integration is responding — the live numbers on this page are current. This panel will flag any down or degraded source in plain English, with the fix.</div>
                </div>
              </div>
            )}
            {issues.map((iss: any) => {
              const [c, bg] = sevCfg[iss.severity] || sevCfg.down;
              return (
                <div key={iss.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + c, background: t.surface2 }}>
                  <span style={{ width: 26, height: 26, borderRadius: 999, background: bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="alert" size={14} color={c} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>{iss.title} <span style={{ color: t.faint, fontWeight: 600 }}>· {iss.source}</span></div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>{iss.detail}</div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5, marginTop: 6 }}><b style={{ color: c }}>Fix:</b> {iss.fix}</div>
                  </div>
                  <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, whiteSpace: 'nowrap' }}>active {sinceLabel(iss.since)}</span>
                </div>
              );
            })}
            {weekDrift.hasData && weekDrift.over.length > 0 && (
              <div onClick={() => onJump && onJump('allocate')} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.warn, background: t.surface2, cursor: 'pointer' }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: t.warnSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="percent" size={14} color={t.warn} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>Profit-First: {weekDrift.over.length} {weekDrift.over.length > 1 ? 'buckets' : 'bucket'} over target <span style={{ color: t.faint, fontWeight: 600 }}>· this week</span></div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>{weekDrift.over.map((d) => `${d.label} +${d.deltaPp}pp`).join(' · ')}{weekDrift.partial ? ' (excl. paused QuickBooks overhead)' : ''}.</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5, marginTop: 6 }}><b style={{ color: t.warn }}>Fix:</b> open Profit-First to trim the bucket or adjust its target.</div>
                </div>
                <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, whiteSpace: 'nowrap' }}>{weekDrift.label.replace('Week of ', '')}</span>
              </div>
            )}
            {monthPay.hasData && !monthPay.onPace && monthPay.dayOfMonth >= 5 && (
              <div onClick={() => onJump && onJump('allocate')} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.bad, background: t.surface2, cursor: 'pointer' }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: t.badSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="dollar" size={14} color={t.bad} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>Payroll: on pace to fall {money(monthPay.shortfall, 0)} short <span style={{ color: t.faint, fontWeight: 600 }}>· this month</span></div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>Owners Pay is set to collect ~{money(monthPay.projected, 0)} vs your {money(monthPay.target, 0)} target (day {monthPay.dayOfMonth} of {monthPay.daysInMonth}).</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5, marginTop: 6 }}><b style={{ color: t.bad }}>Fix:</b> lift ad efficiency or trim a bucket to free up the residual for Owners Pay.</div>
                </div>
              </div>
            )}
            {cfAlert && (
              <div onClick={() => onJump && onJump('integrations')} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + cfDot, background: t.surface2, cursor: 'pointer' }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: cfSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="zap" size={14} color={cfDot} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>Cloudflare: {cf.top?.label || 'a metric'} at {cfPct}% of its monthly limit <span style={{ color: t.faint, fontWeight: 600 }}>· {cfCritical ? 'critical' : 'watch'}</span></div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>Infrastructure usage is climbing toward a plan limit this month.</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5, marginTop: 6 }}><b style={{ color: cfDot }}>Fix:</b> open Integrations → Cloudflare capacity to see which metric and why.</div>
                </div>
              </div>
            )}
            {shopReminder && (
              <div onClick={() => onJump && onJump('ads')} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: '1px solid ' + t.border, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.warn, background: t.surface2, cursor: 'pointer' }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: t.warnSoft, display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="store" size={14} color={t.warn} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}>Shop App: upload this month&rsquo;s snapshot <span style={{ color: t.faint, fontWeight: 600 }}>· monthly</span></div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.5, marginTop: 2 }}>The Shop App ads snapshot is from {shopCapturedLabel} — it has no API, so it needs a fresh manual upload at the start of each month to stay current.</div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5, marginTop: 6 }}><b style={{ color: t.warn }}>Fix:</b> open Paid Ads → Shop App → Upload screenshots (campaigns table + the 7-/30-day bars). <span style={{ color: t.faint }}>Still on the roadmap: a month-over-month Shop App history chart.</span></div>
                </div>
              </div>
            )}
          </div>
        </Card>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <Card pad={22}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Title size={14}>Integration Health</Title>
              {health == null
                ? <Badge tone="neutral">Checking…</Badge>
                : <Badge tone={intLive >= intTotal ? 'good' : 'bad'}>{intLive} / {intTotal} live</Badge>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px 16px' }}>
              {F().integrations.map((i: any) => {
                const cfg = integrationStatusCfg(t, resolveIntegrationStatus(i, health));
                return (
                  <span key={i.name} title={cfg.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FIGTREE, fontSize: 13, color: t.fg2, cursor: 'default', minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: cfg.dot, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</span>
                  </span>
                );
              })}
              {cf.connected && (
                <span title={`Cloudflare capacity — peak ${cfPct}% of a monthly limit`} onClick={() => onJump && onJump('integrations')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FIGTREE, fontSize: 13, color: t.fg2, cursor: 'pointer' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: cfDot, flexShrink: 0 }} />Cloudflare
                </span>
              )}
            </div>
            {intBad.length > 0 && (
              <div style={{ marginTop: 15, fontFamily: FIGTREE, fontSize: 12, color: t.bad, background: t.badSoft, borderRadius: 9, padding: '9px 12px', lineHeight: 1.5 }}>
                {intBad.map((i: any) => i.name).join(', ')} not responding — related metrics may lag.
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ---- Ballpark company value — owner-grade, so it self-hides for anyone
           without the `profit` permission (the API 403s) ---- */}
      <CompanyValueCard />

      {/* ---- Blog automation health — pinned at the foot of Daily Pulse ---- */}
      <BlogHealthBanner />
    </div>
  );
}
function Row({ k, v }: any) {
  const t = useT();
  return <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FIGTREE, fontSize: 12.5, marginTop: 7 }}><span style={{ color: t.muted }}>{k}</span><b style={{ color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{v}</b></div>;
}

// ============================================================ CASH & CREDIT
// "Clear eye on the bank." Bank buckets + Chase owed are LIVE from QuickBooks
// (/api/cash → getBalances); the Shopify Credit card isn't in QuickBooks so it's
// ESTIMATED = month-to-date COMBINED ad spend (Meta + Google — both are charged to
// this card, owner-confirmed), which resets when the card is paid off on the 1st.
// The two cards are shown SEPARATELY and never summed (owner's rule); each shows
// owed/limit/available.
type CardBal = { owed: number; limit: number; available: number; utilization: number };
type AdSplitRow = { id: string; label: string; spend: number; ok: boolean };
type ShopifyCreditBal = CardBal & {
  estimated: boolean; basis: string;
  adsMtd: number; adsOk: boolean; adsComplete: boolean; adsLabel: string;
  adDegraded: string[]; breakdown: AdSplitRow[];
  metaMtd: number; metaOk: boolean; // legacy aliases
};
type CashData = {
  ok: boolean; connected?: boolean; error?: string; at?: string;
  bank?: { buckets: { name: string; balance: number }[]; total: number };
  cards?: { shopifyCredit: ShopifyCreditBal | null; chase: CardBal | null };
};

function useCash() {
  const res = useCachedJson<CashData>('/api/cash');
  return { data: res.data, loading: res.loading, refresh: res.refresh };
}

function CreditBlock({ label, card, note }: { label: string; card: CardBal | null; note?: string }) {
  const t = useT();
  if (!card) {
    return (
      <div style={{ border: '1px solid ' + t.border, borderRadius: t.tileRadius, padding: 18, background: t.surface2 }}>
        <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{label}</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint, marginTop: 8 }}>Balance unavailable</div>
      </div>
    );
  }
  const util = card.utilization || 0;
  const barColor = util >= 70 ? t.bad : util >= 40 ? t.warn : t.accent;
  return (
    <div style={{ border: '1px solid ' + t.border, borderRadius: t.tileRadius, padding: 18, background: t.surface2, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{label}</span>
        {note ? <span style={{ fontFamily: FIGTREE, fontSize: 9.5, color: t.muted, border: '1px solid rgba(150,130,230,0.25)', borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' }}>{note}</span> : null}
      </div>
      <FitValue size={22}>{money(card.owed)}</FitValue>
      <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.07)', margin: '10px 0 6px' }}>
        <div style={{ height: '100%', width: Math.min(100, Math.max(0, util)) + '%', borderRadius: 999, background: barColor }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: FIGTREE, fontSize: 11, color: t.muted, gap: 8 }}>
        <span>{pctS(util)} of {money(card.limit, 0)}</span>
        <span style={{ color: t.fg2, fontVariantNumeric: 'tabular-nums' }}>{money(card.available, 0)} open</span>
      </div>
    </div>
  );
}

function CashCard() {
  const t = useT();
  const isMobile = useIsMobile();
  const { data, loading, refresh } = useCash();
  const [open, setOpen] = React.useState(false);
  const bank = data?.bank;
  const ok = !!(data?.ok && bank);
  const sc = data?.cards?.shopifyCredit || null;
  // Bucket display order — a saved personal preference. Default is high→low by
  // balance; any bucket the owner has dragged sits at its saved rank, and any
  // NEW/unranked bucket falls to the end (high→low) so nothing disappears.
  const [order, setOrder] = React.useState<string[] | null>(null);
  const [dragName, setDragName] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<string[] | null>(null); // live order while dragging
  React.useEffect(() => {
    fetch('/api/config/bucket-order').then((r) => (r.ok ? r.json() : null)).then((d) => setOrder(Array.isArray(d?.order) ? d.order : [])).catch(() => setOrder([]));
  }, []);
  const base = (bank?.buckets || []).slice().sort((a, b) => b.balance - a.balance);
  const bankTotal = bank?.total || base.reduce((s, b) => s + b.balance, 0);
  const ord = order || [];
  const rankOf = (name: string) => { const i = ord.indexOf(name); return i < 0 ? 1e6 : i; };
  const savedNames = base.slice().sort((a, b) => (rankOf(a.name) - rankOf(b.name)) || (b.balance - a.balance)).map((b) => b.name);
  const byName = new Map(base.map((b) => [b.name, b]));
  // During a drag, `preview` holds the live order so tiles shift to open a gap;
  // otherwise the saved order drives the layout.
  const buckets = (preview || savedNames).map((n) => byName.get(n)).filter(Boolean) as { name: string; balance: number }[];
  const persistOrder = (names: string[]) => {
    setOrder(names);
    fetch('/api/config/bucket-order', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ order: names }) }).catch(() => {});
  };
  const startDrag = (name: string) => { setDragName(name); setPreview(savedNames.slice()); };
  // As the dragged tile enters another tile, move it into that slot and shift the
  // rest — the whole grid reflows live, like rearranging iPhone icons.
  const dragOnto = (name: string) => {
    if (!dragName || dragName === name) return;
    setPreview((prev) => {
      const arr = (prev || savedNames).slice();
      const from = arr.indexOf(dragName), to = arr.indexOf(name);
      if (from < 0 || to < 0 || from === to) return arr;
      arr.splice(from, 1); arr.splice(to, 0, dragName);
      return arr;
    });
  };
  const endDrag = () => { if (preview) persistOrder(preview); setPreview(null); setDragName(null); };

  return (
    <Card pad={24} style={{ marginTop: -4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 15, color: t.fg }}>Cash &amp; Credit</span>
        <Badge tone="good"><span style={{ width: 6, height: 6, borderRadius: 999, background: t.good, display: 'inline-block' }} />Live · QuickBooks</Badge>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          {data?.at ? <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>as of {new Date(data.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span> : null}
          <button onClick={refresh} disabled={loading} style={{ cursor: loading ? 'default' : 'pointer', background: 'none', border: '1px solid rgba(150,130,230,0.28)', borderRadius: 999, padding: '4px 12px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: t.purple }}>{loading ? '…' : 'Refresh'}</button>
        </span>
      </div>

      {!ok ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, padding: '10px 0' }}>
          {loading ? 'Loading balances…' : data?.connected === false ? 'QuickBooks not connected.' : `Couldn't load balances${data?.error ? ` (${data.error})` : ''}.`}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr 1fr', gap: 16 }}>
            {/* In the bank */}
            <div style={{ background: t.surface2, border: '1px solid rgba(87,217,138,0.2)', borderRadius: t.tileRadius, padding: 18, minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>In the bank</div>
              <FitValue size={28} color={t.good}>{money(bank!.total)}</FitValue>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 8 }}>
                {bank!.buckets.length} Bluevine bucket{bank!.buckets.length !== 1 ? 's' : ''} ·{' '}
                <span onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer', color: t.purple, fontWeight: 600 }}>{open ? 'hide' : 'show'} buckets</span>
              </div>
            </div>
            <FitGroup>
            <CreditBlock label="Shopify Credit" card={sc}
              note={sc ? (!sc.adsOk ? 'ad feeds down' : sc.adsComplete ? `est · MTD ads · ${sc.adsLabel}` : `est · MTD ads · missing ${(sc.adDegraded || []).join(' + ')}`) : undefined} />
            <CreditBlock label="Chase" card={data?.cards?.chase || null} />
            </FitGroup>
          </div>

          {/* Bluevine buckets reveal — in-card tile grid, sorted high→low,
              bar = share of the largest bucket (replaces the old table drop-down) */}
          {open && (
            <div style={{ marginTop: 16, background: t.surface2, border: '1px solid ' + t.border, borderRadius: t.tileRadius, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11, color: t.muted }}>Bluevine buckets · {buckets.length} accounts</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>drag to reorder · bar = share of bank total</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(210px,1fr))', gap: '10px 16px' }}>
                {buckets.map((b) => {
                  const dragging = dragName === b.name;
                  const pctOfBank = bankTotal > 0 ? (b.balance / bankTotal) * 100 : 0;
                  return (
                  <div key={b.name}
                    draggable
                    onDragStart={(e) => { startDrag(b.name); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', b.name); } catch { /* Safari */ } }}
                    onDragEnter={(e) => { e.preventDefault(); dragOnto(b.name); }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => { e.preventDefault(); endDrag(); }}
                    onDragEnd={endDrag}
                    style={{ padding: '10px 12px 10px 26px', borderRadius: 9, background: 'rgba(255,255,255,0.02)', minWidth: 0, cursor: dragging ? 'grabbing' : 'grab', position: 'relative', opacity: dragging ? 0.45 : 1, boxShadow: dragging ? '0 8px 24px rgba(0,0,0,0.35)' : 'none', transition: 'opacity 120ms, box-shadow 120ms' }}>
                    {/* grip affordance */}
                    <span aria-hidden style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: t.faint, lineHeight: 0.6, fontSize: 13, letterSpacing: '1px', userSelect: 'none' }}>⠿</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 7 }}>
                      <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name.replace(/^Bluevine\s*/i, '') || 'Main'}</span>
                      <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 13.5, color: t.fg, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{money(b.balance, 0)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.06)' }}>
                        <div style={{ width: Math.max(2, pctOfBank) + '%', height: '100%', borderRadius: 999, background: `linear-gradient(90deg,${t.accent},${t.purple})` }} />
                      </div>
                      <span style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{pctOfBank.toFixed(0)}%</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {sc ? (
            <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 14, lineHeight: 1.5 }}>
              Shopify Credit is estimated (not in QuickBooks) as this month&apos;s total ad spend so far ({money(sc.adsMtd, 0)}
              {(sc.breakdown || []).length > 1 ? ' · ' + sc.breakdown.map((r) => `${r.label} ${r.ok ? money(r.spend, 0) : 'unavailable'}`).join(' · ') : ''}) — all ad spend hits this card and it&apos;s paid off on the 1st, so it resets monthly.
              {!sc.adsOk ? ' Ad feeds are down — showing $0 until they recover.'
                : !sc.adsComplete ? ` ${(sc.adDegraded || []).join(' + ')} isn’t reporting, so the balance is understated by that spend.` : ''}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ============================================================ REPLACEMENT LOSSES
// Free replacement orders (broken jars, transit loss, product shortage) + any
// other $0 order are real internal losses. The worker (/api/replacements) finds
// them by tag ("Replacements" + a reason) and by $0 total, and costs each as
// product COGS + owner packaging + real Packiyo postage. Follows the page's range
// picker. Top line = total count + total $; one row per reason (+ Not Tagged).
type ReplRow = { key: string; label: string; count: number; loss: number };
type ReplMonth = { month: string; live: boolean; rows: ReplRow[] };
type ReplData = {
  ok?: boolean; error?: string; at?: string;
  months?: ReplMonth[]; otherKeys?: string[];
  partial?: boolean; usedEstimateFor?: number; uncostedOrders?: number;
  labelSource?: 'packiyo' | 'estimate'; labelPartial?: boolean;
};

function useReplacements(nonce: number) {
  const res = useCachedResource<ReplData>('replacements', async ({ fresh }) => {
    const r = await fetch(`/api/replacements${fresh ? '?fresh=1' : ''}`);
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  });
  // The card's Refresh bumps `nonce`; force a fresh worker read when it does.
  const first = React.useRef(true);
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    res.refresh();
  }, [nonce]);
  return { data: res.data, loading: res.loading, refreshing: res.revalidating };
}

const MONTH_LABEL = (m: string) => {
  const [y, mo] = m.split('-').map((v) => parseInt(v, 10));
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const sumRows = (rows: ReplRow[]) => rows.reduce((a, r) => ({ count: a.count + r.count, loss: a.loss + r.loss }), { count: 0, loss: 0 });

// Monthly report. Closed months are stored snapshots (Shopify only exposes 60 days
// of orders, so they can't be re-queried); the current month is live and moves.
function ReplacementLossesCard({ nonce, onRefresh }: { nonce: number; onRefresh?: () => void }) {
  const t = useT();
  const isMobile = useIsMobile();
  const { data, loading, refreshing } = useReplacements(nonce);
  const [open, setOpen] = React.useState(false);
  const ok = !!(data && data.ok);
  const otherKeys = React.useMemo(() => new Set(data?.otherKeys || []), [data]);
  const months = data?.months || [];

  // Split every month's rows into replacement reasons vs the $0-other groups the
  // owner wants reported separately, then aggregate across months.
  const view = React.useMemo(() => {
    const perMonth = months.map((m) => {
      const repl = m.rows.filter((r) => !otherKeys.has(r.key));
      const other = m.rows.filter((r) => otherKeys.has(r.key));
      return { month: m.month, live: m.live, repl, other, ...sumRows(repl) };
    });
    const agg = (pick: (m: (typeof perMonth)[number]) => ReplRow[]) => {
      const by = new Map<string, ReplRow>();
      for (const m of perMonth) for (const r of pick(m)) {
        const e = by.get(r.key) || { key: r.key, label: r.label, count: 0, loss: 0 };
        e.count += r.count; e.loss = Math.round((e.loss + r.loss) * 100) / 100;
        by.set(r.key, e);
      }
      return [...by.values()].sort((a, b) => b.loss - a.loss);
    };
    const reasons = agg((m) => m.repl);
    const others = agg((m) => m.other);
    return { perMonth, reasons, others, total: sumRows(reasons), otherTotal: sumRows(others) };
  }, [months, otherKeys]);

  // Reason dot palette (redesign handoff). Unmapped reasons fall back to muted.
  const dotFor = (key: string) =>
    key === 'lost-in-transit' ? t.warn
      : key === 'product-shortage' ? t.purple
        : key === 'broken-jars' ? t.accent
          : key === 'pick-missed' ? '#8f85b5'
            : key === 'replacements-no-reason' ? t.bad
              : key === 'empty-box' ? '#7b6cf0'
                : key === 'product-missing' ? t.good
                  : t.faint;

  // Bordered rounded list shell — shared by By month / By reason / Other $0.
  const listBox: any = { border: '1px solid ' + t.border, borderRadius: 14, overflow: 'hidden' };
  const rowBase: any = { display: 'flex', alignItems: 'center', gap: 14, padding: isMobile ? '13px 14px' : '14px 18px' };
  const cellCount: any = { width: 52, textAlign: 'right', fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 };
  const cellLoss: any = { width: isMobile ? 86 : 100, textAlign: 'right', fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.bad, fontVariantNumeric: 'tabular-nums', flexShrink: 0 };

  // Reason rows carry a thin proportional bar (scaled to the largest reason) —
  // readability only; the width comes from the same loss figure shown at right.
  const Rows = ({ rows, dim }: { rows: ReplRow[]; dim?: boolean }) => {
    const max = Math.max(...rows.map((r) => r.loss), 0) || 1;
    return (
      <div style={listBox}>
        {rows.map((r, i) => (
          <div key={r.key} className="bb-hover-row" style={{ padding: isMobile ? '12px 14px' : '13px 18px', borderBottom: i === rows.length - 1 ? 'none' : '1px solid ' + t.border }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: dim ? '#8f85b5' : dotFor(r.key), flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: t.fg, lineHeight: 1.35, ...(isMobile ? { overflowWrap: 'anywhere' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>{r.label}</span>
              <span style={{ ...cellCount, width: 46 }}>{r.count.toLocaleString()}</span>
              <span style={{ ...cellLoss, width: isMobile ? 82 : 92, color: dim ? t.fg2 : t.bad }}>{money(-r.loss)}</span>
            </div>
            {!dim && (
              <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,255,255,0.05)', margin: '9px 0 0 24px' }}>
                <div style={{ width: (r.loss / max) * 100 + '%', height: '100%', borderRadius: 999, background: dotFor(r.key), opacity: 0.9 }} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card pad={isMobile ? '20px 18px' : '26px 28px'}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 14, color: t.fg }}>Replacement Losses</span>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.05em', fontSize: 10, color: t.purple, background: 'rgba(150,130,230,0.13)', padding: '3px 9px', borderRadius: 999 }}>MONTHLY REPORT</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          {onRefresh ? <Btn variant="outline" size="sm" icon="refresh" onClick={onRefresh} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh'}</Btn> : null}
          <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>
            {months.length ? `${MONTH_LABEL(months[0].month)} – ${MONTH_LABEL(months[months.length - 1].month)}` : ''}
          </span>
          {data?.at ? (
            <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>
              {refreshing ? 'Pulling live from Shopify…' : `Updated ${sinceLabel(data.at) === 'just now' ? 'just now' : sinceLabel(data.at) + ' ago'}`}
            </span>
          ) : null}
        </div>
      </div>
      <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, marginBottom: 20, maxWidth: 640, lineHeight: 1.55 }}>
        Free replacements &amp; other $0 orders costed as real internal losses (COGS + packaging + label).
      </div>

      {!ok ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '10px 0' }}>
          {loading ? 'Loading replacement report…' : `Couldn't load replacement losses${data?.error ? ` (${data.error})` : ''}.`}
        </div>
      ) : (
        <>
          {/* Hero — the two figures the whole report exists to produce. */}
          <FitGroup>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', paddingBottom: 22, marginBottom: 22, borderBottom: '1px solid ' + t.border }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <FitValue size={44} color={t.bad}>{money(-view.total.loss)}</FitValue>
                <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 8 }}>total lost · internal cost (COGS + packaging + label)</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, textAlign: 'right' }}>
                <FitValue size={44} color={t.fg} style={{ textAlign: 'right' }}>{view.total.count.toLocaleString()}</FitValue>
                <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 8 }}>replacement{view.total.count === 1 ? '' : 's'} · all months</div>
              </div>
            </div>
          </FitGroup>

          {/* By month — the report's spine. Current month is flagged as still moving. */}
          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 10.5, color: t.faint, margin: '0 0 10px 2px' }}>By month</div>
          <div style={{ ...listBox, marginBottom: 22 }}>
            {view.perMonth.map((m, i) => (
              <div key={m.month} className="bb-hover-row" style={{ ...rowBase, borderBottom: i === view.perMonth.length - 1 ? 'none' : '1px solid ' + t.border }}>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: t.fg }}>
                  {MONTH_LABEL(m.month)}
                  {m.live ? <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.04em', fontSize: 9.5, color: t.accent, background: t.accentSoft, border: '1px solid rgba(248,146,92,0.3)', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>LIVE MONTH</span> : null}
                </span>
                <span style={{ ...cellCount, width: 56 }}>{m.count.toLocaleString()}</span>
                <span style={cellLoss}>{money(-m.loss)}</span>
              </div>
            ))}
          </div>

          {/* Reason breakdown across every month. */}
          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 10.5, color: t.faint, margin: '0 0 10px 2px' }}>By reason · all months</div>
          {view.reasons.length ? <Rows rows={view.reasons} /> : (
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No replacements yet. 🎉</div>
          )}

          {/* $0 orders that AREN'T replacements — reported separately, never in the
              total. The breakdown slides open rather than snapping in. */}
          {view.others.length ? (
            <div style={{ marginTop: 22 }}>
              <div onClick={() => setOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexWrap: 'wrap', marginBottom: 10, padding: '0 2px' }}>
                <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 10.5, color: t.faint }}>Other $0 orders · not replacements</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, fontVariantNumeric: 'tabular-nums' }}>{view.otherTotal.count} · {money(-view.otherTotal.loss)}</span>
                <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.accent, fontWeight: 700, marginLeft: 'auto' }}>{open ? 'hide' : 'show'}</span>
              </div>
              <div style={{ overflow: 'hidden', maxHeight: open ? 460 : 0, opacity: open ? 1 : 0, transition: 'max-height 340ms cubic-bezier(0.2,0.7,0.2,1), opacity 260ms ease' }}>
                <Rows rows={view.others} dim />
              </div>
            </div>
          ) : null}

          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 16, lineHeight: 1.7 }}>
            Closed months are stored snapshots — Shopify only lets this app read the last 60 days of orders, so past months are cemented once and can&apos;t drift. Only the current month is live.
            {data!.partial ? ' ⚠ The live month hit a scan cap — its count is partial.' : ''}
            {data!.labelSource === 'estimate'
              ? ` Postage uses the flat $${(9.9).toFixed(2)}/order estimate (Packiyo feed unavailable).`
              : (data!.usedEstimateFor || 0) > 0
                ? ` ${data!.usedEstimateFor} order${(data!.usedEstimateFor || 0) === 1 ? '' : 's'} in the live month used the flat postage estimate (no exact Packiyo label match).`
                : ''}
            {(data!.uncostedOrders || 0) > 0 ? ` ${data!.uncostedOrders} order${(data!.uncostedOrders || 0) === 1 ? '' : 's'} had an uncosted line — COGS may be slightly understated.` : ''}
          </div>
        </>
      )}
    </Card>
  );
}

// Standalone Finances tab for the Replacement Losses report. Its own Refresh
// bumps a nonce → the card bypasses the worker cache.
export function ReplacementsView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [nonce, setNonce] = React.useState(0);
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 1000 }}>
      <div style={{ marginBottom: 6 }}>
        <Title size={isMobile ? 24 : 32}>Replacements</Title>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, maxWidth: 720, lineHeight: 1.5 }}>
          Free replacements &amp; other $0 orders costed as real internal losses across the last six months.
        </div>
      </div>
      <ReplacementLossesCard nonce={nonce} onRefresh={() => setNonce((n) => n + 1)} />
    </div>
  );
}

// ============================================================ PROFIT
// Real-time P&L — same live cost engine as the Daily Pulse breakdown, formatted
// as a full statement for the selected range (Today / WTD / MTD / custom). Every
// line is actual to-date EXCEPT fixed overhead, which is a monthly run-rate
// pro-rated to the range (trued up monthly against QuickBooks).
const COST_SWATCH = ['#246d7f', '#f47f49', '#3c3291', '#8a6d3b', '#5b8a72', '#9b4dca', '#c2554d', '#4d7ec2'];

// P&L waterfall milestone band — emphasized row with a colored left rail, a
// running-margin chip, and a margin-share bar (this milestone as % of net
// revenue), per the design_handoff_profit_loss redesign.
const PL_TONES: any = {
  purple: { rail: '#b9a9ff', bg: 'rgba(185,169,255,0.07)', chipFg: '#b9a9ff', chipBg: 'rgba(185,169,255,0.14)' },
  green: { rail: '#6fe3a0', bg: 'rgba(87,217,138,0.07)', chipFg: '#6fe3a0', chipBg: 'rgba(87,217,138,0.14)' },
  orange: { rail: '#f8925c', bg: 'rgba(248,146,92,0.09)', chipFg: '#f8925c', chipBg: 'rgba(248,146,92,0.16)' },
};
function PLMilestone({ label, chip, tone = 'purple', value, valueColor, barPct, big }: any) {
  const t = useT();
  const c = PL_TONES[tone] || PL_TONES.purple;
  const w = barPct != null ? Math.max(0, Math.min(100, barPct)) : null;
  return (
    <div style={{ background: c.bg, borderLeft: '3px solid ' + c.rail, borderRadius: '0 10px 10px 0', padding: big ? 14 : '12px 14px', margin: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 9, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: big ? 16 : 15, color: t.fg }}>{label}</span>
          {chip && <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.05em', fontSize: 10, color: c.chipFg, background: c.chipBg, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{chip}</span>}
        </span>
        <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: big ? 20 : 16, color: valueColor || t.fg, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      </div>
      {w != null && (
        <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{ width: w + '%', height: '100%', borderRadius: 999, background: c.rail }} />
        </div>
      )}
    </div>
  );
}

// Indented deduction line ("− COGS   ($129.29)") between milestones.
function PLDeduct({ label, sub, value }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 0 11px 18px', borderBottom: '1px solid rgba(150,130,230,0.09)' }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
        <span style={{ color: t.faint, fontFamily: FIGTREE, fontSize: 13 }}>−</span>
        <span style={{ fontFamily: FIGTREE, fontSize: 14, color: t.sidebarMuted || t.fg2 }}>{label}</span>
        {sub && <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>{sub}</span>}
      </span>
      <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 14.5, color: t.bad, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>({value})</span>
    </div>
  );
}

// Fixed category swatches for the ranked cost bars (per the P&L handoff).
const PL_SWATCH: Record<string, string> = {
  'Ad spend': '#2db7a8', 'Overhead': '#f8925c', 'Shipping': '#7b6cf0', 'COGS': '#c9a648',
  'Pick/pack': '#6fe3a0', 'Payment fees': '#d072d8', 'Facility rent': '#ff6f5e', 'Packaging': '#5b8def',
};

export function ProfitView() {
  const t = useT();
  const isMobile = useIsMobile();
  const rm = useRangeMetrics('today');
  const kpi = useKpiTargets(); // ad-spend ceiling from KPI Targets (was hardcoded 40)
  const dd: any = rm.data?.day || {};
  const hasData = dd.net != null && !dd.shopifyDown && !dd.shopifyApprox;
  const adPct = hasData && dd.revenue > 0 ? ((dd.adSpend || 0) / dd.revenue) * 100 : null;
  const contribMargin = hasData && dd.revenue > 0 ? Math.round((dd.contribution / dd.revenue) * 1000) / 10 : null;
  const discReturns = hasData && dd.grossSales != null ? dd.grossSales - dd.revenue : null;

  const costRows = hasData ? [
    { label: 'COGS', amt: dd.cogs || 0 },
    { label: 'Ad spend', amt: dd.adSpend || 0 },
    { label: 'Shipping', amt: dd.shipping || 0 },
    { label: 'Overhead', amt: dd.overhead || 0 },
    { label: 'Pick/pack', amt: dd.pickPack || 0 },
    { label: 'Payment fees', amt: dd.paymentFees || 0 },
    { label: 'Packaging', amt: dd.packaging || 0 },
    { label: 'Facility rent', amt: dd.facilityRent || 0 },
  ].sort((a, b) => b.amt - a.amt) : [];
  const totalCost = costRows.reduce((s, c) => s + c.amt, 0);
  const maxCost = costRows.length ? costRows[0].amt : 0; // bars scale to the largest cost

  // "Meta $1,186 · Google $20" under the P&L ad-spend line — every channel in the
  // combined figure, straight from the Worker's ad-spend registry.
  const adSub = (dd.adBreakdown || []).length
    ? (dd.adBreakdown as any[]).map((r) => `${r.label} ${r.ok ? money(r.spend, 0) : 'unavailable'}`).join(' · ')
    : (dd.adSourcesLabel || undefined);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* header — shared Daily Pulse pattern */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 6 }}>
        <div>
          <Title size={isMobile ? 24 : 32}>Profit &amp; Loss</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>Live P&amp;L for the selected range · {rm.data?.label || 'Real-time'}</div>
        </div>
        <DateRangePicker range={rm.range} setRange={rm.setRange} custom={rm.custom} setCustom={rm.setCustom} onRefresh={rm.refresh} loading={rm.loading} />
      </div>

      {/* REAL-TIME callout — same explainer, purple-accented info card */}
      <div style={{ display: 'flex', gap: 14, background: 'rgba(29,23,64,0.55)', border: '1px solid rgba(150,130,230,0.16)', borderLeft: '3px solid ' + t.purple, borderRadius: 14, padding: '16px 20px' }}>
        <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: t.purpleSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="pulse" size={16} color={t.purple} strokeWidth={2.2} />
        </span>
        <div>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 11, color: t.purple, marginBottom: 6 }}>Real-time</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.sidebarMuted, lineHeight: 1.6 }}>
            Live P&amp;L for the selected range, current to the minute. Every line is <b style={{ color: '#e6dfff' }}>actual to-date</b> except <b style={{ color: '#e6dfff' }}>Overhead / OpEx</b>, which is your QuickBooks monthly run-rate pro-rated to the range (trued up monthly).{dd.adSpendProvisional ? <> Same-day <b style={{ color: '#e6dfff' }}>ad spend is provisional</b> — Meta/Google report it with a lag, so ad %, ROAS and Net are understated until it finalizes; use <b style={{ color: '#e6dfff' }}>Yesterday</b> or <b style={{ color: '#e6dfff' }}>L7</b> for settled ad numbers.</> : null} Hit <b style={{ color: '#e6dfff' }}>Refresh</b> for the very latest.
          </div>
        </div>
      </div>

      {!hasData ? (
        <Card pad={22}>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted }}>
            {dd.shopifyDown ? 'Shopify is temporarily unreachable — the real-time P&L is paused rather than show a wrong number. It returns automatically when Shopify recovers.'
              : dd.shopifyApprox ? 'Shopify analytics is degraded — revenue is a gross estimate right now, so the costed P&L is paused until analytics recovers.'
              : dd.costUnavailable ? 'Cost data is paused (Shopify analytics). The P&L returns automatically when analytics is healthy.'
              : 'Loading the real-time P&L…'}
          </div>
        </Card>
      ) : (
        <>
          {/* KPI hero row — same gradient treatment as Daily Pulse Business Vitals */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap: 16 }}>
            <FitGroup>
            <HeroCard caps icon="dollar" tint={t.accent} tintBg={t.accentSoft} label="Net Profit"
              grad="linear-gradient(160deg,#33285f,#241c48)" borderColor="rgba(248,146,92,0.28)"
              valueColor={dd.net >= 0 ? t.accent : t.bad}
              value={money(dd.net) + (dd.profitEstimate ? '*' : '')}
              foot={<span style={{ color: t.muted }}>{dd.overheadPending ? 'excl. overhead' : 'after overhead'}</span>} />
            <HeroCard caps icon="percent" tint={t.purple} tintBg={t.purpleSoft} label="Net Margin"
              valueColor={dd.netMargin != null && dd.netMargin < 0 ? t.bad : '#fff'}
              value={dd.netMargin != null ? pctS(dd.netMargin) : '—'}
              foot={<span style={{ color: t.muted }}>net profit ÷ revenue</span>} />
            <HeroCard caps icon="pulse" tint={t.good} tintBg={t.goodSoft} label="Gross Profit"
              value={money(dd.grossProfit)}
              foot={dd.grossMargin != null ? <span style={{ color: t.good }}>{dd.grossMargin}% margin</span> : null} />
            <HeroCard caps icon="spark" tint={t.purple} tintBg={t.purpleSoft} label="Contribution"
              value={money(dd.contribution)}
              foot={contribMargin != null ? <span style={{ color: t.muted }}>{contribMargin}% margin</span> : null} />
            <HeroCard caps icon="zap" tint={t.warn} tintBg={t.warnSoft} label="Ad Spend % Rev"
              valueColor={adPct != null && adPct >= kpi.adCeiling ? t.warn : '#fff'}
              value={adPct != null ? pctS(adPct) : '—'}
              foot={<span style={{ color: t.muted }}>{dd.adSpendProvisional ? 'provisional (same-day lag)' : `ceiling ${kpi.adCeiling}%`}</span>} />
            </FitGroup>
          </div>

          {/* statement (waterfall) + cost breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.12fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
            <Card pad={0} style={{ padding: '24px 26px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: t.accent }} />
                <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Profit &amp; Loss Statement</span>
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint, margin: '0 0 6px 16px' }}>the waterfall from sales down to net — milestones highlighted</div>

              {dd.grossSales != null && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 0 12px', borderBottom: '1px solid rgba(150,130,230,0.09)' }}>
                  <span style={{ fontFamily: FIGTREE, fontSize: 14.5, color: '#e6dfff', fontWeight: 600 }}>Gross sales</span>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 15, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{money(dd.grossSales)}</span>
                </div>
              )}
              {discReturns != null && <PLDeduct label="Discounts & returns" value={money(Math.abs(discReturns))} />}

              <PLMilestone label="Net revenue" chip="100% base" tone="purple" value={money(dd.revenue)} barPct={100} />

              <PLDeduct label="COGS" sub={dd.uncostedUnits > 0 ? `${dd.uncostedUnits} uncosted` : undefined} value={money(dd.cogs)} />

              <PLMilestone label="Gross profit" chip={dd.grossMargin != null ? dd.grossMargin + '% margin' : undefined} tone="green" value={money(dd.grossProfit)} valueColor={t.good} barPct={dd.grossMargin} />

              <PLDeduct label="Payment fees" sub="2.7% + $0.30" value={money(dd.paymentFees)} />
              <PLDeduct label="Shipping" sub={dd.shippingSource === 'packiyo' ? 'real postage' : 'est.'} value={money(dd.shipping)} />
              <PLDeduct label="Pick/pack" sub="Bring 3PL" value={money(dd.pickPack)} />
              <PLDeduct label="Packaging" sub={dd.packagingEstimated ? 'est. avg' : 'per order'} value={money(dd.packaging)} />
              <PLDeduct label="Facility rent" sub={money(dd.facilityRate ?? 2.25) + '/order'} value={money(dd.facilityRent || 0)} />

              <PLMilestone label="Contribution" chip={contribMargin != null ? contribMargin + '% margin' : undefined} tone="purple" value={money(dd.contribution)} barPct={contribMargin} />

              <PLDeduct label="Ad spend" sub={adSub} value={money(dd.adSpend || 0)} />
              <PLDeduct label="Overhead / OpEx" sub={dd.overheadPending ? 'QBO pending' : (dd.overheadDaily != null ? money(dd.overheadDaily, 0) + '/day' : 'QuickBooks')} value={money(dd.overhead || 0)} />

              <PLMilestone big label="Net profit" chip={dd.netMargin != null ? dd.netMargin + '% margin' : undefined} tone="orange" value={money(dd.net) + (dd.profitEstimate ? '*' : '')} valueColor={dd.net >= 0 ? t.good : t.bad} barPct={dd.netMargin} />

              {dd.overheadWatched?.length > 0 && (
                <div style={{ marginTop: 16, fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.55 }}>
                  Tracked separately, <b style={{ color: t.muted }}>not in Net</b> (watched for trend): {dd.overheadWatched.map((w: any) => `${w.name} ${money(w.amount, 0)}/mo`).join(' · ')}.
                </div>
              )}
            </Card>

            <Card pad={0} style={{ padding: '24px 26px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: 999, background: t.purple }} />
                <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Cost Breakdown</span>
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint, margin: '0 0 20px 16px' }}>each cost as a share of total expense</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 17 }}>
                {costRows.map((c: any, i: number) => {
                  const per = totalCost ? (c.amt / totalCost) * 100 : 0;
                  const sw = PL_SWATCH[c.label] || COST_SWATCH[i % COST_SWATCH.length];
                  return (
                    <div key={c.label}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 9, fontFamily: FIGTREE, fontSize: 13.5, color: '#e6dfff' }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: sw, flexShrink: 0 }} />{c.label}
                        </span>
                        <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, fontVariantNumeric: 'tabular-nums' }}><b style={{ color: t.fg }}>{money(c.amt)}</b> · {per.toFixed(1)}%</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.05)' }}>
                        <div style={{ width: (maxCost > 0 ? Math.max(1, (c.amt / maxCost) * 100) : 0) + '%', height: '100%', borderRadius: 999, background: sw }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 22, paddingTop: 16, borderTop: '1px solid rgba(150,130,230,0.14)' }}>
                <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, color: t.fg }}>Total costs</span>
                <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 17, color: t.bad, fontVariantNumeric: 'tabular-nums' }}>({money(totalCost)})</span>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================ PER-UNIT COST
const UNIT_COLS = ['Product', 'Units', 'List', 'Avg', 'COGS', 'Fulfillment', 'Facility', 'Overhead', 'Loaded', 'Contribution', 'Margin', 'Monthly $'];
function UnitHead({ t }: any) {
  const th = (i: number): any => ({ textAlign: i === 0 ? 'left' : 'right', padding: i === 0 ? '0 0 13px' : '0 0 13px 20px', fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.08em', color: t.faint, whiteSpace: 'nowrap' });
  return <thead><tr>{UNIT_COLS.map((h, i) => (
    <th key={h} style={{ ...th(i), ...(h === 'Loaded' ? { color: t.fg2 } : h === 'Contribution' ? { color: t.good } : null) }}>{h}</th>
  ))}</tr></thead>;
}
// One product/component row. `indent` renders a kit sub-row; `parent` emphasizes the
// kit line (heavier weight + stronger divider). Order-level costs (fulfillment /
// facility / overhead) have NO per-jar value on the kit parent — they're inside its
// Loaded — so any non-finite value renders "—" (never −$NaN, never fabricated).
function UnitRow({ r, t, indent, parent, overheadPending }: any) {
  const bd = '1px solid ' + (parent ? 'rgba(150,130,230,0.14)' : 'rgba(150,130,230,0.07)');
  const pad = parent ? 14 : 13;
  const cell: any = { padding: `${pad}px 0 ${pad}px 20px`, textAlign: 'right', fontFamily: FIGTREE, fontSize: 13, fontVariantNumeric: 'tabular-nums', borderBottom: bd, whiteSpace: 'nowrap', color: t.sidebarMuted };
  const neg = (v: number) => (Number.isFinite(v) ? '−' + money(v) : '—');
  const lowMargin = indent && r.margin != null && r.margin < 45; // the thin line the other jars carry (e.g. free-in-kit Binder)
  return (
    <tr className="bb-hover-row">
      <td style={{ ...cell, textAlign: 'left', padding: `${pad}px 0`, paddingLeft: indent ? 18 : 0, fontSize: parent ? 14 : 13.5, color: indent ? t.fg2 : t.fg, fontWeight: parent ? 800 : 600 }}>{indent && <span style={{ color: t.faint }}>↳ </span>}{r.name}</td>
      <td style={{ ...cell, ...(parent ? { fontWeight: 600, color: t.fg2 } : null) }}>{r.units.toLocaleString()}</td>
      <td style={{ ...cell, color: t.faint }}>{money(r.list)}</td>
      <td style={{ ...cell, fontWeight: parent ? 800 : 700, color: t.fg }}>{money(r.avg)}</td>
      <td style={cell}>{neg(r.cogs)}</td>
      <td style={cell}>{neg(r.fulfillment)}</td>
      <td style={cell}>{neg(r.facility)}</td>
      <td style={cell}>{overheadPending ? '—' : neg(r.overhead)}</td>
      <td style={{ ...cell, fontWeight: 700, color: t.fg }}>{neg(r.loaded)}</td>
      <td style={{ ...cell, fontWeight: 700, color: lowMargin ? t.warn : r.contribution >= 0 ? t.good : t.bad }}>{money(r.contribution)}</td>
      <td style={{ ...cell, color: lowMargin ? t.warn : r.margin != null && r.margin < 0 ? t.bad : t.fg2 }}>{r.margin != null ? r.margin + '%' : '—'}</td>
      <td style={{ ...cell, color: t.fg2 }}>{money(r.monthly, 0)}</td>
    </tr>
  );
}

export function UnitView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback((fresh: boolean) => {
    setLoading(true);
    fetch(`/api/unit${fresh ? '?fresh=1' : ''}`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(false); }, [load]);

  const individual: any[] = data?.individual || [];
  const combos: any[] = data?.combos || [];
  const kit = data?.kit;
  const biz = data?.business;
  const per = data?.perUnit || { fulfillment: 0, facility: 0, overhead: 0 };
  const facilityRate = data?.facilityRate ?? 2.25;
  const monthlyOverhead = data?.overheadMonthly ?? 0;
  const units = data?.units ?? 0;
  const orders = data?.orders ?? 0;
  const overheadPending = data?.overheadPending ?? true;
  const shopDown = !!data?.shopifyDown;
  const hasRows = individual.length > 0 || combos.length > 0 || !!kit;
  const covered = biz?.coverageX != null && biz.coverageX >= 1;

  // Oswald caps card-header (dot + title + muted sub), shared by both tables
  const tableHead = (dot: string, title: string, sub: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 11, marginBottom: 20, flexWrap: 'wrap' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: dot }} />
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>{title}</span>
      </span>
      <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>{sub}</span>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* header — shared Daily Pulse pattern */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 6 }}>
        <div>
          <Title size={isMobile ? 24 : 32}>Per-Unit Economics</Title>
          <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, maxWidth: 640 }}>The true, fully-loaded cost to put each unit out the door — every expense included — and what it really contributes.</div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ padding: '7px 14px', borderRadius: 999, background: 'rgba(20,15,42,0.6)', border: '1px solid rgba(150,130,230,0.16)', color: t.fg2, fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.06em', fontSize: 12, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{data?.label || 'Last 30 days'}</span>
          <button onClick={() => load(true)} title="Refresh now" aria-label="Refresh" style={{ width: 34, height: 34, borderRadius: 999, border: '1px solid rgba(150,130,230,0.2)', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="refresh" size={15} color={t.muted} style={{ animation: loading ? 'bbspin 0.8s linear infinite' : 'none' }} />
          </button>
        </span>
      </div>

      {/* METHOD — purple-accented info card (same pattern as Profit's Real-time) */}
      <div style={{ display: 'flex', gap: 14, background: 'rgba(29,23,64,0.55)', border: '1px solid rgba(150,130,230,0.16)', borderLeft: '3px solid ' + t.purple, borderRadius: 14, padding: '16px 20px' }}>
        <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: t.purpleSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="box" size={16} color={t.purple} strokeWidth={2.2} />
        </span>
        <div>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 11, color: t.purple, marginBottom: 6 }}>Method</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.sidebarMuted, lineHeight: 1.6 }}>
            <b style={{ color: '#e6dfff' }}>Loaded</b> is <b style={{ color: '#e6dfff' }}>every</b> cost per jar: COGS + fulfillment ({money(per.fulfillment)} — postage, payment fees, pick/pack, packaging) + facility ({money(per.facility)}) + overhead ({overheadPending ? <>paused</> : money(per.overhead)}). <b style={{ color: '#e6dfff' }}>Avg</b> = what you actually collect per unit (net of bundle/discount); <b style={{ color: '#e6dfff' }}>List</b> = sticker price, for comparison. Contribution = Avg − Loaded. Kit jars are pulled out of these single-jar numbers and shown in their own section below at the <b style={{ color: '#e6dfff' }}>real kit-allocated price</b> (each kit jar earns less than sold alone).{data?.partial ? ' Based on a large sample of orders.' : ''}
          </div>
        </div>
      </div>

      {/* BUSINESS BREAK-EVEN — gradient hero, three key figures + owner's summary line */}
      {biz && (
        <div style={{ background: 'linear-gradient(160deg,#33285f,#241c48)', border: '1px solid ' + (overheadPending ? 'rgba(150,130,230,0.16)' : covered ? 'rgba(87,217,138,0.3)' : 'rgba(255,111,94,0.3)'), borderRadius: t.cardRadius, padding: isMobile ? 20 : '24px 26px' }}>
          <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg, marginBottom: 18 }}>Business Break-Even</div>
          <div style={{ display: 'flex', gap: isMobile ? 20 : 40, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <FitGroup>
            <div style={{ flex: '1 1 130px', minWidth: 0 }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10.5, color: t.muted, marginBottom: 8 }}>Contribution · 30d</div>
              <FitValue size={32} color={t.good}>{money(biz.totalContribution, 0)}</FitValue>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(150,130,230,0.18)' }} />
            <div style={{ flex: '1 1 130px', minWidth: 0 }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10.5, color: t.muted, marginBottom: 8 }}>Fixed Overhead</div>
              <FitValue size={32}>{overheadPending ? '—' : <>{money(biz.monthlyOverhead, 0)}<span style={{ fontSize: 15, color: t.muted, fontWeight: 600 }}>/mo</span></>}</FitValue>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(150,130,230,0.18)' }} />
            <div style={{ flex: '1 1 110px', minWidth: 0 }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10.5, color: t.muted, marginBottom: 8 }}>Coverage</div>
              <FitValue size={32} color={overheadPending ? '#7a7099' : covered ? t.accent : t.bad}>{biz.coverageX != null && !overheadPending ? biz.coverageX + '×' : '—'}</FitValue>
            </div>
            </FitGroup>
            <div style={{ flex: '1 1 240px', minWidth: 240, fontFamily: FIGTREE, fontSize: 12.5, color: t.sidebarMuted, lineHeight: 1.6 }}>
              {overheadPending
                ? <>Fixed overhead is paused (QuickBooks) — coverage returns automatically when it reconnects.</>
                : <>Sales exceeded break-even by <b style={{ color: t.good }}>{money(biz.totalContribution, 0)}</b> over the {(data?.label || 'last 30 days').toLowerCase()}, which is <b style={{ color: covered ? t.accent : t.bad }}>{biz.coverageX} times</b> the fixed overhead of <b style={{ color: '#e6dfff' }}>{money(biz.monthlyOverhead, 0)}/mo</b>.</>}
            </div>
          </div>
        </div>
      )}

      {shopDown ? (
        <Card pad={16}><div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Shopify is temporarily unreachable — per-unit figures paused.</div></Card>
      ) : loading && !data ? (
        <Card pad={16}><div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Reading the last 30 days of orders…</div></Card>
      ) : !hasRows ? (
        <Card pad={16}><div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No product sales in the last 30 days.</div></Card>
      ) : (
        <>
          {(individual.length > 0 || combos.length > 0) && (
            <Card pad={0} style={{ padding: '24px 26px 20px' }}>
              {tableHead(t.good, 'Individual Products', 'sold on their own (full price)')}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000, fontVariantNumeric: 'tabular-nums' }}>
                  <UnitHead t={t} />
                  <tbody>
                    {individual.map((r: any) => <UnitRow key={r.name} r={r} t={t} overheadPending={overheadPending} />)}
                    {combos.map((r: any) => <UnitRow key={r.name} r={r} t={t} overheadPending={overheadPending} />)}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {kit && (
            <Card pad={0} style={{ padding: '24px 26px 20px' }}>
              {tableHead(t.accent, 'The 5-Detox Kit', '4 cleanses + free binder · sold as a bundle')}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000, fontVariantNumeric: 'tabular-nums' }}>
                  <UnitHead t={t} />
                  <tbody>
                    <UnitRow r={kit} t={t} parent overheadPending={overheadPending} />
                    {kit.components.map((r: any) => <UnitRow key={r.name} r={r} t={t} indent overheadPending={overheadPending} />)}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '14px 0 0', fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.55 }}>
                The kit lists at {money(kit.list)} and you actually collect {money(kit.avg)} avg. The prices above are the <b>real per-jar rates your store records</b> — the {money(kit.list)} spread across the 5 jars proportional to list, so a cleanse comes in around its kit rate vs its full {money(individual[0]?.list || 34)} sold alone, and the Binder (which you bill as “free” in the kit) is recorded at ~{money(kit.components?.find((c: any) => /binder/i.test(c.name))?.avg ?? 15)} — a thin/negative line the cleanse margins carry. That’s the trade-off the kit’s volume justifies. The kit’s own fulfillment, facility and overhead are charged <b>once at the order level</b> — they’re inside the parent’s Loaded, so those per-jar cells show “—”.
              </div>
            </Card>
          )}
        </>
      )}

      {/* Cost basis (read-only — inputs live on the Expenses tab / QuickBooks) */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 }}>
        <Card pad={22}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 11, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: t.purple }} />
              <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Cost Basis</span>
            </span>
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>{data?.label || 'last 30 days'}</span>
          </div>
          <Row k="Fulfillment / unit" v={money(per.fulfillment)} />
          <Row k="Facility / unit" v={money(per.facility)} />
          <Row k="Overhead / unit" v={overheadPending ? '— QBO paused' : money(per.overhead)} />
          <div style={{ height: 1, background: t.border, margin: '10px 0' }} />
          <Row k="Facility rate" v={`${money(facilityRate)}/order`} />
          <Row k="Monthly overhead" v={overheadPending ? '— QBO paused' : money(monthlyOverhead, 0)} />
          <Row k="Jars shipped (30d)" v={units.toLocaleString()} />
          <Row k="Orders (30d)" v={orders.toLocaleString()} />
        </Card>
        <div style={{ display: 'flex', gap: 14, background: 'rgba(29,23,64,0.55)', border: '1px solid rgba(150,130,230,0.16)', borderLeft: '3px solid ' + t.purple, borderRadius: 14, padding: '16px 20px', alignItems: 'flex-start' }}>
          <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: t.purpleSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="dollar" size={16} color={t.purple} strokeWidth={2.2} />
          </span>
          <div>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 11, color: t.purple, marginBottom: 6 }}>Inputs</div>
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.sidebarMuted, lineHeight: 1.6 }}>
              These are read-only here. The facility rate is editable on the <b style={{ color: '#e6dfff' }}>Expenses</b> tab; overhead is live from <b style={{ color: '#e6dfff' }}>QuickBooks</b>. Fulfillment (postage/fees/pick-pack/packaging) is a blended per-order average. The <b style={{ color: '#e6dfff' }}>Expenses</b> tab is growing into the full surface — edit every recurring cost, upload invoices → auto-COGS.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================ PROFIT-FIRST
// ---- Profit-First period helpers (Sun–Sat weeks + calendar months) ----------
const ymdLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shortDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
// Sunday–Saturday week containing today, shifted by `off` weeks (0 = current).
function weekRange(off: number) {
  const now = new Date(); now.setHours(12, 0, 0, 0); now.setDate(now.getDate() + off * 7);
  const sun = new Date(now); sun.setDate(now.getDate() - now.getDay());
  const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
  return { start: ymdLocal(sun), end: ymdLocal(sat), label: `Week of ${shortDate(sun)} – ${shortDate(sat)}, ${sat.getFullYear()}` };
}
// Calendar month shifted by `off` months (0 = current).
function monthRange(off: number) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + off, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return { start: ymdLocal(first), end: ymdLocal(last), label: first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) };
}

// ---- Cost-drift (actual bucket spend vs Profit-First target %) --------------
// Seeded target %s of real revenue (owner-editable live in the Allocation UI).
// These are the DEFAULT basis for cost-drift everywhere — the Morning Brief uses
// them directly; the Allocation page lets you tweak them per-session.
export const PF_TARGETS: Record<string, number> = { opex: 17, biztax: 10, inventory: 11, contractors: 4, facility: 7, shipmat: 2, profit: 5 };

// Only these buckets have a real in-period ACTUAL to compare vs target. Biz Taxes
// is a forward reserve, Ads is live/variable, Profit + Owners Pay are the residual
// — none "drift" against a fixed target, so they're excluded from drift.
const DRIFT_BUCKETS = ['opex', 'inventory', 'contractors', 'facility', 'shipmat'] as const;
const DRIFT_LABEL: Record<string, string> = { opex: 'OpEx', inventory: 'Inventory', contractors: 'Contractors', facility: 'Facility / 3PL', shipmat: 'Ship Materials' };
const OVER_PP = 1.0; // ≥1 percentage-point over target = flagged "over"

export type BucketDrift = { key: string; label: string; actual: number; actualPct: number | null; targetPct: number; deltaPp: number | null; state: 'over' | 'under' | 'on' | 'na'; partial: boolean };

// Compare each cost bucket's actual spend (/api/metrics day.costBuckets) to its
// target % of real revenue. `partial` = QBO fixed-overhead slice missing (QuickBooks
// paused) so opex/contractors/facility actuals understate reality.
export function computeBucketDrift(dd: any, targets: Record<string, number>, allocBase: number): Record<string, BucketDrift> {
  const cb = dd?.costBuckets;
  const pending = !!cb?.overheadPending;
  const out: Record<string, BucketDrift> = {};
  for (const key of DRIFT_BUCKETS) {
    const targetPct = targets[key] ?? 0;
    const actual = cb ? (cb[key] ?? 0) : null;
    if (actual == null || allocBase <= 0) {
      out[key] = { key, label: DRIFT_LABEL[key], actual: actual ?? 0, actualPct: null, targetPct, deltaPp: null, state: 'na', partial: false };
      continue;
    }
    const actualPct = Math.round((actual / allocBase) * 1000) / 10;
    const deltaPp = Math.round((actualPct - targetPct) * 10) / 10;
    const partial = pending && (key === 'opex' || key === 'contractors' || key === 'facility');
    const state: BucketDrift['state'] = deltaPp >= OVER_PP ? 'over' : deltaPp <= -OVER_PP ? 'under' : 'on';
    out[key] = { key, label: DRIFT_LABEL[key], actual, actualPct, targetPct, deltaPp, state, partial };
  }
  return out;
}

function driftTone(t: any, state: string) {
  if (state === 'over') return { fg: t.bad, bg: t.badSoft };
  if (state === 'under') return { fg: t.good, bg: t.goodSoft };
  return { fg: t.muted, bg: t.surface2 };
}

// ---- Weekly TAP snapshots (server-persisted, carry-forward) -----------------
// A week's EFFECTIVE TAP = its own saved row, else the nearest EARLIER row
// (whatever you set stays in effect until you change it), else the defaults.
// This is what makes Profit-First history true: past weeks render with the TAP
// actually in effect that week, never today's values.
type TapRow = { week_start: string; targets: Record<string, number> };
export function effectiveTap(rows: TapRow[], weekStart: string): { targets: Record<string, number>; source: 'set' | 'inherited' | 'default'; from?: string } {
  let best: TapRow | null = null;
  for (const r of rows) if (r.week_start <= weekStart && (!best || r.week_start > best.week_start)) best = r;
  if (!best) return { targets: { ...PF_TARGETS }, source: 'default' };
  return { targets: { ...PF_TARGETS, ...best.targets }, source: best.week_start === weekStart ? 'set' : 'inherited', from: best.week_start };
}

// Load all TAP snapshot rows (tiny table) + a debounced, optimistic save.
// Non-owners can still nudge the inputs locally — the save just 403s and we flag it.
export function useTapWeeks() {
  const [rows, setRows] = React.useState<TapRow[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [saveErr, setSaveErr] = React.useState<string | null>(null);
  const timer = React.useRef<any>(null);
  React.useEffect(() => {
    fetch('/api/pf/taps').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d?.rows) setRows(d.rows); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = React.useCallback((weekStart: string, targets: Record<string, number>) => {
    setRows((rs) => {
      const next = rs.filter((r) => r.week_start !== weekStart);
      next.push({ week_start: weekStart, targets: { ...targets } });
      return next.sort((a, b) => (a.week_start < b.week_start ? -1 : 1));
    });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetch('/api/pf/tap', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ weekStart, targets }) })
        .then((r) => setSaveErr(r.ok ? null : r.status === 403 ? 'TAP edits are owner-only — this change is a local preview and won’t persist.' : 'TAP save failed — retry the edit.'))
        .catch(() => setSaveErr('TAP save failed — retry the edit.'));
    }, 600);
  }, []);
  return { rows, loaded, save, saveErr };
}

// Rolling-90-day REAL cost run-rate per bucket (% of real revenue) — the
// reality-check reference shown beside each baseline TAP. Uses the same live
// cost engine as the P&L / drift (day.costBuckets). OpEx / Contractors /
// Facility include the QuickBooks fixed-overhead slice, so when QBO is paused
// they UNDERSTATE — we return null for those (never a misleadingly-low number
// that would falsely read as "plenty of headroom"). COGS/packaging are always
// real. Biz Taxes / Profit are policy reserves with no cost run-rate.
export function useBaselineRunRate() {
  const [data, setData] = React.useState<any>(null);
  React.useEffect(() => {
    fetch('/api/metrics?range=l90').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setData(d); }).catch(() => {});
  }, []);
  return React.useMemo(() => {
    const dd = data?.day;
    if (!dd || dd.revenue == null || dd.shopifyDown) return null;
    const realRev = dd.revenueExTax != null ? dd.revenueExTax : dd.revenue - (dd.salesTax ?? 0);
    const cb = dd.costBuckets;
    if (!cb || !(realRev > 0)) return null;
    const pending = !!cb.overheadPending;
    const pct = (k: string) => (cb[k] != null ? Math.round((cb[k] / realRev) * 1000) / 10 : null);
    return {
      opex: pending ? null : pct('opex'),
      inventory: pct('inventory'),
      contractors: pending ? null : pct('contractors'),
      facility: pending ? null : pct('facility'),
      shipmat: pct('shipmat'),
      pending,
    } as Record<string, any>;
  }, [data]);
}

// ---- Allocation + payroll coverage ------------------------------------------
const PAYROLL_TARGET_FALLBACK = 7000; // seed until the server/owner provides one

// The Profit-First split: fixed-cost targets + live ad % come off the top; Profit
// (capped at its target) then Owners Pay take the residual. Shared by the Allocation
// table, the payroll monitor, and the Morning Brief so they never disagree.
export function computeAllocation(dd: any, targets: Record<string, number>, allocBase: number) {
  const adSpend = dd?.adSpend ?? 0;
  const adsPct = allocBase > 0 ? Math.round((adSpend / allocBase) * 1000) / 10 : 0;
  const fixedSum = targets.opex + targets.biztax + targets.inventory + targets.contractors + targets.facility + targets.shipmat;
  const residual = Math.round((100 - fixedSum - adsPct) * 10) / 10;
  const profitPct = Math.max(0, Math.min(targets.profit, Math.max(0, residual)));
  const ownersPct = Math.max(0, Math.round((Math.max(0, residual) - profitPct) * 10) / 10);
  return { adSpend, adsPct, fixedSum, residual, profitPct, ownersPct };
}

// Payroll coverage for the CURRENT month: is the residual Owners Pay allocation on
// pace to cover the monthly payroll obligation? `dd` MUST be current-month MTD data —
// the projection scales collected-to-date by the fraction of the month elapsed. The
// owner-editable target comes from the server (dd.payrollTarget).
export function computePayroll(dd: any, targets: Record<string, number>) {
  const target = dd?.payrollTarget ?? PAYROLL_TARGET_FALLBACK;
  const hasData = dd?.revenue != null && !dd.shopifyDown && !dd.shopifyApprox;
  const realRev = dd?.revenueExTax != null ? dd.revenueExTax : (dd?.revenue != null ? dd.revenue - (dd.salesTax ?? 0) : 0);
  const { ownersPct } = computeAllocation(dd, targets, realRev || 0);
  const collected = Math.round((realRev || 0) * ownersPct / 100);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayOfMonth = now.getDate();
  const projected = dayOfMonth > 0 ? Math.round((collected * daysInMonth) / dayOfMonth) : collected;
  return { target, hasData, realRev, ownersPct, collected, projected, dayOfMonth, daysInMonth };
}

// Current-month payroll coverage for the Daily Pulse Morning Brief.
// Uses the current week's EFFECTIVE TAP snapshot (carry-forward), matching the page.
export function useMonthPayroll() {
  const rm = useRangeMetrics('custom');
  const m = monthRange(0);
  const taps = useTapWeeks();
  React.useEffect(() => { rm.setCustom({ start: m.start, end: m.end }); }, [m.start, m.end]);
  const dd: any = rm.data?.day || {};
  const pay = computePayroll(dd, effectiveTap(taps.rows, weekRange(0).start).targets);
  return { ...pay, shortfall: Math.max(0, Math.round(pay.target - pay.projected)), onPace: pay.projected >= pay.target, monthLabel: m.label, loading: rm.loading };
}

// Current Sun–Sat week's over-target buckets — for the Daily Pulse Morning Brief.
// Uses the current week's EFFECTIVE TAP snapshot (carry-forward), matching the page.
export function useWeekDrift() {
  const rm = useRangeMetrics('custom');
  const wk = weekRange(0);
  const taps = useTapWeeks();
  React.useEffect(() => { rm.setCustom({ start: wk.start, end: wk.end }); }, [wk.start, wk.end]);
  const dd: any = rm.data?.day || {};
  const hasData = dd.revenue != null && !dd.shopifyDown && !dd.shopifyApprox && dd.costBuckets != null;
  const realRev = dd.revenueExTax != null ? dd.revenueExTax : (dd.revenue != null ? dd.revenue - (dd.salesTax ?? 0) : 0);
  const drift = computeBucketDrift(dd, effectiveTap(taps.rows, wk.start).targets, realRev || 0);
  const over = Object.values(drift).filter((d) => d.state === 'over');
  return { over, hasData, loading: rm.loading, label: wk.label, partial: !!dd.costBuckets?.overheadPending };
}

export function AllocateView() {
  const t = useT();
  const isMobile = useIsMobile();
  const rm = useRangeMetrics('custom');
  const [mode, setMode] = React.useState<'week' | 'month' | 'custom'>('week');
  // Land on the LAST FULL week (owner: the current week is half-finished, so it
  // trips every drift warning on arrival — step forward to it deliberately).
  const [wOff, setWOff] = React.useState(-1); // 0 = current week, -1 = last week …
  const [mOff, setMOff] = React.useState(0);
  const period = mode === 'week' ? weekRange(wOff) : mode === 'month' ? monthRange(mOff) : null;
  React.useEffect(() => {
    if (period) rm.setCustom({ start: period.start, end: period.end });
  }, [mode, period?.start, period?.end]);

  const dd: any = rm.data?.day || {};
  const hasRev = dd.revenue != null && !dd.shopifyDown;
  const revenue = hasRev ? dd.revenue : null;
  const salesTax = hasRev ? (dd.salesTax ?? 0) : null;
  const realRev = hasRev ? (dd.revenueExTax != null ? dd.revenueExTax : dd.revenue - (dd.salesTax ?? 0)) : null;
  const periodLabel = period?.label || rm.data?.label || 'Selected range';

  // ---- Allocation model ------------------------------------------------------
  // Ads = actual spend this week (variable). The cost buckets are editable target
  // %s (seeded from June real-cost averages + buffer). Profit + Owners Pay take
  // whatever's left — last in line, squeezed when ad spend runs hot.
  const allocBase = realRev ?? 0;
  // TAP snapshots: the selected week renders with ITS effective TAP (its own
  // saved row, else carry-forward from the nearest earlier week, else defaults).
  // Edits save automatically to the week being viewed — editing the current week
  // rolls forward to future weeks; editing a past week corrects history only.
  // Month/Custom views use the current week's effective TAP.
  const taps = useTapWeeks();
  const runRate = useBaselineRunRate(); // rolling-90d real cost run-rate (reality check vs baseline)
  const tapWeekStart = mode === 'week' && period ? period.start : weekRange(0).start;
  const eff = React.useMemo(() => effectiveTap(taps.rows, tapWeekStart), [taps.rows, tapWeekStart]);
  const effKey = JSON.stringify(eff.targets);
  const [tgt, setTgt] = React.useState<Record<string, number>>({ ...PF_TARGETS });
  React.useEffect(() => { setTgt(JSON.parse(effKey)); }, [effKey, tapWeekStart]);
  const setT = (k: string) => (v: number) => setTgt((p) => { const next = { ...p, [k]: v }; taps.save(tapWeekStart, next); return next; });
  const { adsPct, fixedSum, residual, profitPct, ownersPct } = computeAllocation(dd, tgt, hasRev ? allocBase : 0);
  // Cost-drift: each cost bucket's ACTUAL spend this window vs its target %.
  const drift = computeBucketDrift(dd, tgt, allocBase);
  const overBuckets = hasRev ? Object.values(drift).filter((d) => d.state === 'over') : [];
  const driftPartial = Object.values(drift).some((d) => d.partial);
  const hasActuals = hasRev && dd.costBuckets != null;
  const over = residual < 0; // ad spend + fixed costs exceed 100%
  const ROWS: any[] = [
    { key: 'opex', name: 'OpEx – Bills', bank: 'OpEx - Bills', pct: tgt.opex, edit: 'opex', sw: '#7b6cf0', note: 'software, subs, postage, fees + other bills (payroll → Owners Pay · bookkeeper → Contractors)' },
    { key: 'biztax', name: 'Biz Taxes', bank: 'Holdings → Biz Taxes', pct: tgt.biztax, edit: 'biztax', sw: '#ff6f5e', note: 'income-tax reserve' },
    { key: 'inventory', name: 'Inventory', bank: 'Inventory', pct: tgt.inventory, edit: 'inventory', sw: '#6fe3a0', note: 'product COGS + reorder reserve' },
    { key: 'contractors', name: 'Contractors', bank: 'Contractors n Affiliates', pct: tgt.contractors, edit: 'contractors', sw: '#a879e6', note: 'contract labor + affiliates + bookkeeper/CPA' },
    { key: 'facility', name: 'Facility / 3PL', bank: 'Facility n 3PL', pct: tgt.facility, edit: 'facility', sw: '#c9a648', note: `rent ${money(dd.facilityRate ?? 2.25)}/order + pick/pack + 3PL fixed` },
    { key: 'shipmat', name: 'Ship Materials', bank: 'Inventory', pct: tgt.shipmat, edit: 'shipmat', sw: '#f8925c', note: 'packaging' },
    { key: 'ads', name: 'Ads', bank: 'Ad Spend', pct: adsPct, live: true, sw: '#5b8def', note: dd.adSpendProvisional ? 'live · provisional (same-day lag)' : 'live · actual spend this week' },
    { key: 'profit', name: 'Profit', bank: 'Profit → Treasure Chest', pct: profitPct, edit: 'profit', capped: residual >= 0 && tgt.profit > residual, sw: '#57c98a', note: 'residual · quarterly, split 50/50 to Treasure Chest' },
    { key: 'owners', name: 'Owners Pay', bank: 'Owners Pay', pct: ownersPct, residual: true, sw: '#6fe3a0', note: 'residual · funds owner salaries + payroll taxes (coverage tracked above)' },
  ];
  const totalPct = Math.round((fixedSum + adsPct + profitPct + ownersPct) * 10) / 10;
  // Bucket bars scale to the largest allocation (largest = full width)
  const maxPct = Math.max(1, ...ROWS.map((r: any) => r.pct || 0));

  const GRID = isMobile ? 'minmax(130px,1fr) 90px 92px 96px' : 'minmax(180px,1fr) 72px 104px 116px'; // Bucket · TAP · AP · Deposit
  // On phones the four columns can't fit ~360px, so the whole table scrolls
  // sideways as one unit (header + rows + total stay aligned) inside this width.
  const gridMinW = isMobile ? 520 : 'auto';
  const stepBtn: any = { width: 30, height: 30, borderRadius: 8, border: '1px solid ' + t.border, background: t.surface2, color: t.fg2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const modePill = (active: boolean): any => ({ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: '0.03em', textTransform: 'uppercase', padding: '5px 12px', borderRadius: 999, border: 'none', cursor: 'pointer', background: active ? t.fg : 'transparent', color: active ? t.surface : t.muted });
  const dateInput: any = { fontFamily: FIGTREE, fontSize: 12, padding: '5px 8px', borderRadius: 8, border: '1px solid ' + t.borderStrong, background: t.surface, color: t.fg, outline: 'none' };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* header — shared Daily Pulse pattern */}
      <div style={{ marginBottom: 2 }}>
        <Title size={isMobile ? 24 : 32}>Profit-First Allocation</Title>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, maxWidth: 640 }}>Set aside sales tax first, then allocate your real revenue into bank buckets. Pick a Sun–Sat week (or a month to reconcile).</div>
      </div>

      {/* Period toolbar: Week (Sun–Sat) / Month / Custom + navigator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', background: 'rgba(20,15,42,0.6)', border: '1px solid rgba(150,130,230,0.16)', borderRadius: 999, padding: 4 }}>
          <button onClick={() => setMode('week')} style={modePill(mode === 'week')}>Week</button>
          <button onClick={() => setMode('month')} style={modePill(mode === 'month')}>Month</button>
          <button onClick={() => setMode('custom')} style={modePill(mode === 'custom')}>Custom</button>
        </div>
        {mode !== 'custom' ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button aria-label="Previous" style={stepBtn} onClick={() => (mode === 'week' ? setWOff((v) => v - 1) : setMOff((v) => v - 1))}><Icon name="chevron" size={16} color={t.fg2} style={{ transform: 'rotate(180deg)' }} /></button>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, minWidth: isMobile ? 0 : 180, textAlign: 'center' }}>{periodLabel}</span>
            <button aria-label="Next" disabled={(mode === 'week' ? wOff : mOff) >= 0} style={{ ...stepBtn, opacity: (mode === 'week' ? wOff : mOff) >= 0 ? 0.4 : 1, cursor: (mode === 'week' ? wOff : mOff) >= 0 ? 'not-allowed' : 'pointer' }} onClick={() => (mode === 'week' ? setWOff((v) => Math.min(0, v + 1)) : setMOff((v) => Math.min(0, v + 1)))}><Icon name="chevron" size={16} color={t.fg2} /></button>
            {((mode === 'week' && wOff !== 0) || (mode === 'month' && mOff !== 0)) && (
              // Jump-to-current BUTTON (not a status flag) — appears whenever you're
              // viewing a past period; bordered + arrow so it reads as an action.
              <button onClick={() => (mode === 'week' ? setWOff(0) : setMOff(0))} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: OSWALD, fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '6px 12px', borderRadius: 999, border: '1px solid ' + t.accent, background: 'rgba(248,146,92,0.10)', color: t.accent, cursor: 'pointer' }}>
                Jump to {mode === 'week' ? 'this week' : 'this month'}<Icon name="chevron" size={13} color={t.accent} />
              </button>
            )}
          </div>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="date" value={rm.custom.start || ''} max={rm.custom.end || undefined} onChange={(e) => rm.setCustom({ ...rm.custom, start: e.target.value })} style={dateInput} />
            <span style={{ color: t.muted, fontFamily: FIGTREE }}>–</span>
            <input type="date" value={rm.custom.end || ''} min={rm.custom.start || undefined} onChange={(e) => rm.setCustom({ ...rm.custom, end: e.target.value })} style={dateInput} />
          </span>
        )}
        <button onClick={rm.refresh} title="Refresh now" aria-label="Refresh" style={{ ...stepBtn, marginLeft: 'auto', borderRadius: 999, width: 34, height: 34, background: 'transparent', border: '1px solid rgba(150,130,230,0.2)' }}>
          <Icon name="refresh" size={15} color={t.muted} style={{ animation: rm.loading ? 'bbspin 0.8s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Step 1 — set aside sales tax → real revenue (the allocation base) */}
      <Card pad={0} style={{ padding: '22px 26px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Real Revenue · {periodLabel}</span>
          {dd.orders != null && <Badge tone="purple">{dd.orders.toLocaleString()} orders</Badge>}
        </div>
        {!hasRev ? (
          <div style={{ padding: '10px 0 6px', fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>{dd.shopifyDown ? 'Shopify is temporarily unreachable — figures paused.' : 'Loading revenue + sales tax…'}</div>
        ) : (
          <div style={{ margin: '8px 0 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 0', borderBottom: '1px solid rgba(150,130,230,0.09)' }}>
              <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: t.fg }}>Revenue <span style={{ color: t.faint, fontWeight: 500, fontSize: 11.5, marginLeft: 4 }}>total sales, incl. tax + shipping</span></span>
              <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 15, color: t.fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(revenue!)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 0 11px 18px', borderBottom: '1px solid rgba(150,130,230,0.09)' }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ color: t.faint, fontSize: 13 }}>−</span><span style={{ fontFamily: FIGTREE, fontSize: 14, color: t.sidebarMuted }}>Sales tax collected</span><span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>all states — set aside, not yours</span></span>
              <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 14.5, color: t.bad, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>({money(salesTax!)})</span>
            </div>
            <div style={{ background: 'rgba(248,146,92,0.09)', borderLeft: '3px solid ' + t.accent, borderRadius: '0 10px 10px 0', padding: '13px 14px', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, color: t.fg }}>Real revenue <span style={{ color: t.muted, fontWeight: 600, fontSize: 11.5, marginLeft: 4 }}>Profit-First allocation base</span></span>
              <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 18, color: t.accent, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(realRev!)}</span>
            </div>
          </div>
        )}
        <div style={{ paddingTop: 12, fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.5 }}>
          Sales tax is money you hold for the states, so Profit-First allocates from <b>real revenue</b> (revenue − tax). Tax total is pulled live from Shopify across <b>all orders/states</b> in the window.
        </div>
      </Card>

      {/* Payroll coverage — always the CURRENT month (payroll is a monthly obligation).
          Gets the snapshot rows so each week's deposit uses ITS OWN TAP. */}
      <PayrollCoverageCard tapRows={taps.rows} />

      {over && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 15px', border: '1px solid ' + t.bad, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.bad, background: t.badSoft }}>
          <Icon name="alert" size={15} color={t.bad} />
          <div style={{ flex: 1, fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2 }}>Ad spend + fixed costs are <b style={{ color: t.bad }}>{Math.abs(residual)}% over</b> — nothing left for Profit or Owners Pay this week. Cut ad spend or trim a bucket, or it's a discussion.</div>
        </div>
      )}

      {/* Cost-drift summary — buckets whose ACTUAL spend ran over their target % */}
      {hasActuals && overBuckets.length > 0 && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 15px', border: '1px solid ' + t.warn, borderRadius: t.cardRadius, borderLeft: '3px solid ' + t.warn, background: t.warnSoft }}>
          <Icon name="alert" size={15} color={t.warn} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ flex: 1, fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.55 }}>
            <b style={{ color: t.warn }}>{overBuckets.length} {overBuckets.length > 1 ? 'buckets' : 'bucket'} over target</b> for {periodLabel}: {overBuckets.map((d) => `${d.label} +${d.deltaPp}pp`).join(' · ')}. Actual spend outran the budgeted share of real revenue — trim the bucket or raise its target if this is the new normal.{driftPartial ? <span style={{ color: t.muted }}> <b>*</b> OpEx/Contractors/Facility exclude QuickBooks fixed overhead (paused) — actuals understate.</span> : ''}
          </div>
        </div>
      )}

      <Card pad={0} style={{ padding: '22px 26px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 10 }}>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Allocation Buckets</span>
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {mode === 'week' && taps.loaded && (
              eff.source === 'set' ? <Badge tone="purple">Custom TAP · saved</Badge>
                : eff.source === 'inherited' ? <Badge tone="neutral">TAP carried from {new Date(eff.from + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Badge>
                : <Badge tone="neutral">Baseline TAP</Badge>
            )}
            {hasActuals && (overBuckets.length > 0 ? <Badge tone="warn">{overBuckets.length} over target</Badge> : <Badge tone="good">On target</Badge>)}
            <Badge tone={totalPct === 100 ? 'good' : 'accent'}>{totalPct}% allocated</Badge>
          </div>
        </div>
        <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginBottom: 14, lineHeight: 1.5 }}><b>TAP</b> (Target Allocation %) = % of real revenue ({money(allocBase, 0)}) — <b>Deposit</b> is the $ to transfer to each bank. <b>CAP</b> (Current Actual %) is what you really spent this window (live), with drift vs TAP. Ads = live actual; Profit + Owners Pay take the remainder. TAP edits <b>save automatically</b> and carry forward until you change them — editing a past week corrects that week&apos;s history only. Under each input: <b>baseline</b> is the company target/limit (yellow if this week&apos;s TAP is above it) and <b>90d</b> is the real trailing-90-day cost run-rate — your reality check on whether the target still fits{runRate?.pending ? ' (OpEx/Contractors/Facility 90d hidden while QuickBooks overhead is paused)' : ''}.{taps.saveErr ? <span style={{ color: t.warn }}> {taps.saveErr}</span> : ''}</div>
        <div style={{ overflowX: isMobile ? 'auto' : 'visible', margin: isMobile ? '0 -2px' : 0 }}>
        <div style={{ minWidth: gridMinW }}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 12, padding: '0 0 10px', borderBottom: '1px solid rgba(150,130,230,0.14)' }}>
          <span style={hCell(t)}>Bucket → bank account</span>
          <span style={{ ...hCell(t), textAlign: 'center' }}>TAP</span>
          <span style={{ ...hCell(t), textAlign: 'right' }}>CAP</span>
          <span style={{ ...hCell(t), textAlign: 'right' }}>Deposit</span>
        </div>
        {ROWS.map((r: any, i: number) => (
          <div key={r.key} style={{ padding: '12px 0', borderTop: i ? '1px solid rgba(150,130,230,0.07)' : 'none' }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg }}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: r.sw, marginRight: 7 }} />{r.name} <span style={{ color: t.faint, fontWeight: 500, fontSize: 11 }}>→ {r.bank}</span></div>
                <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginTop: 2 }}>{r.note}{r.capped ? <span style={{ color: t.warn }}> · capped by what's left</span> : ''}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                {r.edit ? (
                  <>
                    <NumInput value={tgt[r.edit]} onChange={setT(r.edit)} step={0.5} suffix="%" width={44} />
                    {/* company baseline TAP (the limit to gauge against; yellow the
                        moment this week's TAP exceeds it) + the rolling-90d REAL cost
                        run-rate as a reality check (null when QBO-paused understates it) */}
                    <span title="Baseline = the company target/limit for this bucket. 90d = the real trailing-90-day cost run-rate (reality check)." style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 10, whiteSpace: isMobile ? 'normal' : 'nowrap', textAlign: 'center', lineHeight: 1.4 }}>
                      <span style={{ color: tgt[r.edit] > (PF_TARGETS[r.edit] ?? 0) ? t.warn : t.faint }}>baseline {PF_TARGETS[r.edit]}%{tgt[r.edit] > (PF_TARGETS[r.edit] ?? 0) ? ' · over' : ''}</span>
                      {runRate && runRate[r.edit] != null && (
                        <span style={{ color: runRate[r.edit] > (PF_TARGETS[r.edit] ?? 0) ? t.warn : t.good }}> · 90d {runRate[r.edit]}%</span>
                      )}
                    </span>
                  </>
                ) : (
                  <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, color: r.live ? '#5b8def' : t.accent, fontVariantNumeric: 'tabular-nums' }}>{r.pct}%</span>
                )}
              </div>
              <ActualCell r={r} dr={drift[r.key]} t={t} hasActuals={hasActuals} />
              <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.pct <= 0 ? t.faint : t.fg }}>{money(allocBase * r.pct / 100)}</span>
            </div>
            <div style={{ marginTop: 8, height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.05)' }}>
              <div style={{ width: Math.min(100, Math.max(0, (r.pct / maxPct) * 100)) + '%', height: '100%', borderRadius: 999, background: r.sw, transition: 'width 200ms' }} />
            </div>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 12, padding: '14px 0 0', borderTop: '1px solid rgba(150,130,230,0.14)', marginTop: 4 }}>
          <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11, color: t.muted }}>Total</span>
          <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, textAlign: 'center', color: totalPct === 100 ? t.good : t.warn }}>{totalPct}%</span>
          <span />
          <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: t.fg }}>{money(allocBase * totalPct / 100)}</span>
        </div>
        </div>
        </div>
      </Card>

      {/* Treasure Chest — the quarterly Profit accrual, split 50/50 */}
      <TreasureChestCard />

      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.55, padding: '0 2px' }}>
        <b style={{ color: t.fg2 }}>Live now:</b> cost-drift (the <b>Actual</b> column + the alert compare each bucket's real spend to its target), <b>payroll coverage</b> (is Owners Pay on pace to cover the monthly payroll target), and the <b>Treasure Chest</b> (this quarter's Profit accrual, split 50/50). <b style={{ color: t.fg2 }}>Coming next:</b> recording the quarter-end distribution + a growing cumulative reserve, and <b>Holdings</b> (month-end Biz-Tax reconcile → top up OpEx/Inventory or send to taxes).
      </div>
    </div>
  );
}

// ---- Treasure Chest ---------------------------------------------------------
// Quarterly ritual: each Sun–Sat week's Profit-bucket allocation accrues over the
// quarter; at quarter-end it splits 50/50 → half to a growing reserve (parks in
// Holdings), half distributed evenly to the owners as a bonus ON TOP of Owners Pay.
// Read-only for now (server = /api/treasure); recording the distribution + the
// cumulative reserve is the next chunk.
type TreasureData = { quarter: string; label: string; isCurrent: boolean; accrued: number; reserveHalf: number; distributionHalf: number; perOwner: number; owners: number; weeksElapsed: number; weeksTotal: number; partial: boolean };

// Client-side quarter string ("YYYY-Qn") shifted by `off` quarters from now. The
// server recomputes isCurrent authoritatively; this only drives the navigator label.
function quarterStr(off: number): string {
  const now = new Date();
  let qi = Math.floor(now.getMonth() / 3) + 1 + off;
  let y = now.getFullYear();
  while (qi > 4) { qi -= 4; y += 1; }
  while (qi < 1) { qi += 4; y -= 1; }
  return `${y}-Q${qi}`;
}

function TreasureChestCard(): any {
  const t = useT();
  const [qOff, setQOff] = React.useState(0); // 0 = current quarter, -1 = last …
  const [data, setData] = React.useState<TreasureData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(false);
  const quarter = quarterStr(qOff);

  React.useEffect(() => {
    let live = true;
    setLoading(true); setErr(false);
    fetch(`/api/treasure?quarter=${quarter}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => { if (live) { setData(j); setLoading(false); } })
      .catch(() => { if (live) { setErr(true); setLoading(false); } });
    return () => { live = false; };
  }, [quarter]);

  const stepBtn: any = { width: 30, height: 30, borderRadius: 999, border: '1px solid rgba(150,130,230,0.2)', background: 'transparent', color: t.fg2, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const splitTile = (title: string, sub: string, val: number, color: string): any => (
    <div style={{ flex: 1, minWidth: 150, background: 'rgba(13,10,30,0.35)', border: '1px solid rgba(150,130,230,0.16)', borderRadius: 12, padding: '13px 15px' }}>
      <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg }}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: color, marginRight: 7 }} />{title}</div>
      <FitValue size={26} style={{ marginTop: 6 }}>{money(val, 0)}</FitValue>
      <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.muted, marginTop: 4 }}>{sub}</div>
    </div>
  );

  return (
    <div style={{ background: 'linear-gradient(160deg,#33285f,#241c48)', border: '1px solid rgba(150,130,230,0.16)', borderRadius: t.cardRadius, padding: '22px 26px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Treasure Chest</span>
        <Badge tone="neutral">Quarterly</Badge>
        {data?.partial && <Badge tone="warn">Still tallying</Badge>}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button aria-label="Previous quarter" style={stepBtn} onClick={() => setQOff((v) => v - 1)}><Icon name="chevron" size={16} color={t.fg2} style={{ transform: 'rotate(180deg)' }} /></button>
          <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, minWidth: 78, textAlign: 'center' }}>{data?.label || quarterLabelOf(quarter)}</span>
          <button aria-label="Next quarter" disabled={qOff >= 0} style={{ ...stepBtn, opacity: qOff >= 0 ? 0.4 : 1, cursor: qOff >= 0 ? 'not-allowed' : 'pointer' }} onClick={() => setQOff((v) => Math.min(0, v + 1))}><Icon name="chevron" size={16} color={t.fg2} /></button>
        </div>
      </div>

      {err ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '10px 0' }}>Couldn’t load the Treasure Chest for this quarter.</div>
      ) : loading || !data ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '10px 0' }}>Tallying this quarter’s profit…</div>
      ) : (
        <>
          <div style={{ margin: '6px 0 4px' }}>
            <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10.5, color: t.muted }}>{data.isCurrent ? 'Profit accrued so far' : 'Profit accrued'} <span style={{ color: t.faint }}>· {data.weeksElapsed} of {data.weeksTotal} weeks</span></span>
            <FitValue size={34} color={t.accent} style={{ marginTop: 6 }}>{money(data.accrued, 0)}</FitValue>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <FitGroup>
            {splitTile('Treasure Chest reserve', 'half — parks in Holdings', data.reserveHalf, '#57c98a')}
            {splitTile('Owner distribution', `half — ${money(data.perOwner, 0)} × ${data.owners} owners`, data.distributionHalf, '#6fe3a0')}
            </FitGroup>
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, lineHeight: 1.55, marginTop: 12, paddingTop: 12, borderTop: '1px solid ' + t.border }}>
            Each Sun–Sat week’s Profit bucket (capped at its 5% target) accrues over the quarter. {data.isCurrent ? <>At quarter-end this splits <b>50/50</b> — half grows the reserve, half is your distribution.</> : <>This quarter closed — <b>50/50</b> split shown.</>} The distribution is a <b>bonus on top of Owners Pay</b> (which funds your regular salary). Recording the distribution + a running cumulative reserve is coming next.{data.partial ? ' Some weeks are still being computed — refresh shortly for the final figure.' : ''}
          </div>
        </>
      )}
    </div>
  );
}

// Fallback quarter label ("Q3 2026") while the server response is in flight.
function quarterLabelOf(q: string): string {
  const [y, qs] = q.split('-Q');
  return `Q${qs} ${y}`;
}

// ---- Weekly deposits for payroll coverage -----------------------------------
// The owner transfers Owners Pay at each WEEK'S END (Sun–Sat, deposited on/after
// Saturday), so "funded" must only count COMPLETED weeks — a live MTD accrual
// always reads ahead of the cash actually moved. A week belongs to the month its
// SATURDAY falls in (that's when its transfer happens).
function monthDepositWeeks() {
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const todayYmd = ymdLocal(today);
  const m = monthRange(0);
  const first = new Date(m.start + 'T12:00:00');
  // first Saturday on/after the 1st
  const sat0 = new Date(first); sat0.setDate(first.getDate() + ((6 - first.getDay() + 7) % 7));
  const weeks: { start: string; end: string; done: boolean }[] = [];
  for (const s = new Date(sat0); s.getMonth() === first.getMonth(); s.setDate(s.getDate() + 7)) {
    const sun = new Date(s); sun.setDate(s.getDate() - 6);
    const end = ymdLocal(s);
    weeks.push({ start: ymdLocal(sun), end, done: end < todayYmd }); // done once Saturday has fully passed
  }
  return { weeks, todayYmd, monthLabel: m.label };
}

// Sum each completed week's Owners Pay allocation (that week's real revenue ×
// its residual % under THAT WEEK'S effective TAP snapshot), plus the in-progress
// week's accrual-to-date (shown separately, never counted as funded). Past weeks
// are KV-cached server-side, so this is a handful of cheap sequential fetches.
function useWeeklyDeposits(tapRows: TapRow[]) {
  const [st, setSt] = React.useState<{ deposited: number; weeksDone: number; weeksTotal: number; accruing: number | null; loading: boolean }>({ deposited: 0, weeksDone: 0, weeksTotal: 0, accruing: null, loading: true });
  const tapKey = JSON.stringify(tapRows);
  React.useEffect(() => {
    let live = true;
    (async () => {
      const rows: TapRow[] = JSON.parse(tapKey);
      const { weeks, todayYmd } = monthDepositWeeks();
      let deposited = 0, weeksDone = 0;
      let accruing: number | null = null;
      for (const w of weeks) {
        const end = w.done ? w.end : (w.start <= todayYmd ? todayYmd : null);
        if (!end) continue; // week hasn't started
        const p = new URLSearchParams({ range: 'custom', start: w.start, end });
        const d = await fetch(`/api/metrics?${p.toString()}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
        const dd = d?.day;
        if (!dd || dd.revenue == null || dd.shopifyDown) continue;
        const realRev = dd.revenueExTax != null ? dd.revenueExTax : dd.revenue - (dd.salesTax ?? 0);
        const { ownersPct } = computeAllocation(dd, effectiveTap(rows, w.start).targets, realRev || 0);
        const amt = Math.round(((realRev || 0) * ownersPct) / 100);
        if (w.done) { deposited += amt; weeksDone++; } else { accruing = amt; }
      }
      if (live) setSt({ deposited, weeksDone, weeksTotal: weeks.length, accruing, loading: false });
    })();
    return () => { live = false; };
  }, [tapKey]);
  return st;
}

// Payroll coverage card — is Owners Pay covering the monthly payroll obligation?
// Always the CURRENT month (payroll is monthly, independent of the page's period
// picker). The BAR only counts week-end deposits (completed Sun–Sat weeks); the
// in-progress week shows as a faint "accruing" segment that converts to funded
// when its Saturday passes. The month-end projection stays as a separate,
// clearly-labeled forecast sentence. Target is edited on the Expenses tab.
function PayrollCoverageCard({ tapRows }: any): any {
  const t = useT();
  const rm = useRangeMetrics('custom');
  const m = monthRange(0);
  React.useEffect(() => { rm.setCustom({ start: m.start, end: m.end }); }, [m.start, m.end]);
  const dd: any = rm.data?.day || {};
  const pay = computePayroll(dd, effectiveTap(tapRows || [], weekRange(0).start).targets); // MTD accrual → the pace projection only
  const dep = useWeeklyDeposits(tapRows || []); // week-end deposits → the bar (each week under its own TAP)
  const effTarget = pay.target;
  const onPace = pay.projected >= effTarget;
  const shortfall = Math.max(0, Math.round(effTarget - pay.projected));
  const depPct = effTarget > 0 ? Math.round((dep.deposited / effTarget) * 100) : 0;
  const accrPct = effTarget > 0 && dep.accruing != null ? Math.round((dep.accruing / effTarget) * 100) : 0;
  const pacePct = pay.daysInMonth > 0 ? Math.round((pay.dayOfMonth / pay.daysInMonth) * 100) : 0;
  const barColor = onPace ? t.good : t.bad;
  return (
    <Card pad={0} style={{ padding: '22px 26px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>Payroll Coverage</span>
        <Badge tone="neutral">{m.label}</Badge>
        {pay.hasData && <Badge tone={onPace ? 'good' : 'bad'}>{onPace ? 'On pace' : `Short ${money(shortfall, 0)}`}</Badge>}
      </div>
      {!pay.hasData ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '8px 0' }}>{dd.shopifyDown ? 'Shopify is temporarily unreachable — coverage paused.' : 'Loading this month’s numbers…'}</div>
      ) : (
        <>
          {/* Funded = week-end deposits only. Faint segment = current week accruing. */}
          <div style={{ margin: '8px 0 6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>Deposited · completed weeks only</span>
              <span style={{ fontFamily: FIGTREE, fontSize: 14.5, fontWeight: 800, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{dep.loading ? '…' : money(dep.deposited, 0)} <span style={{ color: t.faint, fontWeight: 500, fontSize: 12 }}>of {money(effTarget, 0)}</span></span>
            </div>
            <div style={{ position: 'relative' }}>
              <div style={{ height: 12, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', display: 'flex' }}>
                <div style={{ width: Math.min(100, depPct) + '%', height: '100%', background: 'linear-gradient(90deg,#f8925c,#ff8b7d)', transition: 'width 200ms' }} />
                {accrPct > 0 && <div title="Accruing this week — deposits Saturday" style={{ width: Math.min(100 - Math.min(100, depPct), accrPct) + '%', height: '100%', background: 'rgba(248,146,92,0.25)', transition: 'width 200ms' }} />}
              </div>
              {pacePct > 0 && pacePct < 100 && (
                <div title={`On-pace target for day ${pay.dayOfMonth}`} style={{ position: 'absolute', top: -3, bottom: -3, left: `calc(${pacePct}% - 1px)`, width: 2, background: '#e6dfff', opacity: 0.6, borderRadius: 2 }} />
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontFamily: FIGTREE, fontSize: 11, color: t.faint, flexWrap: 'wrap', gap: 6 }}>
              <span><b style={{ color: barColor }}>{dep.loading ? '…' : depPct + '% funded'}</b> · {dep.weeksDone} of {dep.weeksTotal} week deposit{dep.weeksTotal === 1 ? '' : 's'} in{dep.accruing != null ? <span style={{ color: t.muted }}> · +{money(dep.accruing, 0)} accruing this week — deposits Saturday</span> : ''}</span>
              <span>▏marker = today’s pace · day {pay.dayOfMonth} of {pay.daysInMonth}</span>
            </div>
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, lineHeight: 1.55, margin: '10px 0 2px' }}>
            Deposits land at each week’s end, so the bar only moves when a Sun–Sat week closes. Owners Pay is taking <b>{pay.ownersPct}%</b> of real revenue — <b>forecast:</b> at this pace the month finishes around <b style={{ color: barColor }}>~{money(pay.projected, 0)}</b> against your <b>{money(effTarget, 0)}</b> target.{onPace ? ' You’re on track to cover payroll.' : ` That’s ${money(shortfall, 0)} short — lift ad efficiency or trim a bucket to free up the residual for Owners Pay.`}
          </div>
        </>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, paddingTop: 12, borderTop: '1px solid ' + t.border, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Monthly payroll target</span>
        <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 15, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{money(effTarget, 0)}</span>
        <span style={{ marginLeft: 'auto', fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>salaries + payroll taxes, all-in · edit on the <b style={{ color: t.muted }}>Expenses</b> tab</span>
      </div>
    </Card>
  );
}

// Actual-spend + drift cell for a Profit-First bucket row. Cost buckets show
// actual % of real revenue + a drift pill vs target; Ads is live, Biz Taxes is a
// reserve, Profit/Owners are the residual (no target to drift against).
function ActualCell({ r, dr, t, hasActuals }: any): any {
  const muted: any = { fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, textAlign: 'right', display: 'block' };
  if (r.key === 'ads') return <span style={{ ...muted, color: '#4d7ec2', fontWeight: 700 }}>{r.pct}% · live</span>;
  if (r.key === 'biztax') return <span style={muted}>reserve</span>;
  if (r.residual || r.key === 'profit') return <span style={muted}>residual</span>;
  if (!hasActuals || !dr || dr.actualPct == null) return <span style={muted}>—</span>;
  const tone = driftTone(t, dr.state);
  const sign = dr.deltaPp > 0 ? '+' : '';
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>{dr.actualPct}%{dr.partial ? '*' : ''}</div>
      <div style={{ display: 'inline-block', marginTop: 3, padding: '1px 7px', borderRadius: 999, background: tone.bg, color: tone.fg, fontFamily: FIGTREE, fontSize: 10.5, fontWeight: 700 }}>{dr.state === 'on' ? 'on target' : `${sign}${dr.deltaPp}pp`}</div>
    </div>
  );
}

// ============================================================ EXPENSES
// The Expenses tab — the source of truth for recurring costs. Phase 1 (this): the
// three owner-editable rates/targets, relocated here from Profit-First / Per-Unit
// (facility rent, Bring fixed fees, monthly payroll target). Persisted to KV via
// POST /api/config/overhead (owner-only, audited); read via GET on mount. Later
// phases add editable ingredient / packaging / software lines, invoice upload →
// auto-COGS, and price-trend tracking (see the roadmap card below).

type OverheadCfg = { facilityRentPerOrder: number; bringFixedMonthly: number; payrollMonthlyTarget: number };

// One editable rate/target row: label + description on the left, stepper on the right.
function RateRow({ t, name, desc, value, step, prefix, suffix, onChange, last }: any) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, padding: '16px 0', borderBottom: last ? 'none' : '1px solid ' + t.border, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14.5, color: t.fg }}>{name}</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 5, lineHeight: 1.55, maxWidth: 600 }}>{desc}</div>
      </div>
      <NumInput box value={value} onChange={onChange} step={step} prefix={prefix} suffix={suffix} width={72} />
    </div>
  );
}

// Card header shared by every Expenses block — square colored dot + Oswald caps
// title + one-line hint, with an optional right-hand chip/action slot.
function ExpHead({ dot, title, hint, chips, right, mb = 16 }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap', marginBottom: mb }}>
      <span style={{ width: 11, height: 11, borderRadius: 4, background: dot, flexShrink: 0 }} />
      <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 13, color: t.fg }}>{title}</span>
      {chips}
      {hint && <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint }}>{hint}</span>}
      {right && <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{right}</span>}
    </div>
  );
}

// Count / total chip on a block header ("18 LINES").
function ExpChip({ children, tone }: any) {
  const t = useT();
  return <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.05em', fontSize: 10, color: tone || t.muted, background: 'rgba(150,130,230,0.13)', padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{children}</span>;
}

// Orange outline "+ Add line" / "+ Ingredient" pill.
function AddPill({ onClick, children }: any) {
  const t = useT();
  return (
    <button onClick={onClick} style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: '0.04em', fontSize: 11, color: t.accent, border: '1px solid rgba(248,146,92,0.45)', background: 'rgba(248,146,92,0.08)', padding: '6px 13px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap' }}>{children}</button>
  );
}

// Bare row-action glyph (clock/cog/x in the prototype) — muted, orange on hover.
function RowIcon({ icon, title, onClick, danger }: any) {
  const t = useT();
  const [h, setH] = React.useState(false);
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', color: h ? (danger ? t.bad : t.accent) : t.faint, transition: 'color 140ms' }}>
      <Icon name={icon} size={15} color="currentColor" strokeWidth={1.8} />
    </button>
  );
}

// ---- Facility rent due — the check amount ----------------------------------
// The facility is billed as a flat rate PER ORDER, so the bill for any window is
// just orders × rate. This lives on Expenses (not inside the P&L) because it's an
// accounts-payable answer — "what do I write the check for" — rather than a margin
// question, and the rate it multiplies is edited in the card directly below it.
// Defaults to Last Mo, the window you're almost always paying for; the range picker
// covers any other window (MTD to see the bill accruing, custom for a partial month).
function FacilityDue({ cardPad, rate }: any) {
  const t = useT();
  const isMobile = useIsMobile();
  const rm = useRangeMetrics('lastmonth');
  const dd: any = rm.data?.day || {};
  const perOrder = rate ?? dd.facilityRate ?? 2.25;
  const orders: number | null = dd.orders ?? null;
  const due = orders != null ? Math.round(orders * perOrder * 100) / 100 : null;
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (due == null) return;
    navigator.clipboard?.writeText(due.toFixed(2)).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
      () => {},
    );
  };
  const copyBtn: any = { fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 15px', borderRadius: 999, border: '1px solid ' + t.borderStrong, background: 'transparent', color: copied ? t.good : t.fg2, cursor: due == null ? 'default' : 'pointer', whiteSpace: 'nowrap' };

  return (
    <Card pad={cardPad}>
      <ExpHead mb={2} dot={t.accent} title="Facility Rent Due"
        chips={<ExpChip tone={t.accent}>BILL TO PAY</ExpChip>}
        right={<DateRangePicker range={rm.range} setRange={rm.setRange} custom={rm.custom} setCustom={rm.setCustom} onRefresh={rm.refresh} loading={rm.loading} />} />
      <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, margin: '0 0 18px 22px' }}>
        Orders shipped in the selected window × the per-order rate below — the amount to make the check out for.
      </div>

      {dd.shopifyDown ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, padding: '10px 0 4px' }}>
          Shopify is unreachable, so the order count is paused rather than shown wrong. Hit refresh in a minute.
        </div>
      ) : orders == null ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '10px 0 4px' }}>Counting orders…</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 22, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 10.5, color: t.faint, marginBottom: 6 }}>
              {rm.data?.label || 'Selected range'}
            </div>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: isMobile ? 40 : 54, lineHeight: 1, color: t.accent, fontVariantNumeric: 'tabular-nums' }}>
              {money(due as number)}
            </div>
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.sidebarMuted || t.fg2, marginTop: 10 }}>
              <b style={{ color: '#e6dfff', fontVariantNumeric: 'tabular-nums' }}>{orders.toLocaleString('en-US')}</b> orders × <b style={{ color: '#e6dfff' }}>{money(perOrder)}</b>/order
              {dd.shopifyApprox ? <span style={{ color: t.warn }}> · analytics degraded, order count is approximate</span> : null}
            </div>
          </div>
          <button onClick={copy} style={copyBtn}>{copied ? 'Copied ✓' : 'Copy amount'}</button>
        </div>
      )}
    </Card>
  );
}

// ---- Bills to Pay — the monthly payables checklist ---------------------------
// Every bill the owner pays by hand, grouped the way a bill-reminder app does it:
// past due → due today → coming up. Tick one off and its next cycle drops into
// "coming up". The server stores bills + payments (worker/bills.ts); the grouping
// is worked out here in the owner's local time. Amounts: facility rent is computed
// from orders × rate, Bring + the bookkeeper come from their newest invoice email,
// the rest are typed — and any of them can be overwritten before ticking.
type Bill = { id: string; name: string; kind: 'manual' | 'facility' | 'email'; amount: number; amount_for: string | null; due_day: number; every_months: number; start_period: string; email_query: string | null; sort: number };
type BillPay = { bill_id: string; period: string; amount: number; paid_at: string; paid_by: string | null };
type Invoice = { amount: number | null; dueDate: string | null; number: string | null; subject: string; snippet: string; receivedAt: string; mailbox: string; paid?: { subject: string; receivedAt: string; amount: number | null } | null };
type BillsData = { bills: Bill[]; payments: BillPay[]; invoices: Record<string, Invoice | null>; invoicesAt: string | null };

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const REPEAT_OPTS = [{ v: 1, l: 'Monthly' }, { v: 3, l: 'Every 3 months' }, { v: 12, l: 'Yearly' }, { v: 0, l: "Don't repeat (paid off)" }];
const pAdd = (p: string, n: number) => { const [y, m] = p.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const localPeriod = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const pLabel = (p: string) => { const [y, m] = p.split('-').map(Number); return `${MON[m - 1]} ${y}`; };
const dueOf = (b: Bill, p: string) => { const [y, m] = p.split('-').map(Number); return new Date(y, m - 1, Math.min(b.due_day, new Date(y, m, 0).getDate())); };
const billDay = (d: Date) => `${MON[d.getMonth()]} ${d.getDate()}`;
const isoShort = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-').map(Number); return billDay(new Date(y, m - 1, d)); };

function useBills() {
  const res = useCachedResource<BillsData>('bills', async ({ fresh }) => {
    const r = await fetch('/api/bills' + (fresh ? '?fresh=1' : ''));
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  });
  const mutate = async (method: string, url: string, body?: any) => {
    try {
      const r = await fetch(url, { method, headers: body ? { 'content-type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
      if (!r.ok) return false;
      res.set(await r.json());
      return true;
    } catch { return false; }
  };
  return {
    data: res.data, err: res.error, loading: res.loading || res.revalidating, refresh: res.refresh,
    create: (b: any) => mutate('POST', '/api/bills', b),
    update: (id: string, b: any) => mutate('PATCH', `/api/bills/${id}`, b),
    remove: (id: string) => mutate('DELETE', `/api/bills/${id}`),
    pay: (id: string, period: string, amount: number) => mutate('POST', `/api/bills/${id}/paid`, { period, amount }),
    unpay: (id: string, period: string) => mutate('DELETE', `/api/bills/${id}/paid/${period}`),
  };
}

type BillRow = { bill: Bill; period: string; due: Date; days: number; amount: number | null; source: string; next?: boolean };

function BillsToPay({ cardPad, rate }: any) {
  const t = useT();
  const isMobile = useIsMobile();
  const bills = useBills();
  // Same cache keys the facility card + Daily Pulse use, so these are usually free.
  const last = useRangeMetrics('lastmonth');
  const mtd = useRangeMetrics('mtd');
  const perOrder = rate ?? last.data?.day?.facilityRate ?? 2.25;
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [editing, setEditing] = React.useState<string | null>(null); // bill id or 'new'
  const [busy, setBusy] = React.useState<string | null>(null);
  const [showPaid, setShowPaid] = React.useState(false);
  const [showStopped, setShowStopped] = React.useState(false);
  type Pending = { kind: 'unpay' | 'remove' | 'payAhead'; bill: Bill; period?: string; amount?: number; row?: BillRow };
  const [pending, setPending] = React.useState<Pending | null>(null);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const curP = localPeriod(today);
  const data = bills.data;
  const active = (data?.bills || []).filter((b) => b.every_months > 0);
  const stopped = (data?.bills || []).filter((b) => b.every_months === 0);

  // What to prefill for one bill + cycle, and where the number came from.
  const amountFor = (b: Bill, p: string, paidFor: BillPay[]): { amount: number | null; source: string; dueIso?: string } => {
    if (b.amount_for === p) return { amount: b.amount, source: 'Amount you entered' };
    const lastPaid = paidFor[0];
    const paidAtIso = lastPaid ? lastPaid.paid_at.replace(' ', 'T') + 'Z' : '';
    if (b.kind === 'facility') {
      const prevMonth = pLabel(pAdd(p, -1)).split(' ')[0];
      if (p === curP) {
        const o = last.data?.day?.orders;
        return o != null ? { amount: Math.round(o * perOrder * 100) / 100, source: `${prevMonth} orders ${o.toLocaleString('en-US')} × ${money(perOrder)}` } : { amount: null, source: 'Counting last month’s orders…' };
      }
      if (p === pAdd(curP, 1)) {
        const o = mtd.data?.day?.orders;
        if (o != null) return { amount: Math.round(o * perOrder * 100) / 100, source: `${prevMonth} orders so far ${o.toLocaleString('en-US')} × ${money(perOrder)} · still growing` };
      }
    }
    // Any bill with an invoice-email search — the seeded ones and ones added on the page.
    let note = '';
    if (b.email_query) {
      const inv = data?.invoices?.[b.id];
      // An invoice that arrived after the last payment is the one still open.
      const isNew = !!inv && (!lastPaid || inv.receivedAt > paidAtIso);
      // A payment email (receipt / confirmation / scheduled) newer than the invoice
      // means it was already paid outside this page — say so rather than show it owed.
      const settled = !!(inv?.paid && inv.paid.receivedAt >= inv.receivedAt && (!lastPaid || inv.paid.receivedAt > paidAtIso));
      if (inv && settled) {
        const amt = inv.amount ?? inv.paid!.amount;
        return { amount: amt, source: `Payment email ${isoShort(inv.paid!.receivedAt)}${inv.paid!.amount != null ? ' for ' + money(inv.paid!.amount) : ''} (“${inv.paid!.subject.slice(0, 50)}”) — already paid? Tick it off`, dueIso: inv.dueDate || undefined };
      }
      if (inv && isNew && inv.amount != null) {
        return { amount: inv.amount, source: `Invoice${inv.number ? ' #' + inv.number : ''} · emailed ${isoShort(inv.receivedAt)}${inv.dueDate ? ' · due date from the invoice' : ''}`, dueIso: inv.dueDate || undefined };
      }
      note = inv && isNew ? `Invoice emailed ${isoShort(inv.receivedAt)}, amount is in the attachment — type it`
        : lastPaid ? 'waiting on the new invoice email' : 'no invoice email found yet';
    }
    const join = (a: string) => (note ? `${a} · ${note}` : a);
    if (b.kind === 'manual' && b.amount > 0) return { amount: b.amount, source: join(lastPaid ? `Last paid ${money(lastPaid.amount)} on ${isoShort(lastPaid.paid_at)}` : 'Saved amount') };
    if (lastPaid) return { amount: lastPaid.amount, source: join(`Last paid ${money(lastPaid.amount)} on ${isoShort(lastPaid.paid_at)}`) };
    return { amount: null, source: note ? note.charAt(0).toUpperCase() + note.slice(1) + (note.includes('type it') ? '' : ' — type the amount') : 'Type the amount' };
  };

  // Every open cycle per bill: unpaid cycles up to this month (last 12 at most), or
  // — once everything is paid — the next cycle, which lands in "coming up".
  const rows: BillRow[] = [];
  for (const b of active) {
    const paidFor = (data?.payments || []).filter((x) => x.bill_id === b.id);
    const paid = new Set(paidFor.map((x) => x.period));
    const cycles: string[] = [];
    for (let p = b.start_period, n = 0; p <= curP && n < 400; p = pAdd(p, b.every_months), n++) cycles.push(p);
    const open = cycles.slice(-12).filter((p) => !paid.has(p));
    const next = !open.length; // everything due so far is paid → show the next cycle
    if (next) {
      let p = b.start_period;
      while (p <= curP || paid.has(p)) p = pAdd(p, b.every_months);
      open.push(p);
    }
    for (const p of open) {
      // An open invoice that states its own due date beats the bill's usual due day.
      const { dueIso, ...amt } = amountFor(b, p, paidFor);
      const due = dueIso ? new Date(Number(dueIso.slice(0, 4)), Number(dueIso.slice(5, 7)) - 1, Number(dueIso.slice(8, 10))) : dueOf(b, p);
      rows.push({ bill: b, period: p, due, days: Math.round((due.getTime() - today.getTime()) / 86400000), ...amt, next });
    }
  }
  rows.sort((a, b) => a.due.getTime() - b.due.getTime() || a.bill.sort - b.bill.sort);
  // Bills already paid for this cycle sit in their own section under a divider, so
  // the top of the card is only ever what's still owed.
  const owed = rows.filter((r) => !r.next);
  const pastDue = owed.filter((r) => r.days < 0);
  const dueToday = owed.filter((r) => r.days === 0);
  const comingUp = owed.filter((r) => r.days > 0);
  const paidUp = rows.filter((r) => r.next);
  const keyOf = (r: BillRow) => `${r.bill.id}:${r.period}`;
  const valueOf = (r: BillRow): number | null => { const d = drafts[keyOf(r)]; return d != null && d !== '' ? (parseFloat(d) || 0) : r.amount; };
  const dueNow = [...pastDue, ...dueToday].reduce((s, r) => s + (valueOf(r) ?? 0), 0);
  const recentPaid = (data?.payments || []).filter((x) => Date.now() - Date.parse(x.paid_at.replace(' ', 'T') + 'Z') < 45 * 86400000);
  const billById = new Map((data?.bills || []).map((b) => [b.id, b]));

  // Typing an amount saves it: on a manual bill it becomes the bill's amount; on a
  // facility/email bill it overrides just this cycle.
  const saveDraft = async (r: BillRow) => {
    const d = drafts[keyOf(r)];
    if (d == null) return;
    const n = parseFloat(d);
    if (!Number.isFinite(n) || n === r.amount) { setDrafts((x) => { const y = { ...x }; delete y[keyOf(r)]; return y; }); return; }
    await bills.update(r.bill.id, { amount: n, amount_for: r.period });
    setDrafts((x) => { const y = { ...x }; delete y[keyOf(r)]; return y; });
  };
  const pay = async (r: BillRow, confirmed = false) => {
    const v = valueOf(r);
    if (v == null || v <= 0) return;
    // A bill below the "Already paid" line is a FUTURE cycle — ticking it pays next
    // month early, so it asks first (an accidental tick here used to slip through).
    if (r.next && !confirmed) { setPending({ kind: 'payAhead', bill: r.bill, period: r.period, amount: v, row: r }); return; }
    setBusy(keyOf(r));
    await bills.pay(r.bill.id, r.period, v);
    setDrafts((x) => { const y = { ...x }; delete y[keyOf(r)]; return y; });
    setBusy(null);
  };
  const runPending = async () => {
    if (!pending) return;
    if (pending.kind === 'unpay' && pending.period) await bills.unpay(pending.bill.id, pending.period);
    if (pending.kind === 'payAhead' && pending.row) await pay(pending.row, true);
    if (pending.kind === 'remove') { await bills.remove(pending.bill.id); setEditing(null); }
    setPending(null);
  };

  const groupHead = (label: string, color: string, n: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0 6px' }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: color }} />
      <span style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 11, color }}>{label}</span>
      <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>{n}</span>
    </div>
  );

  const row = (r: BillRow, tone: string) => {
    const k = keyOf(r);
    const v = valueOf(r);
    const canPay = v != null && v > 0 && busy !== k;
    const when = r.days < 0 ? `${-r.days} day${r.days === -1 ? '' : 's'} late` : r.days === 0 ? 'today' : r.days === 1 ? 'tomorrow' : `in ${r.days} days`;
    if (editing === r.bill.id && rows.find((x) => x.bill.id === r.bill.id) === r) return <BillEditor key={k} t={t} init={r.bill} onSave={(b: any) => bills.update(r.bill.id, b)} onCancel={() => setEditing(null)} onRemove={() => setPending({ kind: 'remove', bill: r.bill })} />;
    return (
      <div key={k} className="bb-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 10px', borderTop: '1px solid ' + t.border, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <button onClick={() => pay(r)} disabled={!canPay} title={canPay ? `Mark ${r.bill.name} (${pLabel(r.period)}) paid — ${money(v as number)}` : 'Enter the amount first'}
          style={{ width: 26, height: 26, flexShrink: 0, borderRadius: 99, border: '2px solid ' + (canPay ? tone : t.borderStrong), background: 'transparent', cursor: canPay ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: tone }}>
          {busy === k ? <span style={{ fontSize: 10 }}>…</span> : <Icon name="check" size={13} color={canPay ? tone : t.faint} strokeWidth={2.6} style={{ opacity: 0.35 }} />}
        </button>
        <div style={{ flex: 1, minWidth: isMobile ? 'calc(100% - 50px)' : 0 }}>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: '#e6dfff' }}>
            {r.bill.name} <span style={{ fontWeight: 500, fontSize: 12, color: t.faint }}>· {pLabel(r.period)}</span>
          </div>
          <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 3, lineHeight: 1.45 }}>
            <span style={{ color: r.days < 0 ? t.bad : r.days === 0 ? t.accent : t.fg2, fontWeight: 600 }}>Due {billDay(r.due)} · {when}</span>
            <span style={{ color: t.faint }}> · {r.source}</span>
          </div>
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: isMobile ? 40 : 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(13,10,30,0.6)', border: '1px solid rgba(150,130,230,0.2)', borderRadius: 10, padding: '7px 12px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 14, color: t.faint }}>
            $
            <input type="text" inputMode="decimal" aria-label={`Amount for ${r.bill.name}`}
              value={drafts[k] ?? (r.amount != null ? r.amount.toFixed(2) : '')} placeholder="0.00"
              onChange={(e) => { const x = e.target.value; if (/^\d*\.?\d{0,2}$/.test(x)) setDrafts((d) => ({ ...d, [k]: x })); }}
              onBlur={() => saveDraft(r)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              style={{ width: 86, border: 'none', background: 'transparent', color: t.fg, fontFamily: FIGTREE, fontWeight: 700, fontSize: 15, outline: 'none', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
          </span>
          <RowIcon icon="wrench" title="Edit bill — name, due day, how often it repeats" onClick={() => setEditing(r.bill.id)} />
        </span>
      </div>
    );
  };

  const smallBtn: any = { fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: t.muted, background: 'none', border: 'none', cursor: 'pointer', padding: '11px 10px', textAlign: 'left' };

  return (
    <Card pad={cardPad}>
      <ExpHead mb={2} dot={t.accent} title="Bills to Pay"
        chips={dueNow > 0 ? <ExpChip tone={t.bad}>{money(dueNow)} DUE NOW</ExpChip> : rows.length ? <ExpChip tone={t.good}>NOTHING DUE TODAY</ExpChip> : null}
        right={<>
          <AddPill onClick={() => setEditing(editing === 'new' ? null : 'new')}>+ Add bill</AddPill>
          <RowIcon icon="refresh" title="Re-check the invoice emails for new amounts" onClick={() => bills.refresh()} />
        </>} />
      <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, margin: '0 0 6px 22px' }}>
        Tick the circle once a bill is paid; it drops below the “Already paid” line, showing when it’s due next. Amounts are pre-filled where we can pull them; type over any of them.
      </div>

      {editing === 'new' && <BillEditor t={t} onSave={(b: any) => bills.create(b)} onCancel={() => setEditing(null)} />}

      {bills.err && !data ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0' }}>
          <Icon name="alert" size={15} color={t.warn} />
          <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>Couldn’t load your bills.</span>
          <button onClick={() => bills.refresh()} style={{ ...smallBtn, padding: '6px 12px', border: '1px solid ' + t.border, borderRadius: 999 }}>Retry</button>
        </div>
      ) : !data ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '14px 0' }}>Loading bills…</div>
      ) : (
        <div>
          {pastDue.length > 0 && <>{groupHead('Past due', t.bad, pastDue.length)}{pastDue.map((r) => row(r, t.bad))}</>}
          {dueToday.length > 0 && <>{groupHead('Due today', t.accent, dueToday.length)}{dueToday.map((r) => row(r, t.accent))}</>}
          {pastDue.length + dueToday.length === 0 && owed.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0 4px', fontFamily: FIGTREE, fontSize: 13, color: t.good }}>
              <Icon name="check" size={14} color={t.good} strokeWidth={2.4} /> All caught up — nothing past due or due today.
            </div>
          )}
          {comingUp.length > 0 && <>{groupHead('Coming up · not paid yet', t.fg2, comingUp.length)}{comingUp.map((r) => row(r, t.fg2))}</>}
          {owed.length === 0 && paidUp.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 0 4px', fontFamily: FIGTREE, fontSize: 13, color: t.good }}>
              <Icon name="check" size={14} color={t.good} strokeWidth={2.4} /> Everything is paid. Next bills below.
            </div>
          )}
          {paidUp.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0 4px' }}>
                <span style={{ flex: 1, height: 2, background: 'rgba(110,220,160,0.35)', borderRadius: 2 }} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', fontSize: 11, color: t.good, whiteSpace: 'nowrap' }}>
                  <Icon name="check" size={13} color={t.good} strokeWidth={2.6} /> Already paid · next bill shown
                </span>
                <span style={{ flex: 1, height: 2, background: 'rgba(110,220,160,0.35)', borderRadius: 2 }} />
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.faint, textAlign: 'center', padding: '0 0 6px' }}>
                Nothing to pay here right now. These are paid up, and each row is its next due date.
              </div>
              <div style={{ opacity: 0.62 }}>{paidUp.map((r) => row(r, t.muted))}</div>
            </div>
          )}
          {rows.length === 0 && editing !== 'new' && (
            <div style={{ textAlign: 'center', padding: '22px 0', fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No repeating bills. Add one with + Add bill.</div>
          )}

          {recentPaid.length > 0 && (
            <div style={{ borderTop: '1px solid ' + t.border, marginTop: 8 }}>
              <button onClick={() => setShowPaid((v) => !v)} style={smallBtn}>{showPaid ? '▾' : '▸'} Paid in the last 45 days · {recentPaid.length}</button>
              {showPaid && recentPaid.map((x) => {
                const b = billById.get(x.bill_id);
                return (
                  <div key={x.bill_id + x.period} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderTop: '1px solid ' + t.border }}>
                    <Icon name="check" size={14} color={t.good} strokeWidth={2.4} />
                    <div style={{ flex: 1, minWidth: 0, fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>
                      {b?.name || 'Removed bill'} <span style={{ color: t.faint }}>· {pLabel(x.period)} · paid {isoShort(x.paid_at)}</span>
                    </div>
                    <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg2, fontVariantNumeric: 'tabular-nums' }}>{money(x.amount)}</span>
                    {b && <button onClick={() => setPending({ kind: 'unpay', bill: b, period: x.period, amount: x.amount })} title="Mark this as not paid" style={{ ...smallBtn, padding: '2px 4px', color: t.faint }}>Undo</button>}
                  </div>
                );
              })}
            </div>
          )}

          {stopped.length > 0 && (
            <div style={{ borderTop: '1px solid ' + t.border }}>
              <button onClick={() => setShowStopped((v) => !v)} style={smallBtn}>{showStopped ? '▾' : '▸'} Not repeating (paid off) · {stopped.length}</button>
              {showStopped && stopped.map((b) => editing === b.id ? (
                <BillEditor key={b.id} t={t} init={b} onSave={(x: any) => bills.update(b.id, x)} onCancel={() => setEditing(null)} onRemove={() => setPending({ kind: 'remove', bill: b })} />
              ) : (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 10px', borderTop: '1px solid ' + t.border }}>
                  <div style={{ flex: 1, minWidth: 0, fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>{b.name}</div>
                  <button onClick={() => setEditing(b.id)} title="Edit — set it back to monthly (or another term) to put it on the list again" style={{ ...smallBtn, padding: '2px 4px', color: t.accent }}>Turn back on</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title={pending.kind === 'unpay' ? 'Mark as not paid?' : pending.kind === 'payAhead' ? `Pay ${pLabel(pending.period as string).split(' ')[0]} early?` : 'Remove this bill?'}
          confirmLabel={pending.kind === 'unpay' ? 'Mark not paid' : pending.kind === 'payAhead' ? 'Yes, paid early' : 'Remove'}
          tone={pending.kind === 'remove' ? 'danger' : 'accent'}
          body={pending.kind === 'payAhead'
            ? <><b style={{ color: t.fg2 }}>{pending.bill.name}</b> is already paid for this cycle. This records a {money(pending.amount || 0)} payment for <b style={{ color: t.fg2 }}>{pLabel(pending.period as string)}</b> too, and pushes its next due date out another cycle. Only do this if you’ve actually paid next month’s bill. (You can undo it under “Paid in the last 45 days”.)</>
            : pending.kind === 'unpay'
            ? <>Move <b style={{ color: t.fg2 }}>{pending.bill.name} · {pLabel(pending.period as string)}</b> back onto the list as unpaid. The {money(pending.amount || 0)} payment record is deleted. You can tick it again any time.</>
            : <>Take <b style={{ color: t.fg2 }}>{pending.bill.name}</b> off your bills completely. Its past payments stay on record, but it won’t show anywhere on this page again. If it’s just paid off, choose <b style={{ color: t.fg2 }}>Don’t repeat</b> instead, so you can turn it back on later.</>}
          onCancel={() => setPending(null)}
          onConfirm={runPending}
        />
      )}
    </Card>
  );
}

// Add / edit a bill: name, due day, how often it repeats (or "don't repeat" once
// paid off), and an optional Gmail search for pulling the amount from its invoice.
function BillEditor({ t, init, onSave, onCancel, onRemove }: any) {
  const [name, setName] = React.useState(init?.name ?? '');
  const [dueDay, setDueDay] = React.useState<string>(String(init?.due_day ?? 1));
  const [every, setEvery] = React.useState<number>(init?.every_months ?? 1);
  const [amount, setAmount] = React.useState<string>(init ? '' : '');
  const [query, setQuery] = React.useState<string>(init?.email_query ?? '');
  const [busy, setBusy] = React.useState(false);
  const inp: any = { fontFamily: FIGTREE, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(150,130,230,0.25)', background: 'rgba(13,10,30,0.7)', color: t.fg, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const day = parseInt(dueDay, 10);
  const canSave = name.trim().length > 0 && day >= 1 && day <= 31 && !busy;
  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    const body: any = { name: name.trim(), due_day: day, every_months: every };
    if (!init && amount) body.amount = parseFloat(amount) || 0;
    if (!init || init.kind !== 'facility') body.email_query = query.trim() || null;
    const ok = await onSave(body);
    setBusy(false);
    if (ok) onCancel();
  };
  const lbl: any = { fontFamily: FIGTREE, fontSize: 11, color: t.muted, marginBottom: 4, display: 'block' };
  return (
    <div style={{ padding: 12, background: 'rgba(248,146,92,0.06)', borderTop: '1px solid ' + t.border, display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <label style={{ gridColumn: 'span 2', minWidth: 0 }}><span style={lbl}>Bill</span>
          <input autoFocus placeholder="e.g. Chase card" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }} style={inp} /></label>
        <label><span style={lbl}>Due on day</span>
          <input inputMode="numeric" value={dueDay} onChange={(e) => { if (/^\d{0,2}$/.test(e.target.value)) setDueDay(e.target.value); }} style={inp} /></label>
        <label><span style={lbl}>Repeats</span>
          <select value={every} onChange={(e) => setEvery(Number(e.target.value))} style={inp}>
            {REPEAT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select></label>
        {!init && <label><span style={lbl}>Usual amount</span>
          <input inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => { if (/^\d*\.?\d{0,2}$/.test(e.target.value)) setAmount(e.target.value); }} style={inp} /></label>}
      </div>
      {(!init || init.kind !== 'facility') && (
        <label><span style={lbl}>Invoice email search (optional) — a Gmail search; the newest match fills in the amount</span>
          <input placeholder='e.g. from:billing@vendor.com "invoice"' value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inp, fontFamily: MONO, fontSize: 12 }} /></label>
      )}
      {every === 0 && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>Paid off: it leaves the list and waits under “Not repeating”. Set it back to Monthly (or another term) and it returns, starting this month.</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={save} disabled={!canSave} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: '8px 15px', borderRadius: 999, border: 'none', background: t.accent, color: '#1a1330', cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.5 }}>{busy ? 'Saving…' : init ? 'Save' : 'Add bill'}</button>
        <button onClick={onCancel} style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, padding: '8px 12px', borderRadius: 999, border: '1px solid ' + t.borderStrong, background: 'transparent', color: t.fg2, cursor: 'pointer' }}>Cancel</button>
        {init && onRemove && <button onClick={onRemove} style={{ marginLeft: 'auto', fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, padding: '8px 12px', borderRadius: 999, border: '1px solid rgba(255,139,125,0.4)', background: 'transparent', color: t.bad, cursor: 'pointer' }}>Remove bill</button>}
      </div>
    </div>
  );
}

export function ExpensesView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [cfg, setCfg] = React.useState<OverheadCfg | null>(null);
  const [draft, setDraft] = React.useState<OverheadCfg | null>(null);
  const [loadErr, setLoadErr] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await fetch('/api/config/overhead');
      if (!r.ok) throw new Error('load');
      const j = (await r.json()) as OverheadCfg;
      setCfg(j); setDraft(j); setLoadErr(false);
    } catch { setLoadErr(true); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const set = (k: keyof OverheadCfg) => (v: number) => { setDraft((p) => (p ? { ...p, [k]: v } : p)); setSaved(false); };
  const dirty = !!(cfg && draft && (['facilityRentPerOrder', 'bringFixedMonthly', 'payrollMonthlyTarget'] as const).some((k) => Math.round(draft[k] * 100) !== Math.round(cfg[k] * 100)));

  const save = async () => {
    if (!draft || !dirty) return;
    setSaving(true);
    try {
      const r = await fetch('/api/config/overhead', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(draft) });
      if (r.ok) { const j = (await r.json()) as OverheadCfg; setCfg(j); setDraft(j); setSaved(true); }
    } catch { /* keep the draft so the owner can retry */ }
    setSaving(false);
  };
  const reset = () => { if (cfg) { setDraft(cfg); setSaved(false); } };

  const saveBtn: any = { fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '11px 22px', borderRadius: 999, border: 'none', cursor: saving ? 'default' : 'pointer', background: t.accent, color: '#1a1330', opacity: saving ? 0.6 : 1 };
  const linkBtn: any = { fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, padding: '10px 16px', borderRadius: 999, border: '1px solid ' + t.borderStrong, background: 'transparent', color: t.fg2, cursor: 'pointer' };
  const cardPad = isMobile ? '20px 18px' : '22px 26px';

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 1040 }}>
      <div style={{ marginBottom: 6 }}>
        <Title size={isMobile ? 24 : 32}>Expenses</Title>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, maxWidth: 720, lineHeight: 1.5 }}>
          Every recurring cost, editable in one place — the source of truth Profit-First and Per-Unit read from.
        </div>
      </div>

      <BillsToPay cardPad={cardPad} rate={cfg?.facilityRentPerOrder} />

      <FacilityDue cardPad={cardPad} rate={cfg?.facilityRentPerOrder} />

      {/* Phase 1 — the three editable rates/targets, relocated here */}
      <Card pad={cardPad}>
        <ExpHead mb={2} dot={t.accent} title="Rates & Targets" chips={<>
          <ExpChip tone={t.warn}>OWNER-ONLY</ExpChip>
          <ExpChip tone={t.good}>AUDITED</ExpChip>
        </>} />
        <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, margin: '0 0 6px 22px' }}>Moved here from Profit-First &amp; Per-Unit. Changes apply live across the app.</div>

        {loadErr ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0' }}>
            <Icon name="alert" size={15} color={t.warn} />
            <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>Couldn’t load the current rates.</span>
            <button onClick={load} style={linkBtn}>Retry</button>
          </div>
        ) : !draft ? (
          <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, padding: '16px 0' }}>Loading current rates…</div>
        ) : (
          <>
            <div style={{ marginTop: 8 }}>
              <RateRow t={t} name="Facility rent" desc="Per order — the rent you pay modeled as a per-order cost. Feeds the Profit-First Facility bucket and the Per-Unit facility line." value={draft.facilityRentPerOrder} step={0.25} prefix="$" onChange={set('facilityRentPerOrder')} />
              <RateRow t={t} name="Bring fixed fees" desc="Per month — Management + API + Storage from the rate card (can’t be split out of Bring’s lump bill). Pro-rated into overhead." value={draft.bringFixedMonthly} step={5} prefix="$" onChange={set('bringFixedMonthly')} />
              <RateRow t={t} last name="Payroll target" desc="Per month — owners’ salaries + payroll taxes, all-in. The goal the Owners Pay coverage monitor tracks against." value={draft.payrollMonthlyTarget} step={100} prefix="$" onChange={set('payrollMonthlyTarget')} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + t.border }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={save} disabled={!dirty || saving} style={{ ...saveBtn, opacity: !dirty || saving ? 0.5 : 1, cursor: !dirty || saving ? 'default' : 'pointer' }}>{saving ? 'Saving…' : 'Save changes'}</button>
                {dirty && <button onClick={reset} style={linkBtn}>Discard</button>}
              </div>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: FIGTREE, fontSize: 12, color: dirty ? t.warn : t.good }}>
                {!dirty && <Icon name="check" size={14} color={t.good} strokeWidth={2.4} />}
                {dirty ? 'Unsaved changes' : saved ? 'Saved ✓' : 'All changes saved'}
              </span>
            </div>
          </>
        )}
      </Card>

      {/* Editable cost catalog — software / packaging / other (Phase 2a) */}
      <ExpenseCatalog cardPad={cardPad} />

      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, lineHeight: 1.7, marginTop: 4, padding: '0 2px' }}>
        <b style={{ color: t.muted }}>Live now:</b> per-product ingredient breakdown with a per-jar formulation (amount + unit) on each ingredient, plus editable/archivable software, packaging &amp; other lines — every amount change stamps a price history that drives the up/down/flat trend. <b style={{ color: t.muted }}>Coming next:</b> invoice upload — convert a bulk order into a per-jar cost and flag if you’re over/under paying. This tab stays standalone (it doesn’t feed the P&amp;L) until you decide it’s validated.
      </div>
    </div>
  );
}

// ---- Expense catalog (Phase 2a: software / packaging / other) ---------------
type ExpLine = { id: string; category: string; label: string; vendor: string | null; amount: number; unit: string; product_sku: string | null; qty_per_jar: number | null; uom: string | null; loss_pct: number; order_qty: number | null; order_total: number | null; notes: string | null; status: 'active' | 'archived'; trend: 'up' | 'down' | 'flat'; trendPct: number | null; monthly: number | null };

const UOM_OPTS = ['mg', 'g', 'mL', 'capsule', 'each'];
// Compact formulation label, e.g. "500 mg/jar" or "2 capsule/jar".
const fmtFormula = (l: ExpLine): string | null => (l.qty_per_jar != null && l.uom ? `${l.qty_per_jar} ${l.uom}/jar` : null);

// Bulk purchase price per lb, back-derived from the per-jar cost + formulation
// (weight uoms only). amount = ($/lb ÷ 453.592) × grams/jar → $/lb = amount ÷ grams × 453.592.
// A quick "what did we pay" reference to sanity-check against a live buy.
const perLbCost = (l: ExpLine): number | null => {
  if (!l.amount || !l.qty_per_jar) return null;
  const gramsPerJar = l.uom === 'g' ? l.qty_per_jar : l.uom === 'mg' ? l.qty_per_jar / 1000 : null;
  if (!gramsPerJar) return null;
  return Math.round((l.amount / gramsPerJar) * 453.592 * 100) / 100;
};

// Packaging purchase-unit descriptor (what one "amount" buys), inferred from the label —
// so a bulk roll/bag reads as such rather than a misleading "each".
const pkgUnitLabel = (label: string): string => {
  const n = label.toLowerCase();
  if (n.includes('box') || n.includes('mailer')) return '/box';
  if (n.includes('bubble') || n.includes('wrap') || n.includes('roll')) return '/roll';
  if (n.includes('peanut') || n.includes('bag')) return '/bag';
  return '/piece';
};

const UNIT_LABEL: Record<string, string> = { per_month: '/mo', annual: '/yr', per_order: '/order', per_unit: 'each', per_piece: '/piece', one_time: 'one-time' };
const UNIT_OPTS: { v: string; l: string }[] = [
  { v: 'per_month', l: 'per month' }, { v: 'annual', l: 'per year' }, { v: 'per_order', l: 'per order' },
  { v: 'per_unit', l: 'per unit' }, { v: 'per_piece', l: 'per piece' }, { v: 'one_time', l: 'one-time' },
];
const EXP_CATS: { key: string; name: string; dot: string; unit: string; hint: string }[] = [
  { key: 'software', name: 'Software & subscriptions', dot: '#7b6cf0', unit: 'per_month', hint: 'each app / tool as its own line, normalized to monthly' },
  { key: 'packaging', name: 'Packaging', dot: '#c9a648', unit: 'per_unit', hint: 'jars, lids, labels, boxes, mailers, filler' },
  { key: 'other', name: 'Other recurring costs', dot: '#a879e6', unit: 'per_month', hint: 'anything else you pay for regularly' },
];

// Fetch + mutate the expense catalog. Cached (instant on revisit); every write
// returns the fresh list and updates the shared cache in place.
type ExpData = { lines: ExpLine[]; products: { sku: string; name: string }[] };
function useExpenses() {
  const res = useCachedResource<ExpData>('expenses', async () => {
    const r = await fetch('/api/expenses');
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  });
  const mutate = async (method: string, url: string, body?: any) => {
    try {
      const r = await fetch(url, { method, headers: body ? { 'content-type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
      if (!r.ok) return false;
      const j = await r.json().catch(() => ({}));
      // Writes return the fresh lines; keep the products list already cached.
      if (j.lines) res.set({ lines: j.lines, products: res.data?.products || [] });
      return true;
    } catch { return false; }
  };
  return {
    lines: res.data?.lines ?? null, products: res.data?.products ?? [], err: res.error, reload: res.refresh,
    create: (b: any) => mutate('POST', '/api/expenses', b),
    update: (id: string, b: any) => mutate('PATCH', `/api/expenses/${id}`, b),
    archive: (id: string, archived: boolean) => mutate('POST', `/api/expenses/${id}/archive`, { archived }),
    remove: (id: string) => mutate('DELETE', `/api/expenses/${id}`),
  };
}

// Color-coded trend pill — purely the visual treatment of the trend/trendPct the
// price history already computes. Up = coral (price rose), down = green, flat =
// green "NO CHANGE", never-priced = neutral "NEW".
function TrendPill({ t, line }: { t: any; line: ExpLine }) {
  const base: any = { display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: OSWALD, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', padding: '2px 9px', borderRadius: 999, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  if (line.trend === 'up') return <span style={{ ...base, color: t.bad, background: 'rgba(255,139,125,0.13)' }}>▲ {line.trendPct != null ? line.trendPct + '%' : ''}</span>;
  if (line.trend === 'down') return <span style={{ ...base, color: '#7fcf9a', background: 'rgba(87,217,138,0.1)' }}>▼ {line.trendPct != null ? Math.abs(line.trendPct) + '%' : ''}</span>;
  if (line.trendPct == null) return <span style={{ ...base, letterSpacing: '0.06em', color: '#8f85b5', background: 'rgba(150,130,230,0.1)' }}>NEW</span>;
  return <span style={{ ...base, letterSpacing: '0.06em', color: '#7fcf9a', background: 'rgba(87,217,138,0.1)' }}>NO CHANGE</span>;
}

// Inline add/edit form for one line. `init` seeds the fields (edit) or defaults (add).
// For ingredient lines pass `productSku` (the product it rolls up into) + `fixedUnit`
// (hides the unit selector — ingredient cost is always per jar).
function ExpenseEditorRow({ t, cat, init, productSku, fixedUnit, namePlaceholder, formulation, onSave, onCancel }: any) {
  const [label, setLabel] = React.useState(init?.label ?? '');
  const [vendor, setVendor] = React.useState(init?.vendor ?? '');
  const [amount, setAmount] = React.useState<number>(init?.amount ?? 0);
  const [unit, setUnit] = React.useState<string>(init?.unit ?? fixedUnit ?? cat.unit);
  const [qtyPerJar, setQtyPerJar] = React.useState<number>(init?.qty_per_jar ?? 0);
  const [uom, setUom] = React.useState<string>(init?.uom ?? (cat.key === 'ingredient' ? 'mg' : 'each'));
  const [busy, setBusy] = React.useState(false);
  const inp: any = { fontFamily: FIGTREE, fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(150,130,230,0.25)', background: 'rgba(13,10,30,0.7)', color: t.fg, outline: 'none', width: '100%' };
  const canSave = label.trim().length > 0 && !busy;
  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    const body: any = { category: cat.key, label: label.trim(), vendor: vendor.trim() || null, amount, unit: fixedUnit || unit };
    if (productSku) body.product_sku = productSku;
    if (formulation) { body.qty_per_jar = qtyPerJar > 0 ? qtyPerJar : null; body.uom = uom; }
    const ok = await onSave(body);
    setBusy(false);
    if (ok) onCancel();
  };
  return (
    <div style={{ padding: '12px', background: 'rgba(248,146,92,0.06)', borderBottom: '1px solid ' + t.border, display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: fixedUnit ? 'minmax(140px,2fr) minmax(110px,1.3fr) auto' : 'minmax(140px,2fr) minmax(120px,1.4fr) auto auto', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input autoFocus placeholder={namePlaceholder || 'Name (e.g. Klaviyo)'} value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }} style={inp} />
        <input placeholder="Vendor (optional)" value={vendor} onChange={(e) => setVendor(e.target.value)} style={inp} />
        <NumInput value={amount} onChange={setAmount} step={amount < 5 ? 0.05 : 1} prefix="$" width={70} />
        {!fixedUnit && (
          <select value={unit} onChange={(e) => setUnit(e.target.value)} style={{ ...inp, width: 'auto' }}>
            {UNIT_OPTS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
          </select>
        )}
      </div>
      {formulation && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>Per jar:</span>
          <NumInput value={qtyPerJar} onChange={setQtyPerJar} step={qtyPerJar < 10 ? 1 : 25} width={64} />
          <select value={uom} onChange={(e) => setUom(e.target.value)} style={{ ...inp, width: 'auto' }}>
            {UOM_OPTS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>the formulation amount that goes in each jar — used to reconcile invoices</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={!canSave} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: '8px 15px', borderRadius: 999, border: 'none', background: t.accent, color: '#1a1330', cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.5 }}>{busy ? 'Saving…' : (init ? 'Save' : 'Add')}</button>
        <button onClick={onCancel} style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, padding: '8px 12px', borderRadius: 999, border: '1px solid ' + t.borderStrong, background: 'transparent', color: t.fg2, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
}

// Quick-add for an ingredient, scoped to one product group (redesign handoff):
// Name · Supplier · g/jar · $/lb, with the per-jar cost derived from the two
// numbers — (g ÷ 453.592) × $/lb — and posted through the same create mutation.
function IngredientQuickAdd({ t, productSku, onSave, onCancel }: any) {
  const [name, setName] = React.useState('');
  const [supplier, setSupplier] = React.useState('');
  const [grams, setGrams] = React.useState('');
  const [perLb, setPerLb] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const inp: any = { background: 'rgba(13,10,30,0.7)', border: '1px solid rgba(150,130,230,0.25)', borderRadius: 8, padding: '8px 10px', color: t.fg, fontFamily: FIGTREE, fontSize: 13, outline: 'none' };
  const g = parseFloat(grams) || 0;
  const lb = parseFloat(perLb) || 0;
  const perJar = Math.round(((g / 453.592) * lb) * 10000) / 10000;
  const canSave = name.trim().length > 0 && !busy;
  const add = async () => {
    if (!canSave) return;
    setBusy(true);
    const ok = await onSave({
      category: 'ingredient', label: name.trim(), vendor: supplier.trim() || null,
      amount: perJar, unit: 'per_unit', product_sku: productSku,
      qty_per_jar: g > 0 ? g : null, uom: 'g',
    });
    setBusy(false);
    if (ok) onCancel();
  };
  const key = (e: any) => { if (e.key === 'Enter') add(); if (e.key === 'Escape') onCancel(); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: 12, borderBottom: '1px solid ' + t.border, background: 'rgba(248,146,92,0.06)' }}>
      <input autoFocus placeholder="Ingredient name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={key} style={{ ...inp, flex: 1, minWidth: 130 }} />
      <input placeholder="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} onKeyDown={key} style={{ ...inp, width: 98 }} />
      <input placeholder="g/jar" inputMode="decimal" value={grams} onChange={(e) => setGrams(e.target.value)} onKeyDown={key} style={{ ...inp, width: 66, textAlign: 'right' }} />
      <input placeholder="$/lb" inputMode="decimal" value={perLb} onChange={(e) => setPerLb(e.target.value)} onKeyDown={key} style={{ ...inp, width: 66, textAlign: 'right' }} />
      <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, fontVariantNumeric: 'tabular-nums', minWidth: 74 }}>{perJar > 0 ? `= ${money(perJar)}/jar` : ''}</span>
      <button onClick={add} disabled={!canSave} style={{ background: t.accent, border: 0, color: '#1a1330', fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: '8px 15px', borderRadius: 999, cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.5 }}>{busy ? 'Adding…' : 'Add'}</button>
      <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid ' + t.borderStrong, color: t.fg2, fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, padding: '8px 12px', borderRadius: 999, cursor: 'pointer' }}>Cancel</button>
    </div>
  );
}

// Per-product ingredient breakdown (Phase 2b): each base product's raw-ingredient
// lines sum to its material COGS per jar. Ingredient lines are category 'ingredient'
// + product_sku; unit is fixed to per-jar.
const INGR_CAT = { key: 'ingredient', unit: 'per_unit' };
function IngredientSection({ t, exp, editing, setEditing, askDelete, cardPad }: any) {
  const isMobile = useIsMobile();
  const products: { sku: string; name: string }[] = exp.products || [];
  const unitCap: any = { color: t.faint, fontSize: 11, fontWeight: 600 };
  return (
    <Card pad={cardPad}>
      <ExpHead dot={t.good} title="Ingredients & COGS" hint="each product’s raw ingredients → its material cost per jar"
        right={products.length ? <ExpChip tone={t.purple}>{products.length} PRODUCT{products.length === 1 ? '' : 'S'}</ExpChip> : null} />
      {exp.lines == null ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading…</div>
      ) : products.length === 0 ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No base products found.</div>
      ) : (
        products.map((p, pi) => {
          const ing = (exp.lines as ExpLine[]).filter((l) => l.category === 'ingredient' && l.product_sku === p.sku && l.status === 'active');
          const cogs = ing.reduce((s, l) => s + (l.amount || 0), 0);
          const ordered = ing.find((l) => l.order_qty != null)?.order_qty ?? null;
          const addKey = `new:ingredient:${p.sku}`;
          return (
            <div key={p.sku}>
              {/* product header — name · jars ordered · material/landed COGS · add */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: 'rgba(20,15,42,0.4)', border: '1px solid rgba(150,130,230,0.1)', borderRadius: 12, padding: '11px 15px', margin: pi ? '22px 0 2px' : '0 0 2px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14.5, color: t.fg }}>{p.name}</span>
                  {ordered != null && <ExpChip tone={t.purple}>{ordered.toLocaleString()} JARS ORDERED</ExpChip>}
                  <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{ordered != null ? 'landed COGS' : 'material COGS'}</span>
                  <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, color: cogs > 0 ? t.accent : t.faint, fontVariantNumeric: 'tabular-nums' }}>{money(cogs)}<span style={unitCap}>/jar</span></span>
                </div>
                <AddPill onClick={() => setEditing(editing === addKey ? null : addKey)}>+ Ingredient</AddPill>
              </div>

              {/* inline add sits at the top of the group it belongs to */}
              {editing === addKey && (
                <IngredientQuickAdd t={t} productSku={p.sku} onSave={(b: any) => exp.create(b)} onCancel={() => setEditing(null)} />
              )}

              {ing.map((l) => editing === l.id ? (
                <ExpenseEditorRow key={l.id} t={t} cat={INGR_CAT} init={l} productSku={p.sku} fixedUnit="per_unit" formulation namePlaceholder="Ingredient (e.g. Zeolite)" onSave={(b: any) => exp.update(l.id, b)} onCancel={() => setEditing(null)} />
              ) : (
                <div key={l.id} className="bb-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderBottom: '1px solid ' + t.border, flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                  <div style={{ flex: isMobile ? '1 1 100%' : 1, minWidth: isMobile ? 0 : 108 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: '#e6dfff' }}>{l.label}</span>
                      {l.vendor && <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>{l.vendor}</span>}
                    </div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: (fmtFormula(l) || l.notes) ? t.muted : t.faint, marginTop: 2 }}>{fmtFormula(l) || l.notes || 'no formulation set'}</div>
                  </div>
                  <span style={{ width: 80, flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}><TrendPill t={t} line={l} /></span>
                  <span style={{ width: 88, flexShrink: 0, textAlign: 'right', fontFamily: FIGTREE, fontSize: 13, color: t.muted, fontVariantNumeric: 'tabular-nums' }}>
                    {l.order_qty != null
                      ? (l.order_total != null
                          ? <>{money(l.order_total)} <span style={unitCap}>total</span></>
                          : <span style={{ color: t.faint }}>TBD</span>)
                      : (perLbCost(l) != null
                          ? <>{money(perLbCost(l)!)} <span style={unitCap}>/lb</span></>
                          : <span style={{ color: t.faint }}>—</span>)}
                  </span>
                  <span style={{ width: 78, flexShrink: 0, textAlign: 'right', fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>
                    {money(l.amount)} <span style={unitCap}>/jar</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <RowIcon icon="doc" title="Edit line" onClick={() => setEditing(l.id)} />
                    <RowIcon icon="x" danger title="Remove line" onClick={() => askDelete(l, p.name)} />
                  </span>
                </div>
              ))}
              {ing.length === 0 && editing !== addKey && (
                <div style={{ padding: '12px', fontFamily: FIGTREE, fontSize: 12.5, color: t.faint }}>No ingredients yet.</div>
              )}
            </div>
          );
        })
      )}
    </Card>
  );
}

function ExpenseCatalog({ cardPad }: any): any {
  const t = useT();
  const isMobile = useIsMobile();
  const exp = useExpenses();
  const [editing, setEditing] = React.useState<string | null>(null); // line id, `new:<cat>`, or null
  const [showArchived, setShowArchived] = React.useState(false);
  // Every data-changing row action (delete / archive / restore) parks here first
  // and is executed only from the confirm dialog — nothing fires on a bare click.
  type PendingAct = { kind: 'delete' | 'archive' | 'restore'; id: string; label: string; where?: string; permanent?: boolean };
  const [pending, setPending] = React.useState<PendingAct | null>(null);
  const askDelete = (l: ExpLine, where?: string) => setPending({ kind: 'delete', id: l.id, label: l.label, where });
  const askArchive = (l: ExpLine, where?: string) => setPending({ kind: 'archive', id: l.id, label: l.label, where });
  const askRestore = (l: ExpLine) => setPending({ kind: 'restore', id: l.id, label: l.label });
  const runPending = () => {
    if (!pending) return;
    if (pending.kind === 'delete') exp.remove(pending.id);
    else exp.archive(pending.id, pending.kind === 'archive');
    setPending(null);
  };
  // Dialog copy — says plainly what the action does to the numbers.
  const CONFIRM_COPY: Record<string, { title: string; label: string; tone: string; body: (p: PendingAct) => any }> = {
    delete: {
      title: 'Delete this line?', label: 'Delete', tone: 'danger',
      body: (p) => <>Remove <b style={{ color: t.fg2 }}>{p.label}</b> {p.permanent ? 'permanently' : `from ${p.where || 'Expenses'}`}? This deletes the line <b style={{ color: t.fg2 }}>and its price history</b>, drops it out of the monthly total, and can’t be undone.</>,
    },
    archive: {
      title: 'Archive this line?', label: 'Archive', tone: 'accent',
      body: (p) => <>Move <b style={{ color: t.fg2 }}>{p.label}</b> out of {p.where || 'this block'}. It stops counting toward the monthly total and hides under “archived”. Its price history is kept and you can restore it any time — but the total will change now.</>,
    },
    restore: {
      title: 'Restore this line?', label: 'Restore', tone: 'accent',
      body: (p) => <>Put <b style={{ color: t.fg2 }}>{p.label}</b> back on the active list. It starts counting toward the monthly total again.</>,
    },
  };

  const unitCap: any = { color: t.faint, fontSize: 11, fontWeight: 600 };
  const amountCell = (l: ExpLine, w: number) => (
    <div style={{ width: w, flexShrink: 0, textAlign: 'right' }}>
      <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, fontVariantNumeric: 'tabular-nums' }}>
        {money(l.amount)} <span style={unitCap}>{l.category === 'packaging' ? pkgUnitLabel(l.label) : (UNIT_LABEL[l.unit] || '')}</span>
      </div>
      {l.monthly != null && l.unit !== 'per_month' && <div style={{ fontFamily: FIGTREE, fontSize: 10.5, color: t.faint, marginTop: 2 }}>= {money(l.monthly)}/mo</div>}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {exp.err && (
        <Card pad={16}><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="alert" size={15} color={t.warn} /><span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>Couldn’t load the expense lines.</span><button onClick={exp.reload} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: '6px 12px', borderRadius: 999, border: '1px solid ' + t.border, background: 'transparent', color: t.muted, cursor: 'pointer' }}>Retry</button></div></Card>
      )}

      {/* Per-product ingredient breakdown (Phase 2b) */}
      <IngredientSection t={t} exp={exp} editing={editing} setEditing={setEditing} askDelete={askDelete} cardPad={cardPad} />

      {EXP_CATS.map((cat) => {
        const all = (exp.lines || []).filter((l) => l.category === cat.key);
        const active = all.filter((l) => l.status === 'active');
        const archived = all.filter((l) => l.status === 'archived');
        const monthlyTotal = active.reduce((s, l) => s + (l.monthly ?? 0), 0);
        const amtW = cat.key === 'packaging' ? 106 : 112;
        return (
          <Card key={cat.key} pad={cardPad}>
            <ExpHead dot={cat.dot} title={cat.name} hint={cat.hint} right={<>
              {active.length > 0 && <ExpChip>{active.length} LINE{active.length === 1 ? '' : 'S'}</ExpChip>}
              {monthlyTotal > 0 && <span style={{ fontFamily: FIGTREE, fontWeight: 800, fontSize: 14, color: t.accent, fontVariantNumeric: 'tabular-nums' }}>{money(monthlyTotal, 0)}<span style={{ fontSize: 11, color: t.muted, fontWeight: 600 }}>/mo</span></span>}
              <AddPill onClick={() => setEditing(editing === `new:${cat.key}` ? null : `new:${cat.key}`)}>+ Add line</AddPill>
            </>} />

            {exp.lines == null ? (
              <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>Loading…</div>
            ) : active.length === 0 && archived.length === 0 && editing !== `new:${cat.key}` ? (
              <div style={{ textAlign: 'center', padding: '26px 0', fontFamily: FIGTREE, fontSize: 13, color: t.muted }}>No lines yet — <button onClick={() => setEditing(`new:${cat.key}`)} style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, background: 'none', border: 'none', color: t.accent, cursor: 'pointer', padding: 0 }}>add your first {cat.key} line</button>.</div>
            ) : (
              <div>
                {editing === `new:${cat.key}` && (
                  <ExpenseEditorRow t={t} cat={cat} onSave={(b: any) => exp.create(b)} onCancel={() => setEditing(null)} />
                )}
                {active.map((l) => editing === l.id ? (
                  <ExpenseEditorRow key={l.id} t={t} cat={cat} init={l} onSave={(b: any) => exp.update(l.id, b)} onCancel={() => setEditing(null)} />
                ) : (
                  <div key={l.id} className="bb-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 12px', borderBottom: '1px solid ' + t.border, flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                    <div style={{ flex: isMobile ? '1 1 100%' : 1, minWidth: isMobile ? 0 : 120 }}>
                      <div style={{ fontFamily: FIGTREE, fontWeight: 600, fontSize: 13.5, color: '#e6dfff' }}>{l.label}</div>
                      {l.vendor && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, marginTop: 2 }}>{l.vendor}</div>}
                    </div>
                    <span style={{ width: 80, flexShrink: 0, display: 'flex', justifyContent: 'flex-start' }}><TrendPill t={t} line={l} /></span>
                    {amountCell(l, amtW)}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                      <RowIcon icon="doc" title="Edit line" onClick={() => setEditing(l.id)} />
                      <RowIcon icon="archive" title="Archive line (hides it and stops it counting)" onClick={() => askArchive(l, cat.name)} />
                      <RowIcon icon="x" danger title="Delete line" onClick={() => askDelete(l, cat.name)} />
                    </span>
                  </div>
                ))}
                {archived.length > 0 && (
                  <div>
                    <button onClick={() => setShowArchived((v) => !v)} style={{ width: '100%', textAlign: 'left', padding: '11px 12px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: t.muted, background: 'none', border: 'none', cursor: 'pointer' }}>{showArchived ? '▾' : '▸'} {archived.length} archived</button>
                    {showArchived && archived.map((l) => (
                      <div key={l.id} className="bb-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', borderTop: '1px solid ' + t.border, opacity: 0.6 }}>
                        <div style={{ flex: 1, minWidth: 0, fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>{l.label} <span style={{ color: t.faint }}>· {money(l.amount)} {UNIT_LABEL[l.unit] || ''}</span></div>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                          <RowIcon icon="refresh" title="Restore line (starts counting again)" onClick={() => askRestore(l)} />
                          <RowIcon icon="x" danger title="Delete permanently" onClick={() => setPending({ kind: 'delete', id: l.id, label: l.label, permanent: true })} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {pending && (
        <ConfirmDialog
          title={CONFIRM_COPY[pending.kind].title}
          confirmLabel={CONFIRM_COPY[pending.kind].label}
          tone={CONFIRM_COPY[pending.kind].tone}
          body={CONFIRM_COPY[pending.kind].body(pending)}
          onCancel={() => setPending(null)}
          onConfirm={runPending}
        />
      )}
    </div>
  );
}

function hCell(t: any): any { return { fontFamily: OSWALD, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.04em', color: t.muted }; }
function plCell(t: any): any { return { padding: '9px 18px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 13, color: t.fg, borderTop: '1px solid ' + t.border }; }
function plNum(t: any, color: string): any { return { padding: '9px 18px', textAlign: 'right', fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, fontVariantNumeric: 'tabular-nums', color, borderTop: '1px solid ' + t.border }; }

// ============================================================ ADS
// One paid channel's campaign table. The card title names the channel, so the
// per-row Platform column is gone (every row here is the same platform). `children`
// renders in the header's right slot — the Shop app card puts its import controls there.
function CampaignTable({ title, dot, note, rows, empty, children }: any) {
  const t = useT();
  const stTone: any = { winner: 'good', scaling: 'accent', ok: 'neutral', watch: 'warn' };
  const head = ['Campaign', 'Spend', 'Revenue', 'ROAS', 'Orders', 'Status'];
  return (
    <Card pad={0}>
      <div style={{ padding: '20px 24px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          {dot && <span style={{ width: 9, height: 9, borderRadius: 999, background: dot, flexShrink: 0 }} />}
          <Title size={15}>{title}</Title>
          {note && <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>{note}</span>}
        </span>
        {children}
      </div>
      <div style={{ overflowX: 'auto', padding: '0 10px 6px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead><tr>{head.map((h, i) => (
            <th key={h} style={{ textAlign: i === 0 || i === 5 ? 'left' : 'right', padding: '0 14px 12px', fontFamily: OSWALD, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.07em', color: t.muted, borderBottom: '1px solid ' + t.borderStrong }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {rows.map((c: any, i: number) => {
              const cell: any = { padding: '15px 14px', textAlign: 'right', fontFamily: FIGTREE, fontSize: 14, fontVariantNumeric: 'tabular-nums', color: t.fg2, borderTop: '1px solid ' + t.border };
              return (
                <tr key={c.name + i}>
                  <td style={{ ...cell, textAlign: 'left', fontWeight: 600, color: t.fg }}>{c.name}</td>
                  <td style={cell}>{money(c.spend)}</td>
                  <td style={cell}>{money(c.revenue)}</td>
                  <td style={{ ...cell, fontWeight: 700, color: c.roas >= 2.5 ? t.good : c.roas >= 2 ? t.fg : t.warn }}>{c.roas.toFixed(2)}</td>
                  <td style={cell}>{c.orders}</td>
                  <td style={{ ...cell, textAlign: 'left' }}><Badge tone={stTone[c.status]}>{c.status}</Badge></td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr><td colSpan={6} style={{ padding: '16px', textAlign: 'center', fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, borderTop: '1px solid ' + t.border }}>{empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Read a File as raw base64 (no data-URL prefix) plus its MIME type.
function fileToBase64(file: File): Promise<{ base64: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); const i = s.indexOf(','); resolve({ base64: i >= 0 ? s.slice(i + 1) : s, mediaType: file.type }); };
    r.onerror = () => reject(new Error('read_failed'));
    r.readAsDataURL(file);
  });
}

// Shop app (Shopify "Shop" app marketing) — no API to pull, so the owner uploads a
// screenshot; Claude vision reads the numbers, the owner reviews/edits them, and
// they save into KV and fold into the totals above like any other paid channel.
export function ShopAppCard({ data, onSaved }: any) {
  const t = useT();
  const entries = data?.entries || [];
  const [phase, setPhase] = React.useState<'idle' | 'reading' | 'preview' | 'saving'>('idle');
  const [draft, setDraft] = React.useState<any[] | null>(null);
  const [periodLabel, setPeriodLabel] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);
  const [confirmClear, setConfirmClear] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setErr('Please upload an image (PNG, JPG or WebP).'); return; }
    if (file.size > 6_000_000) { setErr('Image is too large (max ~6MB). Crop to just the campaign numbers and retry.'); return; }
    setErr(null); setPhase('reading');
    try {
      const { base64, mediaType } = await fileToBase64(file);
      const r = await fetch('/api/shopapp/extract', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ imageBase64: base64, mediaType }) });
      const j = await r.json().catch(() => null);
      if (!r.ok || !j?.entries?.length) {
        setPhase('idle');
        setErr(r.status === 403 ? 'Owner only — your role can view but not import.'
          : j?.error === 'no_vision' ? 'Screenshot import needs the ANTHROPIC_API_KEY secret on the Worker.'
          : j?.error === 'no_data_read' ? "Couldn't read any campaign numbers from that image — try a tighter, clearer screenshot."
          : 'Import failed — try again.');
        return;
      }
      setDraft(j.entries.map((x: any) => ({ ...x }))); setPeriodLabel(j.periodLabel || ''); setPhase('preview');
    } catch { setPhase('idle'); setErr('Import failed — try again.'); }
  };

  const setRow = (i: number, patch: any) => setDraft((d) => d!.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delRow = (i: number) => setDraft((d) => d!.filter((_, j) => j !== i));

  const save = async () => {
    if (!draft) return;
    setPhase('saving'); setErr(null);
    const r = await fetch('/api/shopapp/save', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ entries: draft, periodLabel }) }).catch(() => null);
    if (r && r.ok) { setDraft(null); setPhase('idle'); onSaved(); }
    else { setPhase('preview'); setErr(r && r.status === 403 ? 'Owner only — your role can view but not import.' : 'Save failed — try again.'); }
  };

  const doClear = async () => {
    setConfirmClear(false);
    const r = await fetch('/api/shopapp/clear', { method: 'POST' }).catch(() => null);
    if (r && r.ok) onSaved();
    else setErr('Clear failed — try again.');
  };

  const uploaded = data?.updatedAt ? new Date(data.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const note = entries.length
    ? `manual import${uploaded ? ' · ' + uploaded : ''}${data?.periodLabel ? ' · ' + data.periodLabel : ''} · not date-filtered`
    : 'manual screenshot import';

  const controls = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
      <Btn variant="outline" size="sm" icon="image" onClick={() => fileRef.current?.click()} disabled={phase === 'reading' || phase === 'saving'}>
        {phase === 'reading' ? 'Reading…' : entries.length ? 'Re-upload screenshot' : 'Upload screenshot'}
      </Btn>
      {entries.length > 0 && phase === 'idle' && <Btn variant="soft" size="sm" onClick={() => setConfirmClear(true)}>Clear</Btn>}
    </span>
  );

  return (
    <>
      {(phase === 'preview' || phase === 'saving') && draft ? (
        <Card pad={0}>
          <div style={{ padding: '20px 24px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span style={{ width: 9, height: 9, borderRadius: 999, background: t.good, flexShrink: 0 }} />
              <Title size={15}>Shop app campaigns</Title>
              <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.warn }}>review what Claude read — edit any misreads, then save</span>
            </span>
          </div>
          <div style={{ padding: '4px 24px 10px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>Period on screenshot:</span>
            <input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="e.g. Jun 1–30"
              style={{ background: t.surface2, border: '1px solid ' + t.border, borderRadius: 8, padding: '5px 10px', fontFamily: FIGTREE, fontSize: 13, color: t.fg, width: 160 }} />
          </div>
          <div style={{ overflowX: 'auto', padding: '0 14px 6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead><tr>{['Campaign', 'Spend', 'Revenue', 'Orders', 'ROAS', ''].map((h, i) => (
                <th key={i} style={{ textAlign: i === 0 || i === 5 ? 'left' : 'right', padding: '0 12px 12px', fontFamily: OSWALD, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.07em', color: t.muted, borderBottom: '1px solid ' + t.borderStrong }}>{h}</th>
              ))}</tr></thead>
              <tbody>
                {draft.map((r, i) => {
                  const roas = r.spend > 0 ? r.revenue / r.spend : 0;
                  const cell: any = { padding: '10px 12px', borderTop: '1px solid ' + t.border, textAlign: 'right' };
                  return (
                    <tr key={i}>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        <input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })}
                          style={{ background: t.surface2, border: '1px solid ' + t.border, borderRadius: 8, padding: '5px 10px', fontFamily: FIGTREE, fontSize: 13.5, fontWeight: 600, color: t.fg, width: '100%', maxWidth: 240 }} />
                      </td>
                      <td style={cell}><NumInput value={r.spend} step={1} prefix="$" width={92} box onChange={(v: number) => setRow(i, { spend: v })} /></td>
                      <td style={cell}><NumInput value={r.revenue} step={1} prefix="$" width={100} box onChange={(v: number) => setRow(i, { revenue: v })} /></td>
                      <td style={cell}><NumInput value={r.orders} step={1} width={64} box onChange={(v: number) => setRow(i, { orders: v })} /></td>
                      <td style={{ ...cell, fontFamily: FIGTREE, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: roas >= 2.5 ? t.good : roas >= 2 ? t.fg2 : t.warn }}>{roas.toFixed(2)}</td>
                      <td style={{ ...cell, textAlign: 'left' }}>
                        <button onClick={() => delRow(i)} title="Remove row" style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.faint, display: 'inline-flex', padding: 4 }}><Icon name="x" size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 24px 20px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Btn size="sm" tone="accent" onClick={save} disabled={phase === 'saving' || !draft.length}>{phase === 'saving' ? 'Saving…' : 'Save to Paid Ads'}</Btn>
            <Btn variant="soft" size="sm" onClick={() => { setDraft(null); setPhase('idle'); setErr(null); }}>Cancel</Btn>
            <Btn variant="outline" size="sm" onClick={() => setDraft((d) => [...(d || []), { name: '', spend: 0, revenue: 0, orders: 0 }])}>+ Add row</Btn>
            {err && <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.warn }}>{err}</span>}
          </div>
        </Card>
      ) : (
        <CampaignTable title="Shop app campaigns" dot={t.good} note={note} rows={entries}
          empty={phase === 'reading' ? 'Reading screenshot…' : 'No Shop app data yet — upload a screenshot of the Shop campaigns dashboard to import it.'}>
          {controls}
        </CampaignTable>
      )}
      {err && phase !== 'preview' && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.warn, marginTop: -8 }}>{err}</div>}
      {confirmClear && (
        <ConfirmDialog title="Clear Shop app data?" confirmLabel="Clear" tone="danger"
          body="This removes the imported Shop app campaign lines and takes their spend/revenue back out of the Paid Ads totals above. You can re-upload a screenshot anytime."
          onConfirm={doClear} onCancel={() => setConfirmClear(false)} />
      )}
    </>
  );
}

// NOTE: The Paid Ads view moved to src/views/paidads.tsx (workspace grid rebuild,
// design_handoff_paid_ads_meta). ShopAppCard + CampaignTable stay here — ShopAppCard
// is imported by paidads for the Shop App pill; CampaignTable backs its idle state.

// ============================================================ EMAIL MARKETING (Klaviyo)
// Small hover tooltip (a circled "i" that reveals a text bubble on hover).
function InfoTip({ text }: any) {
  const t = useT();
  const [show, setShow] = React.useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{ display: 'inline-grid', placeItems: 'center', width: 17, height: 17, borderRadius: 999, border: '1.5px solid ' + t.muted, color: t.muted, fontFamily: FIGTREE, fontSize: 11, fontWeight: 700, cursor: 'help' }}>i</span>
      {show && (
        <span style={{ position: 'absolute', top: '135%', left: 0, zIndex: 60, width: 320, maxWidth: '72vw', padding: '10px 13px', borderRadius: 10, background: t.surface, border: '1px solid ' + t.borderStrong, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', fontFamily: FIGTREE, fontSize: 12, fontWeight: 400, lineHeight: 1.5, color: t.fg2, textTransform: 'none', letterSpacing: 'normal' }}>{text}</span>
      )}
    </span>
  );
}

// Scrolling data table for the Email Marketing breakdowns (matches the Ads
// Campaigns table styling; scrolls left/right when the columns overflow).
function EmailTable({ title, sub, cols, rows, loading, empty }: any) {
  const t = useT();
  const hCell = (align: string): any => ({ textAlign: align, padding: '0 14px 12px', fontFamily: OSWALD, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.07em', color: t.muted, borderBottom: '1px solid ' + t.borderStrong, whiteSpace: 'nowrap' });
  return (
    <Card pad={0}>
      <div style={{ padding: '20px 24px 8px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <Title size={15}>{title}</Title>
        {sub && <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>{sub}</span>}
      </div>
      <div style={{ overflowX: 'auto', padding: '0 10px 6px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: cols.length * 112 }}>
          <thead><tr>{cols.map((c: any) => <th key={c.key} style={hCell(c.align || 'right')}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((r: any, i: number) => (
              <tr key={r.id || i}>
                {cols.map((c: any) => {
                  const cell: any = { padding: '14px 14px', textAlign: c.align || 'right', fontFamily: FIGTREE, fontSize: 14, fontVariantNumeric: 'tabular-nums', color: t.fg2, borderTop: '1px solid ' + t.border, whiteSpace: c.wrap ? 'normal' : 'nowrap' };
                  if (c.strong) { cell.fontWeight = 600; cell.color = t.fg; }
                  if (c.key === 'name') { cell.maxWidth = 260; cell.overflow = 'hidden'; cell.textOverflow = 'ellipsis'; }
                  return <td key={c.key} style={cell} title={c.key === 'name' ? r[c.key] : undefined}>{c.fmt ? c.fmt(r[c.key], r) : r[c.key]}</td>;
                })}
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={cols.length} style={{ padding: '18px 16px', textAlign: 'center', fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, borderTop: '1px solid ' + t.border }}>{loading ? 'Loading…' : empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function EmailMarketingView() {
  const t = useT();
  const isMobile = useIsMobile();
  const rm = useRangeMetrics("l30");
  const d: any = { ...F().day, ...(rm.data?.day || {}) };
  const aov = d.emailOrders ? (d.emailRev || 0) / d.emailOrders : 0;

  // Reporting tables (campaigns / flows / forms) — same window as the cards.
  const [rep, setRep] = React.useState<any>(null);
  const [repLoading, setRepLoading] = React.useState(true);
  const retriesRef = React.useRef(0);
  const loadRep = React.useCallback((fresh: boolean) => {
    const p = new URLSearchParams({ range: rm.range });
    if (rm.range === 'custom' && rm.custom.start && rm.custom.end) { p.set('start', rm.custom.start); p.set('end', rm.custom.end); }
    if (fresh) { p.set('fresh', '1'); retriesRef.current = 0; }
    setRepLoading(true);
    fetch(`/api/email?${p.toString()}`).then((r) => (r.ok ? r.json() : null)).then((data) => {
      if (data) setRep(data);
      // Server warms the cache in the background on a miss; poll until it's ready.
      if (data?.warming && retriesRef.current < 6) {
        retriesRef.current += 1;
        setTimeout(() => loadRep(false), 5000);
      } else {
        setRepLoading(false);
      }
    }).catch(() => setRepLoading(false));
  }, [rm.range, rm.custom.start, rm.custom.end]);
  React.useEffect(() => {
    if (rm.range === 'custom' && !(rm.custom.start && rm.custom.end)) return;
    retriesRef.current = 0;
    loadRep(false);
  }, [loadRep]);
  const refreshAll = () => { rm.refresh(); loadRep(true); };

  const pct = (v: number) => (v != null ? pctS(v) : '—');
  const int = (v: number) => (v != null ? v.toLocaleString() : '—');
  const dol = (v: number) => money(v || 0);
  // "Jul 05, 2026 | Wed | 13:00" in the store's timezone (Mountain).
  const fmtSent = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const date = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', month: 'short', day: '2-digit', year: 'numeric' }).format(d);
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Denver', weekday: 'short' }).format(d);
    const time = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Denver', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    return `${date} | ${wd} | ${time}`;
  };

  const campaignCols = [
    { key: 'name', label: 'Campaign', align: 'left', strong: true },
    { key: 'sentAt', label: 'Sent', align: 'left', fmt: fmtSent },
    { key: 'openRate', label: 'Open', fmt: pct },
    { key: 'clickRate', label: 'Click', fmt: pct },
    { key: 'bounceRate', label: 'Bounce', fmt: pct },
    { key: 'spam', label: 'Spam', fmt: int },
    { key: 'unsubs', label: 'Unsubs', fmt: int },
    { key: 'placedRate', label: 'Order rate', fmt: pct },
    { key: 'revenue', label: 'Revenue', fmt: dol },
  ];
  const flowCols = [
    { key: 'name', label: 'Flow', align: 'left', strong: true },
    { key: 'clickRate', label: 'Click rate', fmt: pct },
    { key: 'cvs', label: 'CVs', fmt: int },
    { key: 'cvr', label: 'CVR', fmt: pct },
    { key: 'delivered', label: 'Delivered', fmt: int },
    { key: 'placedRate', label: 'Placed order %', fmt: pct },
    { key: 'revenue', label: 'Revenue', fmt: dol },
  ];
  const formCols = [
    { key: 'name', label: 'Signup form', align: 'left', strong: true },
    { key: 'views', label: 'Views', fmt: int },
    { key: 'submits', label: 'Submits', fmt: int },
    { key: 'submitRate', label: 'Submit rate', fmt: pct },
    { key: 'orders', label: 'Orders', fmt: int },
    { key: 'aov', label: 'AOV', fmt: dol },
    { key: 'revenue', label: 'Revenue', fmt: dol },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <PageHead isMobile={isMobile}
        title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>Email Marketing<InfoTip text={"Revenue is Klaviyo-attributed (campaigns or flows as the last marketing touch). Rates are over delivered/recipients for the selected window. Flows are aggregated across their messages; campaigns and forms show sent/active items only. % of total revenue divides email revenue by Shopify store revenue for the same window."} /></span>}
        sub={(rm.data?.label || 'Last 30 days') + ' · Klaviyo — email + SMS'}
        right={<DateRangePicker range={rm.range} setRange={rm.setRange} custom={rm.custom} setCustom={rm.setCustom} onRefresh={refreshAll} loading={rm.loading || repLoading} />} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14 }}>
        <StatTile label="Email revenue" value={money(d.emailRev != null ? d.emailRev : 0)} accent={t.purple} icon="mail" sub="campaigns + flows" />
        <StatTile label="% of total revenue" value={d.emailRevPct != null ? d.emailRevPct + '%' : '—'} icon="percent" sub="email share of store" />
        <StatTile label="Email orders" value={d.emailOrders != null ? d.emailOrders.toLocaleString() : '—'} icon="store" />
        <StatTile label="Email AOV" value={aov ? money(aov) : '—'} icon="dollar" />
        <StatTile label="New email subscribers" value={d.newEmails != null ? d.newEmails.toLocaleString() : '—'} icon="mail" />
        <StatTile label="New SMS subscribers" value={d.newSms != null ? d.newSms.toLocaleString() : '—'} icon="bell" />
      </div>

      {/* Klaviyo's reporting API rate-limits hard; when we're in its cooldown the
          tables show the last good pull rather than blanking. Say so plainly. */}
      {rep?.throttledFor ? (
        <div style={{ display: 'flex', gap: 12, background: 'rgba(29,23,64,0.55)', border: '1px solid rgba(150,130,230,0.16)', borderLeft: '3px solid ' + t.warn, borderRadius: 14, padding: '14px 16px' }}>
          <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 9, background: t.warnSoft, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="clock" size={16} color={t.warn} strokeWidth={2.2} /></span>
          <div>
            <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 11, color: t.warn, marginBottom: 5 }}>Klaviyo reporting is catching its breath</div>
            <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.sidebarMuted, lineHeight: 1.6 }}>
              Klaviyo throttles its reporting API. {rep.stale ? 'The tables below show the last good pull' : 'Fresh numbers aren’t cached yet'} — updated numbers return automatically in about {rep.throttledFor} minute{rep.throttledFor === 1 ? '' : 's'}. The subscriber and revenue tiles above are live regardless.
            </div>
          </div>
        </div>
      ) : rep?.stale ? (
        <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, padding: '0 4px' }}>Showing the most recent cached pull — refreshing in the background.</div>
      ) : null}

      <EmailTable title="Campaigns" sub="5 most recent in range" cols={campaignCols} rows={rep?.campaigns || []} loading={repLoading} empty={(rep?.debug?.campaignsList || rep?.debug?.campaignStats) ? `Couldn't load campaigns — reason: ${rep.debug.campaignsList || rep.debug.campaignStats}` : 'No campaigns sent in this range.'} />
      <EmailTable title="Flows" sub="active automations" cols={flowCols} rows={rep?.flows || []} loading={repLoading} empty={rep?.debug?.flows ? `Couldn't load flows — reason: ${rep.debug.flows}` : 'No active flow activity in this range.'} />
      {rep && !rep.formsAvailable
        ? <BuildNote label="Signup forms" icon="doc">Form performance reporting is being finalized against Klaviyo's Forms report API — it'll populate here once verified live.{(rep.debug?.formsReport || rep.debug?.formsList) ? ` (reason: ${rep.debug.formsReport || rep.debug.formsList})` : ''}</BuildNote>
        : <>
            <EmailTable title="Signup forms" sub="active forms" cols={formCols} rows={rep?.forms || []} loading={repLoading} empty="No signup-form activity in this range." />
            {rep?.debug?.formsConv && <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, padding: '0 4px', marginTop: -6 }}>Order/revenue attribution unavailable — reason: {rep.debug.formsConv}</div>}
          </>}
    </div>
  );
}

// ============================================================ INTEGRATIONS
// map an integration's display name to the heartbeat key returned by
// /api/integrations/health (null = not a live-API source, e.g. benchmarks)
function healthKey(name: string): string | null {
  const n = name.toLowerCase();
  if (n.includes('kpi')) return null;
  if (n.includes('shopify')) return 'shopify';
  if (n.includes('meta') || n.includes('facebook')) return 'meta';
  if (n.includes('google')) return 'google';
  if (n.includes('klaviyo')) return 'klaviyo';
  if (n.includes('packiyo')) return 'packiyo';
  if (n.includes('quickbook')) return 'quickbooks';
  return null;
}

// Shared live-health hook + status resolver, used by BOTH the Integrations page
// and the Daily Pulse "Integration health" mini-card so they never diverge.
export function useIntegrationHealth() {
  const { data } = useCachedJson<any>('/api/integrations/health');
  return data ? (data.statuses || {}) : null; // null while loading (unchanged contract)
}

// Resolve one integration's real status from the live health map.
export function resolveIntegrationStatus(i: any, health: any): string {
  if (i.method === 'manual') return 'manual';
  if (!health) return 'checking';
  const k = healthKey(i.name);
  return (k && health[k]) || 'not_connected';
}

// Status id → themed colors + label.
export function integrationStatusCfg(t: any, s: string) {
  return (({
    live: { label: 'Live', fg: t.good, bg: t.goodSoft, dot: t.good },
    degraded: { label: 'Degraded', fg: t.warn, bg: t.warnSoft, dot: t.warn },
    down: { label: 'Down — reconnect', fg: t.bad, bg: t.badSoft, dot: t.bad },
    not_connected: { label: 'Not connected', fg: t.muted, bg: t.surface2, dot: t.faint },
    manual: { label: 'Manual upload', fg: t.warn, bg: t.warnSoft, dot: t.warn },
    checking: { label: 'Checking…', fg: t.faint, bg: t.surface2, dot: t.faint },
  } as any)[s] || { label: 'Not connected', fg: t.muted, bg: t.surface2, dot: t.faint });
}

// Live system-status feed (integration downs / degradations) — polled so the
// Morning Brief clears itself the moment an outage recovers. ?fresh not needed;
// the Worker caches for 45s.
export function useSystemStatus() {
  const { data, refresh } = useCachedJson<any>('/api/status');
  // Keep polling so the Morning Brief clears itself when an outage recovers; the
  // cache makes the first paint on revisit instant instead of a blank feed.
  React.useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);
  return data;
}

// "2h 14m" style age from an ISO timestamp — how long an issue has been active.
export function sinceLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const days = Math.floor(h / 24);
  return `${days}d ${h % 24}h`;
}

// ---- Cloudflare capacity: this month's infra usage vs the plan's included
// allowance. Account-wide (Worker requests are filtered to bb-admin; D1/KV are
// account-level). Lives beside the service connections — the platform "plumbing".
type CfMetric = { key: string; label: string; used: number; limit: number; pct: number; approx?: boolean; unit?: string };
type CfUsageData = { ok: boolean; connected: boolean; needsToken?: boolean; plan?: string; window?: { label: string }; metrics?: CfMetric[]; errors?: string[]; at?: string };

function fmtCompact(n: number): string {
  if (n >= 1e9) return (n / 1e9).toFixed(n >= 1e10 ? 0 : 1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + 'k';
  return String(Math.round(n));
}

function CloudflareUsageCard() {
  const t = useT();
  const [data, setData] = React.useState<CfUsageData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const load = React.useCallback((fresh: boolean) => {
    setLoading(true);
    fetch(`/api/usage${fresh ? '?fresh=1' : ''}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(false); }, [load]);

  const metrics = data?.metrics || [];
  const peak = metrics.reduce((m, x) => Math.max(m, x.pct), 0);
  return (
    <Card pad={0} style={{ alignSelf: 'start' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 18px', borderBottom: '1px solid ' + t.border, flexWrap: 'wrap' }}>
        <Icon name="zap" size={16} color={t.muted} />
        <Title size={14}>Cloudflare capacity</Title>
        {metrics.length > 0 ? <Badge tone={peak >= 80 ? 'bad' : peak >= 50 ? 'warn' : 'good'}>{peak < 0.1 ? '<0.1' : peak}% peak</Badge> : <Badge tone="neutral">{data?.window?.label || 'This month'}</Badge>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          {data?.at ? <span style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>as of {new Date(data.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span> : null}
          <button onClick={() => load(true)} disabled={loading} style={{ cursor: loading ? 'default' : 'pointer', background: 'none', border: '1px solid ' + t.border, borderRadius: 999, padding: '3px 11px', fontFamily: FIGTREE, fontWeight: 600, fontSize: 11.5, color: t.muted }}>{loading ? '…' : 'Refresh'}</button>
        </span>
      </div>
      <div style={{ padding: '16px 18px' }}>
        {data?.needsToken ? (
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.6 }}>
            Not connected. Add a read-only Cloudflare API token (<b>Account Analytics → Read</b>) as the <span style={{ fontFamily: MONO, fontSize: 11.5 }}>CF_ANALYTICS_TOKEN</span> secret to light up live usage gauges.
          </div>
        ) : loading && !data ? (
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Loading usage…</div>
        ) : metrics.length === 0 ? (
          <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Usage unavailable{data?.errors?.length ? ` (${data.errors[0]})` : ''}.</div>
        ) : (
          <div style={{ display: 'grid', gap: 15 }}>
            {metrics.map((m) => {
              const pct = Math.max(0, Math.min(100, m.pct));
              const barColor = m.pct >= 80 ? t.bad : m.pct >= 50 ? t.warn : t.good;
              return (
                <div key={m.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2 }}>{m.label}{m.approx ? <span style={{ color: t.faint }}> · approx</span> : null}</span>
                    <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, fontVariantNumeric: 'tabular-nums' }}>
                      <b style={{ color: t.fg }}>{fmtCompact(m.used)}{m.unit ? ' ' + m.unit : ''}</b> / {fmtCompact(m.limit)}{m.unit ? ' ' + m.unit : ''} · {m.pct < 0.1 ? '<0.1' : m.pct}%
                    </span>
                  </div>
                  <div style={{ height: 7, borderRadius: 999, background: t.border, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', minWidth: m.used > 0 ? 2 : 0, background: barColor }} />
                  </div>
                </div>
              );
            })}
            <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, lineHeight: 1.5, paddingTop: 2 }}>
              This month vs the {data?.plan} monthly allowance · amber ≥50%, red ≥80%. Worker requests are this app; D1/KV are account-wide.
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function IntegrationsView() {
  const t = useT();
  const isMobile = useIsMobile();
  const list = F().integrations;
  const health = useIntegrationHealth();

  const resolve = (i: any): string => resolveIntegrationStatus(i, health);
  const statusCfg = (s: string) => integrationStatusCfg(t, s);

  const resolved = list.map((i: any) => resolve(i));
  const count = (s: string) => resolved.filter((r: string) => r === s).length;
  const liveN = count('live'), downN = count('down'), offN = count('not_connected') + count('checking');

  const summary = (label: string, val: number, color: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: color, flexShrink: 0 }} /><b style={{ color: t.fg2 }}>{val}</b> {label}
    </span>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SectionTitle sub="Live heartbeat — each source is probed in real time. Not connected = no credentials; Down = set up but the API isn't responding." right={<Badge tone="purple">Live · real-time</Badge>}>Integrations &amp; platform</SectionTitle>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.5fr) minmax(300px,1fr)', gap: 16, alignItems: 'start' }}>
        {/* LEFT — connected sources, condensed to one list card */}
        <Card pad={0} style={{ alignSelf: 'start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid ' + t.border, flexWrap: 'wrap' }}>
            <Title size={14}>Connected sources</Title>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 14, flexWrap: 'wrap' }}>
              {summary('live', liveN, t.good)}
              {offN > 0 ? summary('off', offN, t.faint) : null}
              {summary('down', downN, downN > 0 ? t.bad : t.faint)}
            </span>
          </div>
          {downN > 0 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 18px', borderBottom: '1px solid ' + t.border, background: t.badSoft }}>
              <Icon name="alert" size={15} color={t.bad} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2, lineHeight: 1.5 }}>{downN} source{downN > 1 ? 's' : ''} set up but not responding (expired token or revoked access) — reconnect to bring {downN > 1 ? 'them' : 'it'} back.</div>
            </div>
          )}
          {list.map((i: any, idx: number) => {
            const status = resolve(i);
            const cfg = statusCfg(status);
            return (
              <div key={i.name} style={{ padding: '13px 18px', borderTop: idx ? '1px solid ' + t.border : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 8, background: t.surface2, display: 'grid', placeItems: 'center', fontFamily: OSWALD, fontWeight: 700, fontSize: 15, color: t.fg2, flexShrink: 0 }}>{i.name[0]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13.5, color: t.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.name}</div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.role}</div>
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: cfg.bg, color: cfg.fg, fontFamily: OSWALD, fontWeight: 600, fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 999, flexShrink: 0 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: cfg.dot, boxShadow: status === 'live' ? '0 0 0 3px ' + t.goodSoft : status === 'down' ? '0 0 0 3px ' + t.badSoft : 'none' }} />{cfg.label}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px', marginTop: 8, paddingLeft: 44, fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>
                  <span>{i.auth}</span>
                  <span aria-hidden>·</span>
                  <span>refresh <b style={{ color: status === 'live' ? t.good : t.muted, fontWeight: 600 }}>{i.cadence}</b></span>
                  <span aria-hidden>·</span>
                  <span>{i.cost}</span>
                </div>
              </div>
            );
          })}
        </Card>

        {/* RIGHT — platform capacity (Cloudflare) */}
        <CloudflareUsageCard />
      </div>
    </div>
  );
}

// ============================================================ SECURITY
export function SecurityView() {
  const t = useT();
  const [auth, setAuth] = React.useState<any>({ passkey: true, totp: true, sms: false, magic: false });
  const roles = [['Rachel Stone', 'Owner · full access', 'good'], ['Marco Diaz', 'Ops · Profit, Ads, App', 'purple'], ['Sara Lin', 'Support · Help Desk + KB', 'accent']];
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SectionTitle sub="No one reaches this dashboard without an approved account and a second factor.">Security</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
        <Card pad={18}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}><Icon name="lock" size={18} color={t.fg} /><Title size={14}>Access &amp; sign-in</Title></div>
          {[['passkey', 'Passkey (Face ID / fingerprint)', 'Primary — phishing-proof, nothing to steal'], ['totp', 'Authenticator app (TOTP)', 'Backup second factor'], ['sms', 'SMS one-time code', 'Fallback only — least secure'], ['magic', 'Email magic link', 'Disabled for admin accounts']].map(([k, n, d], i) => (
            <div key={k} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 0', borderTop: i ? '1px solid ' + t.border : 'none' }}>
              <Toggle on={auth[k]} onClick={() => setAuth({ ...auth, [k]: !auth[k] })} />
              <div><div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{n}</div><div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{d}</div></div>
            </div>
          ))}
          <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 12 }}>Recommendation: <b style={{ color: t.fg }}>passkey + one backup TOTP</b>. Build now, enforce at launch.</div>
        </Card>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <Card pad={18}>
            <Title size={14} style={{ marginBottom: 12 }}>Team access</Title>
            {roles.map(([n, r, c], i) => (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: i ? '1px solid ' + t.border : 'none' }}>
                <Avatar name={n} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg }}>{n}</div><div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted }}>{r}</div></div>
                <Badge tone={c}>Active</Badge>
              </div>
            ))}
            <Btn variant="ghost" size="sm" icon="users" style={{ marginTop: 12 }}>Invite teammate</Btn>
          </Card>
          <Card pad={18}>
            <Title size={14} style={{ marginBottom: 8 }}>Guarantees</Title>
            {['Financial data + customer AI run on separate, isolated infrastructure', 'Customer AI cannot read orders, PII, or company financials', 'All access logged with an immutable audit trail', 'Least-privilege roles — teammates see only what they need'].map((x) => (
              <div key={x} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '6px 0' }}><Icon name="check" size={15} color={t.good} style={{ marginTop: 1, flexShrink: 0 }} /><span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.5 }}>{x}</span></div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
